/*!
 * sofia-widget.js — Chat flutuante da "Sofia" (agente de vendas por IA da Prohall)
 * -----------------------------------------------------------------------------
 * Vanilla JS. Sem dependências, sem CDN, sem build. CSS embutido.
 * Expõe apenas window.SofiaWidget -> { open(), close(), toggle(), isOpen(), destroy() }
 *
 * Fala o contrato do embed de `ws.prohall.app.br/agente-vendas/embed`:
 * POST /sessao (abre/retoma) + POST /mensagem (conversa). USE_MOCK = true troca
 * os dois por um mock com o MESMO formato, para QA sem rede.
 *
 * Decisões estruturais (o "porquê" dos pontos não óbvios):
 *  - QUEM GUARDA A CONVERSA É O SERVIDOR. Ele devolve o histórico em /sessao,
 *    indexado pelo hash da sessão. No navegador fica só um espelho em
 *    sessionStorage (morre ao fechar a aba) para o painel repintar na hora ao
 *    trocar de página, em vez de piscar vazio por ~20s. Quem apaga de verdade é
 *    o backend; SofiaWidget.newSession() aqui só corta o vínculo com o hash.
 *  - A RESPOSTA DEMORA ~20s DE PROPÓSITO (debounce do backend). Isso muda esta
 *    camada inteira: timeout longo, campo destravado durante a espera e
 *    deduplicação de rajada. Ver o bloco do debounce na configuração.
 *  - A renderização do texto escapa HTML ANTES de converter a marcação do
 *    WhatsApp. Inverter essa ordem abre XSS. Ver renderRichText().
 */
(function () {
  'use strict';

  /* ==========================================================================
   * 1. CONFIGURAÇÃO
   * ========================================================================== */

  // >>> ÚNICA chave para sair do backend real e cair no mock. <<<
  // O mock devolve o MESMO formato dos endpoints, então trocar isto não mexe em
  // mais nada. Útil quando o CORS do ambiente ainda não foi liberado.
  var USE_MOCK = false;

  // Atalho de QA: `?sofia_mock=1` na URL liga o mock sem editar o arquivo e sem
  // push. É o jeito de testar no tema de teste o que depende de uma resposta
  // específica do backend (link de checkout, vitrine, sacola) sem esperar os
  // ~56s dela e sem torcer para ela mandar o link. Gatilhos: #sacola, #vitrine,
  // #acoes, #fim, #lento, #429, #403, #rede — ver a seção do MOCK.
  //
  // NÃO funciona no tema publicado (`Shopify.theme.role === 'main'`): um link com
  // o parâmetro circulando levaria uma cliente de verdade a conversar com uma
  // Sofia falsa, que inventa produto e preço. No preview e no tema de teste,
  // role é 'unpublished'/'development' e a chave vale.
  try {
    if (/[?&]sofia_mock=1\b/.test(location.search)) {
      var papel = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || '';
      if (String(papel).toLowerCase() !== 'main') USE_MOCK = true;
    }
  } catch (e) {}

  // Contrato do embed. Base + dois POST, sem token e sem credencial:
  //   /sessao   -> abre ou retoma a conversa; devolve histórico e sacola
  //   /mensagem -> manda uma mensagem; devolve texto, vitrines, sacola e link
  var BASE_URL = 'https://ws.prohall.app.br';
  var EP_SESSAO = '/agente-vendas/embed/sessao';
  var EP_MENSAGEM = '/agente-vendas/embed/mensagem';

  // 1 = Prohall, 4 = Weux. Obrigatório nas duas requisições.
  var EMPRESA_ID = 1;

  var MAX_CHARS = 1000;          // limite do campo "mensagem"
  var MAX_HISTORY = 20;          // turnos guardados localmente (ver SEND_LOCAL_HISTORY)
  var MAX_CARDS = 4;             // mostra 4 produtos; o resto arrasta na horizontal

  /* ---------------------------------------------------------------------------
   * Card clicável: abrir o produto e adicionar ao carrinho
   *
   * A vitrine do backend traz só `id` (interno deles), `nome`, `preco` e
   * `imagem_url` — sem `url` e sem `variant_id`. Quem resolve é o tema, pelos
   * endpoints públicos da própria loja:
   *   /search/suggest.json  -> handle e url do produto   (~4 KB)
   *   /products/<handle>.js -> variantes com id          (~5 KB)
   *
   * Só roda no CLIQUE: card que ninguém toca não custa requisição nenhuma. Se um
   * dia o backend mandar `url`/`variant_id`, os dois passos são pulados.
   * ------------------------------------------------------------------------- */
  var CARD_CLICAVEL = true;       // false = volta ao card só com "Ver produto"
  var CARD_ADD_CARRINHO = true;   // false = esconde o botão de adicionar
  var BUSCA_TIMEOUT_MS = 6000;

  // Clicar em "Adicionar ao carrinho" escreve o nome do produto na conversa,
  // como se a pessoa tivesse pedido aquele item, e o resultado vem logo abaixo.
  // Sem isto, a única resposta ao clique é o botão mudar de texto — e ele pode
  // estar fora da tela quando a conversa é longa.
  //
  var AVISAR_SELECAO_NO_CHAT = true;

  /* ---------------------------------------------------------------------------
   * Contar para a Sofia o que entrou no carrinho pelo card
   *
   * O clique não dispara uma requisição só para avisar: seriam ~56s de espera
   * (e com o campo travado) para ela responder "ok". E `contexto.cart`, que o
   * widget manda desde sempre, o backend IGNORA — é campo fora do contrato.
   *
   * Então o aviso pega carona na PRÓXIMA mensagem: o texto da pessoa vai com
   * uma linha de contexto no fim. Custo zero de latência, e ela fica sabendo no
   * momento em que isso importa — quando a pessoa fala "finaliza a compra" e
   * antes ela perguntava "qual produto você escolheu?".
   *
   * O texto é um gabarito para o time da IA afinar: {itens} vira a lista.
   * ------------------------------------------------------------------------- */
  var AVISAR_IA_DO_CARRINHO = true;
  var NOTA_CARRINHO =
    '\n\n[contexto do site, não foi o cliente que escreveu: ele já adicionou ao ' +
    'carrinho pelos cards do chat: {itens}. Trate esses itens como escolhidos e ' +
    'não pergunte de novo qual ele quer.]';
  var MAX_ITENS_NOTA = 6;

  /* ---------------------------------------------------------------------------
   * O DEBOUNCE DE 20s — o que mais afeta esta camada
   *
   * O backend agrupa mensagens numa janela DESLIZANTE de 20s (o mesmo parâmetro
   * do WhatsApp): cada mensagem nova reinicia a contagem. Quatro consequências
   * que este arquivo TEM que respeitar:
   *
   *   1) a resposta chega em ~20s + 5 a 13s de modelo. Timeout curto faz o chat
   *      dizer "não consegui enviar" justamente quando a Sofia respondeu;
   *   2) o teto da rajada é 60s, então o pior caso é ~75s — é daí que sai o
   *      REQUEST_TIMEOUT_MS;
   *   3) 3 mensagens seguidas deixam 3 requisições abertas que resolvem com a
   *      MESMA resposta. Desenhar as 3 = três bolhas idênticas. Quem evita é o
   *      controle de rajada (state.rajada) + assinatura, em handleResponse();
   *   4) o campo NÃO pode travar durante a espera. Travar mata a rajada, que é
   *      exatamente o que o debounce existe para atender.
   * ------------------------------------------------------------------------- */
  // MEDIDO em produção (16/09/2026): uma mensagem simples levou *56s* ponta a
  // ponta — bem acima dos ~33s que a doc do backend estimava (20s de janela +
  // 5 a 13s de modelo). Com o teto de rajada de 60s, o pior caso passa de 90s.
  // Daí os 120s: cortar antes disso mata uma resposta que ia chegar.
  var REQUEST_TIMEOUT_MS = 120000;
  var SESSAO_TIMEOUT_MS = 12000;   // /sessao é leitura de banco, não passa por modelo
  var MAX_EM_VOO = 4;              // requisições da mesma rajada em paralelo
  var LIMITE_POR_MINUTO = 12;      // igual ao teto do backend, para não tomar 429

  /* ---------------------------------------------------------------------------
   * TRAVAR O CAMPO ENQUANTO A RESPOSTA NÃO CHEGA (decisão da loja)
   *
   * Com isto ligado o campo fica desabilitado do envio até a resposta, e a
   * rajada do backend deixa de acontecer: só existe uma mensagem por turno.
   *
   * O que isso troca: a pessoa não consegue completar o raciocínio em duas
   * mensagens seguidas ("tenho frizz" / "e cabelo tingido") — tem que esperar o
   * turno inteiro, que hoje passa de 50s. Em troca, ninguém fica com dúvida se a
   * mensagem foi enviada nem manda a mesma coisa duas vezes.
   *
   * A deduplicação de rajada continua no lugar: ela protege o retry e qualquer
   * caminho que ainda abra duas requisições.
   * ------------------------------------------------------------------------- */
  var TRAVAR_DURANTE_ESPERA = true;

  var PLACEHOLDER_PADRAO = 'Escreva sua mensagem…';
  var PLACEHOLDER_ESPERANDO = 'Aguarde a resposta…';

  // A espera passa de 50s: em silêncio, parece travamento. Cada par é
  // [ms, texto] e escreve na linha de status acima do campo enquanto a resposta
  // não chega. Lista vazia desliga o recurso — as três bolinhas seguem sozinhas.
  // (A dica dos 7s saiu a pedido da loja: cedo demais para falar qualquer coisa.)
  var DICAS_ESPERA = [
    [24000, 'Quase lá, montando a resposta…'],
    [45000, 'Ela está caprichando na indicação, já sai…']
  ];

  // Link no meio do texto vira botão abaixo da bolha, com rótulo tirado da
  // própria URL (/collections/tratamento → "Ver Tratamento").
  // false volta ao link sublinhado dentro da frase.
  var LINKS_COMO_BOTAO = true;
  var MAX_BOTOES_LINK = 3;       // por resposta; o excedente é ignorado
  // Domínios que contam como "a própria loja" (abrem na mesma aba). O host
  // atual e o Shopify.shop entram automaticamente.
  var DOMINIOS_DA_LOJA = ['lojaprohall.com.br', 'prohall-professional.myshopify.com'];

  // O `link` de checkout chega em sofia.prohall.app.br — host DO BACKEND, não da
  // loja. Um /cart/... nesse domínio não é a sacola deste visitante, e o
  // checkout seria de outro storefront. Por isso o host é reescrito para a loja
  // atual, preservando caminho e query. Vale só para os hosts listados; qualquer
  // outro domínio passa intacto.
  var REESCREVER_LINK_DO_AGENTE = true;
  var HOSTS_DO_AGENTE = ['sofia.prohall.app.br', 'ws.prohall.app.br'];

  var CART_TIMEOUT_MS = 3000;
  var MOCK_DELAY_MS = 800;
  var DEFAULT_RETRY_AFTER = 30;  // fallback quando o header Retry-After não vem

  /* ---------------------------------------------------------------------------
   * Carrinho — a IA decide, o NAVEGADOR executa
   *
   * Dois caminhos, e o primeiro é o único que o backend usa hoje:
   *
   *   1) SACOLA (contrato atual). Toda resposta traz a `sacola` inteira, montada
   *      no servidor. O widget desenha o painel e, quando a pessoa confirma,
   *      passa a sacola para o carrinho nativo via /cart/add.js. A sincronia é
   *      IDEMPOTENTE: soma só a diferença entre o que a sacola pede e o que o
   *      carrinho já tem, então clicar duas vezes não duplica item.
   *   2) AÇÕES (extensão; o backend ainda não emite). Se um dia vier `acoes` na
   *      resposta, este arquivo executa add/set/remove linha a linha — é o que
   *      permite REMOVER e DEFINIR quantidade, coisa que a sacola, que só soma,
   *      não expressa. Formato em docs/sofia-acoes-carrinho.md.
   *
   * Por que o navegador e não o servidor: a Ajax API (/cart/add.js,
   * /cart/change.js) só aceita POST da própria sessão do navegador — a doc da
   * Shopify diz "update the cart for the current session". A Storefront Cart API
   * aceitaria servidor, e nesta loja o /cart.js chega a expor o `?key=` que ela
   * exige (medido), mas isso pede um Storefront access token e sai do escopo.
   * ------------------------------------------------------------------------- */
  var ACOES_HABILITADAS = true;   // false = ignora `acoes` por completo
  var ACOES_AUTO = false;         // true = aplica sem pedir confirmação (ver doc)
  var MAX_ACOES = 10;             // por resposta; o excedente é descartado
  var MAX_QTY = 10;               // teto por linha, mesmo que a IA peça mais
  var CART_OP_TIMEOUT_MS = 8000;

  /* ---------------------------------------------------------------------------
   * Painel da sacola (a `sacola` que vem em toda resposta)
   *
   * Fica entre a conversa e o campo de texto, recolhido, mostrando contagem e
   * total. É redesenhado INTEIRO a cada resposta — a sacola do servidor é a
   * fonte da verdade, o widget não acumula estado próprio.
   * ------------------------------------------------------------------------- */
  var SACOLA_VISIVEL = true;      // false = ignora `sacola` e nunca desenha o painel
  var SACOLA_ABERTA = false;      // true = nasce expandida em vez de recolhida
  /* true = o que a Sofia monta entra no carrinho da loja SEM pedir confirmação
     (pedido da loja em 17/09/2026). O card de "Quer que eu atualize sua sacola?"
     some: quando ela diz "coloquei na sua sacola", já está no carrinho.
     Risco conhecido: se ela entender errado, o produto entra do mesmo jeito —
     a pessoa só descobre no painel ou no checkout. */
  var SACOLA_AUTO = true;
  var MAX_ITENS_SACOLA = 20;

  /* Espelhar TIRAR, não só somar.
     A sacola dela só somava no carrinho: quando a pessoa pedia "tira o shampoo",
     ela respondia "removi da sua sacola" e o produto continuava no carrinho da
     loja — ia para o checkout do mesmo jeito. Agora, quando um item some da
     sacola dela, a mesma quantidade sai do carrinho.
     Só mexe no que ESTE widget colocou por ela: o que a pessoa adicionou pelos
     cards, ou em outra aba, não é nosso para remover. */
  var ESPELHAR_REMOCAO = true;

  // Só para testar o fluxo com USE_MOCK = true: troque por variant_id REAIS da
  // loja (Admin > produto > variante, o número no fim da URL). Com 0 o mock
  // devolve ações inválidas de propósito e nada acontece.
  var MOCK_VARIANT_ADD = 0;
  var MOCK_VARIANT_REMOVE = 0;

  // A Sofia não tem bolinha própria: quem abre o chat é o item "Comprar agora"
  // dentro do menu "Fale conosco" do WhatsApp (snippets/whatsapp-float.liquid),
  // via window.SofiaWidget.open(). Ligue SHOW_LAUNCHER se um dia ela precisar
  // de um botão flutuante independente.
  var SHOW_LAUNCHER = false;

  /* ---------------------------------------------------------------------------
   * Empilhamento: quem fica por cima
   *
   * MEDIDO NO LIVE (16/09/2026): o popup da roleta (#phr-overlay, seção
   * custom-popup-roleta) roda em z-index 99999 e cobria o chat INTEIRO — clique
   * nenhum passava. E a aba .phr-open cobria justamente o campo de mensagem,
   * mesmo com o popup fechado. Na prática o chat ficava inutilizável no site.
   *
   * Duas medidas, as duas só enquanto o chat está aberto:
   *   1) o painel sobe acima do popup (Z_PAINEL);
   *   2) o que estiver em OCULTAR_QUANDO_ABERTO some da tela, e volta ao fechar.
   *
   * Some com o seletor da lista se um dia a roleta precisar aparecer por cima.
   * ------------------------------------------------------------------------- */
  var Z_PAINEL = 100050;
  var Z_LAUNCHER = 100049;
  var OCULTAR_QUANDO_ABERTO = ['#phr-overlay', '.phr-open'];

  // A bolinha do WhatsApp do tema ocupa o canto (right:20px / bottom:24px, 58px).
  // Com launcher, a Sofia empilha acima dela; sem launcher, o painel abre no
  // mesmo lugar onde estava o box "Fale conosco".
  var LAUNCHER_BOTTOM = 94;
  var LAUNCHER_RIGHT = 20;
  var LAUNCHER_BOTTOM_MOBILE = 92;
  var LAUNCHER_RIGHT_MOBILE = 14;
  var PANEL_BOTTOM = SHOW_LAUNCHER ? LAUNCHER_BOTTOM + 68 : LAUNCHER_BOTTOM;

  /* ---------------------------------------------------------------------------
   * Identificação de sessão (hash)
   *
   * LEIA ANTES DE MEXER: o cookie NÃO chega no backend sozinho.
   * O cookie nasce em lojaprohall.com.br e a API está em ws.prohall.app.br —
   * domínios registráveis diferentes, ou seja, cookie de terceiro. Safari/iOS
   * bloqueia por padrão (ITP) e o Chrome está indo pelo mesmo caminho. Por isso:
   *
   *   - o cookie serve só para LEMBRAR o hash entre pageviews/recarregamentos;
   *   - quem TRANSPORTA o hash é o corpo do JSON (campo `sessao`);
   *   - a requisição continua sem credenciais (`credentials: 'omit'`), então o
   *     backend não precisa de Allow-Credentials nem de origem espelhada.
   *
   * FORMATO exigido pelo backend: [A-Za-z0-9_-], 16 a 64 caracteres. O que
   * geramos ("s_" + 32 hex = 34) cabe com folga. Quem tem o hash TEM A CONVERSA,
   * que agora mora no servidor: não vai para a URL, não é derivado de e-mail nem
   * do cart token, não se reaproveita entre pessoas.
   *
   * SESSION_SCOPE:
   *   'cookie' → cookie primário da loja, renovado a cada mensagem (padrão)
   *   'tab'    → sessionStorage: dura a aba, morre ao fechar
   *   'memory' → só na memória do JS: recarregou a página, sessão nova
   * ------------------------------------------------------------------------- */
  // Quando chamar o POST /sessao: false = na PRIMEIRA abertura do painel (padrão),
  // true = no load da página. Fica false porque a maioria dos visitantes nunca
  // abre o chat, e esta loja já briga por main thread no carregamento.
  var SESSAO_NO_LOAD = false;

  var SESSION_SCOPE = 'cookie';
  var SESSION_COOKIE_NAME = 'ph_sofia_sid';
  var SESSION_TTL_MIN = 30;        // janela de inatividade, em minutos (rolling)
  var SESSION_COOKIE_DOMAIN = '';  // ex.: '.lojaprohall.com.br' p/ valer no www e no apex

  // O backend guarda o histórico e devolve em /sessao, então mandar `historico`
  // seria enviar a conversa duas vezes (e ele ignora o campo). Fica false;
  // existe só para um backend que volte a pedir o histórico no corpo.
  var SEND_LOCAL_HISTORY = false;

  /* ---------------------------------------------------------------------------
   * Identidade do agente
   *
   * Foto, cargo e artigo saem do elenco abaixo. Dá para forçar uma vendedora
   * específica por página ou campanha, pelo Liquid:
   *     window.PH_CHAT = { agente: { id: 'sami' } };
   * — nesse caso ela vale para aquela página, por cima do sorteio, e é o número
   * dela que vai no `persona` da requisição. Só vale para conversa NOVA: numa
   * sessão que o backend já tem, quem manda é a persona gravada lá.
   *
   * `artigo` existe porque em pt-BR a frase muda: "a Sofia" / "o Rafael".
   * Os nomes de classe CSS e o global window.SofiaWidget continuam como estão —
   * são identificadores internos, não texto de tela.
   * ------------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------------
   * O ELENCO — uma personalidade por conversa
   *
   * O backend tem QUATRO vendedoras, identificadas por NÚMERO no campo
   * `persona` (contrato de 17/09/2026):
   *
   *     1 Sofia Monteiro     consultiva, equilibrada
   *     2 Ana Beatriz       informal, "Oi, amiga!"
   *     3 Samira Machado    carismática, entusiasmada
   *     4 Mariana Oliveira  calma e elegante
   *
   * A tabela abaixo é o lado visual disso: foto, cargo e artigo. O `persona` de
   * cada entrada é o que amarra as duas pontas — é ele que viaja na requisição.
   *
   * COMO A ESCOLHA FUNCIONA, DOS DOIS LADOS
   *   - Aqui: cada visitante cai com UMA e fica com ela, derivado do hash da
   *     sessão (não de Math.random()). Mesmo hash, mesma vendedora, em qualquer
   *     página e depois de recarregar.
   *   - Lá: o número só vale na CRIAÇÃO da sessão. Depois disso a conversa é
   *     sempre daquela vendedora — mandar outro número nas mensagens seguintes
   *     não troca nada, de propósito.
   *
   * POR ISSO O NOME NA TELA SAI DA RESPOSTA, não daqui: as duas rotas devolvem
   * `persona: { numero, nome }`, e numa sessão que já existia pode ser outra
   * pessoa que a sorteada localmente. Ver receberPersona() e getAgente().
   *
   * `ativo: false` deixa a personalidade pronta mas fora do sorteio LOCAL — é o
   * jeito de tirar uma do ar sem apagar nada. (O backend tem o sorteio dele: se
   * a gente não mandar `persona`, ele escolhe entre as ativas de lá.)
   *
   * ESTADO EM 17/09/2026: as quatro no ar, cada uma com foto — um quarto dos
   * visitantes para cada.
   *
   * EM 15/10/2026 os nomes viraram nome e sobrenome. O PH CORE já respondia
   * assim (conferido no /sessao das quatro personas); aqui ainda estavam os
   * curtos, então o cabeçalho abria "Sami" e virava "Samira Machado" na
   * primeira resposta. Agora os dois lados dizem o mesmo e não pisca mais.
   *
   * OS NOMES AQUI SÃO SÓ O PALPITE INICIAL. Quem escreve no cabeçalho é o
   * `persona.nome` da RESPOSTA: se o backend responder outro nome para o mesmo
   * número, é o dele que aparece. Renomear uma vendedora é mudança dos DOIS
   * lados — aqui e no PH CORE —, senão a tela pisca um nome e assume o outro.
   *
   * As fotos são quadradas (552×552), já recortadas em círculo e com o busto
   * centralizado; daí o mesmo `zoom`/`foco` servir para as quatro. Foto em outro
   * formato provavelmente pede ajuste — conferir no círculo de 44px, não na arte.
   *
   * `zoom` e `foco` são opcionais e enquadram o rosto de cada foto no círculo de
   * 44px (o padrão está no CSS de .sofia-avatar img).
   * ------------------------------------------------------------------------- */
  var AGENTES = [
    {
      id: 'sofia',
      persona: 1,                                // número no backend (1 a 4)
      ativo: true,
      nome: 'Sofia Monteiro',
      artigo: 'a',                               // 'a' ou 'o'
      cargo: 'Consultora de beleza',             // subtítulo do cabeçalho
      apresentacao: 'consultora da Prohall',     // usado na saudação
      avatar: 'https://cdn.shopify.com/s/files/1/0895/5586/1810/files/sofa-novo.png?v=1789643560',
      zoom: 1.35,
      foco: '50% 28%'
    },
    {
      id: 'bia',
      persona: 2,
      ativo: true,
      nome: 'Ana Beatriz',
      artigo: 'a',
      cargo: 'Consultora de beleza',
      apresentacao: 'consultora da Prohall',
      avatar: 'https://cdn.shopify.com/s/files/1/0895/5586/1810/files/ana-bia.png?v=1789643560',
      zoom: 1.35,
      foco: '50% 28%'
    },
    {
      id: 'sami',
      persona: 3,
      ativo: true,
      nome: 'Samira Machado',
      artigo: 'a',
      cargo: 'Consultora de beleza',
      apresentacao: 'consultora da Prohall',
      avatar: 'https://cdn.shopify.com/s/files/1/0895/5586/1810/files/samira.png?v=1789643560',
      zoom: 1.35,
      foco: '50% 28%'
    },
    {
      id: 'mari',
      persona: 4,
      ativo: true,
      nome: 'Mariana Oliveira',
      artigo: 'a',
      cargo: 'Consultora de beleza',
      apresentacao: 'consultora da Prohall',
      avatar: 'https://cdn.shopify.com/s/files/1/0895/5586/1810/files/mari.png?v=1789651958',
      zoom: 1.35,
      foco: '50% 28%'
    }
  ];

  // false = todo mundo cai na primeira ativa, sem sorteio.
  var ELENCO_ROTATIVO = true;

  // Bolha de boas-vindas escrita pelo próprio widget, antes de qualquer
  // requisição. DESLIGADA por padrão a pedido da loja: quem apresenta a Sofia é
  // a primeira resposta dela, não um texto fixo do tema. O painel abre com a
  // conversa vazia (ou com o histórico que o /sessao devolver).
  var MOSTRAR_BOAS_VINDAS = false;

  var PERGUNTA_INICIAL =
    'Me conta o seu tipo de cabelo ou o que você está procurando que eu te indico o produto certo.';

  // Aviso de privacidade no cabeçalho: desligado a pedido da loja.
  // Vire true para trazer de volta a tarja.
  var SHOW_PRIVACY_NOTE = false;
  var PRIVACY_NOTE = SESSION_SCOPE === 'memory'
    ? 'Esta conversa não fica salva. Não envie dados pessoais.'
    : 'Guardamos esta conversa só para continuar o atendimento. Não envie dados pessoais.';

  /* ==========================================================================
   * 2. GUARDAS DE AMBIENTE
   * ========================================================================== */

  // Já carregado (script duplicado no tema)? Não faz nada, não quebra a página.
  if (window.SofiaWidget) return;

  // Navegador sem o mínimo necessário: sai em silêncio em vez de estourar erro.
  if (!window.document || !document.createElement || !window.Promise) return;

  var NS = 'sofia';
  var STYLE_ID = 'sofia-widget-style';

  /* ==========================================================================
   * 3. ESTADO (tudo em memória)
   * ========================================================================== */

  var state = {
    open: false,
    destroyed: false,
    cooldownUntil: 0,
    // history: só turnos que REALMENTE completaram (user + assistant).
    // Uma mensagem que falhou no envio não entra aqui — senão o backend
    // receberia um turno de usuário sem resposta correspondente.
    history: [],
    lastFocused: null,
    sessionId: null,    // hash da sessão; ver getSessionId()
    cartToken: null,    // token do carrinho visto pela última vez
    cartTokenAnterior: null, // token de antes da rotação (checkout/esvaziar)
    restoring: false,   // true enquanto remonta a conversa salva
    msgs: [],           // espelho da conversa para o sessionStorage

    /* ---- rajada (o debounce de 20s do backend) ----
     * emVoo: quantas requisições estão abertas agora. O campo NÃO trava com
     *   isso; serve para limitar a rajada em MAX_EM_VOO e manter o "digitando".
     * rajada: id do grupo atual. Enquanto houver requisição aberta, toda
     *   mensagem nova entra no MESMO grupo — porque o backend vai responder
     *   todas com o mesmo texto.
     * rajadaPintada / assinatura: qual grupo já virou bolha e com que conteúdo.
     *   É o par que joga no lixo a 2ª e a 3ª resposta idênticas. */
    emVoo: 0,
    rajada: 0,
    rajadaPintada: -1,
    assinatura: '',
    envios: [],         // timestamps dos últimos envios (teto de 12/min)
    dicaTimers: [],     // timers das DICAS_ESPERA em curso

    /* ---- sessão no servidor ---- */
    sessaoAberta: false,   // /sessao já respondeu nesta página
    // { numero, nome } que o backend confirmou para ESTA sessão. É a fonte da
    // verdade do nome na tela — o sorteio local é só o palpite inicial.
    persona: null,
    abrindoSessao: null,   // promise em curso, para não abrir duas vezes
    encerrada: false,      // o backend fechou a conversa (`encerrada: true`)
    soBoasVindas: false,   // a tela tem apenas a saudação local

    /* ---- sacola do servidor ---- */
    sacola: null,           // último `sacola` recebido, já normalizado
    sacolaSincronizada: '', // assinatura da sacola já jogada no carrinho nativo
    // variant_id -> quantidade que ESTE widget colocou no carrinho por conta da
    // sacola dela. É o que permite desfazer sem encostar no resto do carrinho.
    itensDaSofia: {},

    // Produtos que entraram no carrinho pelo card e que a Sofia ainda não sabe.
    // Esvazia quando a próxima mensagem leva o aviso junto.
    adicionados: []
  };

  var el = {};          // referências de DOM
  var timers = [];      // todo setTimeout/setInterval registrado aqui p/ destroy()
  var listeners = [];   // todo addEventListener registrado aqui p/ destroy()

  function track(id) { timers.push(id); return id; }
  function on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    listeners.push([target, type, fn, opts]);
  }

  /* ==========================================================================
   * 4. UTILITÁRIOS DE TEXTO — segurança
   * ========================================================================== */

  /**
   * Escapa os caracteres que dão sentido a HTML.
   * Roda SEMPRE antes de qualquer conversão de marcação.
   */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // URL "crua" dentro do texto já escapado. `<`, `>` e `"` não existem mais nesse
  // ponto (viraram entidades), então parar neles é seguro.
  var URL_RE = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

  // Marca invisível usada para tirar as URLs de cena antes de converter a
  // marcação. Montada em runtime de propósito: caractere de controle escrito
  // direto no arquivo faz editor/git tratarem o .js como binário.
  var SENT = String.fromCharCode(1);

  /**
   * Converte um marcador de WhatsApp (* _ ~) em tag.
   *
   * A abertura só vale no início da string/linha ou depois de espaço/pontuação de
   * abertura, e o conteúdo não pode começar nem terminar em espaço. É isso que
   * impede `snake_case` de virar itálico e um asterisco solto de virar negrito.
   * (Sem lookbehind de propósito: Safari antigo não suporta.)
   */
  function applyInline(str, marker, tag) {
    var m = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var re = new RegExp(
      '(^|[\\s(\\[{;>"\'' + SENT + '])' +   // 1: limite de abertura (consumido, devolvido em $1)
      m +
      '(?![\\s' + m + '])' +          // conteúdo não começa com espaço nem com o próprio marcador
      '([^' + m + '\\n]*[^' + m + '\\s\\n])' + // 2: conteúdo, sem quebra de linha, não termina em espaço
      m +
      // limite de fechamento (não consumido). O "&" entra porque, nesse ponto,
      // ele só aparece abrindo uma entidade (&amp; &quot; &lt; &gt;).
      '(?=$|[\\s.,!?:;)\\]}<&"\'' + SENT + '])',
      'g'
    );
    return str.replace(re, '$1<' + tag + '>$2</' + tag + '>');
  }

  function isSafeUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return /^https?:\/\//i.test(url.trim());
  }

  /**
   * Destino de botão nosso: URL absoluta OU caminho da própria loja.
   *
   * O `/checkout` do CTA é montado aqui dentro (shopRoot() + 'checkout'), nunca
   * vem da API — e absolutizar com location.origin quebra fora de http(s), que
   * é justamente onde o widget é testado (file://, preview). "//host" fica de
   * fora de propósito: é URL de outro domínio disfarçada de caminho.
   */
  function isSafeHref(url) {
    if (isSafeUrl(url)) return true;
    return typeof url === 'string' && url.charAt(0) === '/' && url.charAt(1) !== '/';
  }

  /**
   * Texto puro da API -> HTML seguro.
   *
   * ORDEM (não inverter):
   *   1) escapar HTML;
   *   2) tirar as URLs de cena (viram placeholders) para que * _ ~ dentro de uma
   *      URL não virem marcação e não quebrem o link;
   *   3) converter *negrito*, _itálico_, ~riscado~ e \n;
   *   4) devolver as URLs já como <a>.
   *
   * O passo 2 é só um detalhe de implementação da linkificação — o escape
   * continua sendo o primeiro passo, que é o que importa para XSS.
   * O texto cru NUNCA toca innerHTML.
   */
  function renderRichText(raw) {
    return renderTexto(raw).html;
  }

  /**
   * Faz o trabalho de verdade e devolve { html, botoes }.
   *
   * Com LINKS_COMO_BOTAO ligado, a URL sai do meio da frase e vira um botão
   * abaixo da bolha — URL crua no meio do texto é feia, quebra linha em lugar
   * ruim e ninguém lê "https://lojaprohall.com.br/collections/tratamento".
   */
  function renderTexto(raw) {
    // Tira caracteres de controle (inclusive um SENT que viesse da API e pudesse
    // se passar por placeholder de link). \n e \r ficam, são a quebra de linha.
    var clean = String(raw == null ? '' : raw).replace(/[\x00-\x09\x0B-\x1F\x7F]/g, '');
    var out = escapeHtml(clean);
    var links = [];
    var botoes = [];
    var vistos = {};

    out = out.replace(URL_RE, function (match) {
      // Pontuação colada no fim da frase não faz parte da URL.
      var trail = '';
      var tail = /[.,!?:;)\]}'"]+$/.exec(match);
      if (tail) {
        trail = match.slice(match.length - tail[0].length);
        match = match.slice(0, match.length - tail[0].length);
      }
      if (!match) return trail;

      var href = /^www\./i.test(match) ? 'https://' + match : match;

      if (LINKS_COMO_BOTAO) {
        // desescapa só para comparar/analisar; o href do botão é montado no DOM
        var url = href.replace(/&amp;/g, '&');
        if (!vistos[url] && botoes.length < MAX_BOTOES_LINK) {
          vistos[url] = true;
          botoes.push(url);
        }
        // A URL some do texto e o botão assume. Fica uma marca no lugar: é ela
        // que diz a limparSobras() ONDE limpar — linha sem link não se toca.
        return trail + SENT + 'X' + SENT;
      }

      // `href` e o texto já estão escapados (o "&" virou "&amp;", que o
      // navegador decodifica de volta para "&" ao ler o atributo).
      links.push(
        '<a class="' + NS + '-link" href="' + href +
        '" target="_blank" rel="noopener noreferrer">' + match + '</a>'
      );
      return SENT + 'L' + (links.length - 1) + SENT + trail;
    });

    if (LINKS_COMO_BOTAO && botoes.length) out = limparSobras(out);

    out = applyInline(out, '*', 'strong');
    out = applyInline(out, '_', 'em');
    out = applyInline(out, '~', 's');
    out = out.replace(/\r\n|\r|\n/g, '<br>');

    out = out.replace(new RegExp(SENT + 'L(\\d+)' + SENT, 'g'), function (all, i) {
      var link = links[Number(i)];
      return typeof link === 'string' ? link : '';
    });

    return { html: out, botoes: botoes };
  }

  /**
   * Depois de arrancar a URL sobra "Dá uma olhada em" ou "Link:" pendurado no
   * fim da linha. Corta o conector órfão e a pontuação solta, linha a linha.
   */
  function limparSobras(txt) {
    var MARCA = SENT + 'X' + SENT;
    var partes = txt.split(MARCA);
    if (partes.length === 1) return txt;

    var out = partes[0];
    for (var i = 1; i < partes.length; i++) {
      out = cortarCauda(out);    // só o pedaço imediatamente antes do link
      out += partes[i];
    }
    return out.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').replace(/\s+$/, '');
  }

  /** Limpa o fim da ÚLTIMA linha de `s` — o resto do texto não é tocado. */
  function cortarCauda(s) {
    var q = s.lastIndexOf('\n');
    var cabeca = q >= 0 ? s.slice(0, q + 1) : '';
    var l = (q >= 0 ? s.slice(q + 1) : s).replace(/[ \t]+$/, '');

    // Duas passadas: tirar a conjunção costuma expor uma preposição atrás.
    for (var n = 0; n < 2; n++) {
      // "… e também", "… e ainda", "… e", "… ou"
      l = l.replace(/\s+(?:e|ou)(?:\s+(?:tamb[ée]m|ainda))?\s*$/i, '');
      // preposição sem objeto: "Dá uma olhada em" → "Dá uma olhada"
      l = l.replace(/\s+(?:em|no|na|nos|nas|de|do|da|dos|das|por|pelo|pela|para|pra|com)\s*$/i, '');
      // pontuação que só existia para apresentar o link
      l = l.replace(/\s*[:\-–,;]\s*$/, '');
    }
    // "olha aqui ." → "olha aqui."
    l = l.replace(/\s+([.!?])\s*$/, '$1').replace(/[ \t]+$/, '');

    // "aqui", "o link", "neste link" ficam: leem bem logo acima de um botão.
    return cabeca + l;
  }

  /* ---------------------------------------------------------------------------
   * Rótulo do botão a partir da própria URL.
   *
   * Só o TIPO da página importa (produto, coleção, checkout). O slug não vira
   * mais texto: "Ver Select One Um So Passo" era comprido, sem acento e roubava
   * a cena da frase dela.
   * ------------------------------------------------------------------------- */

  /**
   * A loja atende em mais de um domínio (lojaprohall.com.br no ar,
   * *.myshopify.com no preview do tema). Sem essa lista, um link para o
   * domínio "de verdade" abriria em aba nova durante o teste no preview.
   */
  function mesmaLoja(u) {
    try {
      var host = String(u.host || '').toLowerCase().replace(/^www\./, '');
      var lista = [String(location.host || '').toLowerCase().replace(/^www\./, '')];

      try {
        if (window.Shopify && window.Shopify.shop) lista.push(String(window.Shopify.shop).toLowerCase());
      } catch (e) {}

      for (var i = 0; i < DOMINIOS_DA_LOJA.length; i++) {
        lista.push(String(DOMINIOS_DA_LOJA[i]).toLowerCase().replace(/^www\./, ''));
      }
      for (var j = 0; j < lista.length; j++) {
        if (lista[j] && lista[j] === host) return true;
      }
      return false;
    } catch (e) { return false; }
  }

  function rotuloDoLink(url) {
    var u;
    try { u = new URL(url, location.href); } catch (e) { return { texto: 'Abrir link', externo: true }; }

    var interno = mesmaLoja(u);
    var p = decodeURIComponent(u.pathname).replace(/\/+$/, '');
    var partes = p.split('/').filter(Boolean);
    // /pt-br/products/x, /en/collections/y — ignora prefixo de idioma/mercado
    if (partes.length && /^[a-z]{2}(-[a-z]{2})?$/i.test(partes[0])) partes.shift();
    var tipo = partes[0] || '';
    var slug = partes.length > 1 ? partes[partes.length - 1] : '';

    if (!interno) return { texto: 'Abrir ' + u.host.replace(/^www\./, ''), externo: true };

    // Permalink de compra: /checkout, /cart/c/... e também /cart/<variante>:1,
    // que é o formato que o backend manda e que leva direto para o checkout.
    if (/^checkouts?$/.test(tipo) || (tipo === 'cart' && (partes[1] === 'c' || /:\d+$/.test(slug)))) {
      return { texto: 'Finalizar compra', externo: false, destaque: true };
    }
    if (tipo === 'cart') return { texto: 'Ver meu carrinho', externo: false };
    if (tipo === 'products') return { texto: 'Ver produto', externo: false };
    if (tipo === 'collections') {
      if (slug === 'all' || !slug) return { texto: 'Ver todos os produtos', externo: false };
      return { texto: 'Ver coleção', externo: false };
    }
    if (tipo === 'blogs') return { texto: 'Ler artigo', externo: false };
    if (tipo === 'pages') return { texto: 'Abrir página', externo: false };
    if (tipo === 'search') return { texto: 'Ver resultados', externo: false };
    if (!partes.length) return { texto: 'Ir para a loja', externo: false };
    return { texto: 'Acessar', externo: false };
  }

  /**
   * Monta os botões de link de uma resposta.
   *
   * O rótulo é CURTO de propósito (decisão da loja, 17/09/2026): o nome do
   * produto vindo do slug dava barras compridas e sem acento ("Ver Select One
   * Um So Passo"), que competiam com a bolha e ainda erravam o nome. Quem diz
   * o que é o produto é a frase dela; o botão só diz o que o clique faz.
   *
   * Destaque (verde cheio) é só para o link de COMPRA. Se todo link virasse
   * verde, nenhum seria destaque.
   */
  /**
   * Nome curto tirado do slug, SÓ para desempatar dois botões iguais.
   * "kit-reparacao-total" -> "Kit reparacao total", cortado em 18 caracteres.
   */
  function apelidoDoSlug(url) {
    try {
      var p = decodeURIComponent(new URL(url, location.href).pathname).replace(/\/+$/, '');
      var partes = p.split('/').filter(Boolean);
      var slug = partes.length ? partes[partes.length - 1] : '';
      slug = slug.replace(/[-_]+/g, ' ').trim();
      if (!slug) return '';
      slug = slug.charAt(0).toUpperCase() + slug.slice(1);
      return slug.length > 18 ? slug.slice(0, 17).replace(/\s+\S*$/, '') + '…' : slug;
    } catch (e) { return ''; }
  }

  function buildBotoesLink(urls, jaUsado) {
    if (!urls || !urls.length) return null;
    var wrap = null;

    // 1ª passada: o que cada link vira e quantas vezes o rótulo se repete.
    var itens = [];
    var quantos = {};
    for (var k = 0; k < urls.length; k++) {
      if (!isSafeUrl(urls[k])) continue;
      if (jaUsado && urls[k] === jaUsado) continue;   // não repete o link_carrinho
      var r = rotuloDoLink(urls[k]);
      itens.push({ url: urls[k], info: r });
      quantos[r.texto] = (quantos[r.texto] || 0) + 1;
    }

    // Duas coleções na mesma resposta virariam dois "Ver coleção" — aí o rótulo
    // curto deixa de dizer o que o clique faz. Só nesse caso o nome da página
    // entra, nos DOIS botões, e ainda curto.
    for (var d = 0; d < itens.length; d++) {
      if (quantos[itens[d].info.texto] < 2) continue;
      var apelido = apelidoDoSlug(itens[d].url);
      if (!apelido) continue;
      itens[d].info = {
        texto: itens[d].info.texto + ' · ' + apelido,
        externo: itens[d].info.externo,
        destaque: itens[d].info.destaque
      };
    }

    for (var i = 0; i < itens.length; i++) {
      var url = itens[i].url;
      var info = itens[i].info;
      var b = h('a', NS + '-linkbtn' + (info.destaque ? ' ' + NS + '-linkbtn--forte' : ''), {
        href: url,
        // link da própria loja abre na mesma aba: o chat continua aberto do
        // outro lado (sessionStorage), então o visitante não perde a conversa.
        target: info.externo ? '_blank' : '_self',
        rel: 'noopener noreferrer'
      });
      var txt = h('span', NS + '-linkbtn__txt');
      txt.textContent = info.texto;
      b.appendChild(txt);
      var ico = h('span', NS + '-linkbtn__ico', { 'aria-hidden': 'true' });
      ico.innerHTML = info.externo ? ICONS.externo : ICONS.seta;
      b.appendChild(ico);

      if (!wrap) wrap = h('div', NS + '-linkbtns');
      wrap.appendChild(b);
    }
    return wrap;
  }

  /* ==========================================================================
   * 5. CSS (injetado em <style>, sem arquivo externo)
   * ========================================================================== */

  var CSS = [
    /* Nunca herdar box-sizing do tema: sem isso o padding estoura a altura do campo. */
    '.' + NS + '-panel,.' + NS + '-panel *,.' + NS + '-launcher,.' + NS + '-launcher *{',
    '  box-sizing:border-box;',
    '}',
    /* Paleta: cinza no painel inteiro, verde SÓ nas ações de compra (adicionar
       ao carrinho, finalizar). Antes o acento era bronze e vazava para tudo —
       borda, preço, ícone, barra da sacola. */
    ':root{',
    '  --' + NS + '-ink:#16171a;',
    '  --' + NS + '-ink-2:#232529;',
    '  --' + NS + '-ink-soft:#5b5f66;',
    '  --' + NS + '-accent:#4c5057;',        // grafite: AA com texto branco
    '  --' + NS + '-accent-2:#6b7078;',
    '  --' + NS + '-accent-tint:#f1f2f3;',
    /* Verde da loja (#3cbd7a): o mesmo do botão COMPRAR da PDP e da "Cor de
       destaque" do tema. Chapado, sem degradê, a pedido da loja.
       `cta-forte` é o mesmo verde escurecido, e existe para TEXTO: branco sobre
       o verde da loja dá ~2,4:1 de contraste, e verde claro sobre fundo claro
       fica ilegível. Fundo grande usa o verde da marca; letra pequena e ícone
       usam o forte. */
    '  --' + NS + '-cta:#3cbd7a;',
    '  --' + NS + '-cta-hover:#34a76c;',
    '  --' + NS + '-cta-forte:#137a4b;',
    '  --' + NS + '-cta-tint:#eaf7f0;',
    '  --' + NS + '-cta-line:#bde3cf;',
    '  --' + NS + '-surface:#ffffff;',
    '  --' + NS + '-canvas:#f7f7f8;',        // fundo da conversa, cinza neutro
    '  --' + NS + '-line:#e6e7e8;',
    '  --' + NS + '-line-soft:#eff0f1;',
    '  --' + NS + '-online:#2fbf71;',
    '}',

    /* ---------- launcher (opcional) ---------- */
    '.' + NS + '-launcher{',
    '  position:fixed;right:' + LAUNCHER_RIGHT + 'px;bottom:' + LAUNCHER_BOTTOM + 'px;',
    '  z-index:' + Z_LAUNCHER + ';width:56px;height:56px;padding:0;margin:0;border:0;',
    '  border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;',
    '  background:var(--' + NS + '-ink);color:#fff;font:inherit;',
    '  transition:transform .18s ease, box-shadow .18s ease;',
    '  -webkit-tap-highlight-color:transparent;',
    '}',
    '.' + NS + '-launcher:hover{transform:translateY(-2px);}',
    '.' + NS + '-launcher:focus-visible{outline:3px solid var(--' + NS + '-accent);outline-offset:3px;}',
    '.' + NS + '-launcher svg{width:26px;height:26px;display:block;pointer-events:none;}',
    '.' + NS + '-launcher__dot{',
    '  position:absolute;top:2px;right:2px;width:12px;height:12px;border-radius:50%;',
    '  background:var(--' + NS + '-accent);border:2px solid #fff;display:block !important;',
    '}',

    /* ---------- painel ---------- */
    '.' + NS + '-panel{',
    '  position:fixed;right:' + LAUNCHER_RIGHT + 'px;bottom:' + PANEL_BOTTOM + 'px;',
    '  z-index:' + Z_PAINEL + ';width:384px;max-width:calc(100vw - 32px);height:580px;',
    '  max-height:calc(100vh - ' + (PANEL_BOTTOM + 32) + 'px);',
    '  display:none;flex-direction:column;overflow:hidden;',
    '  background:var(--' + NS + '-surface);color:var(--' + NS + '-ink);',
    '  border:1px solid var(--' + NS + '-line);border-radius:20px;',
    '  font-family:inherit;font-size:14px;line-height:1.55;',
    '  -webkit-font-smoothing:antialiased;',
    '}',
    '.' + NS + '-panel.is-open{display:flex;animation:' + NS + '-in .26s cubic-bezier(.16,1,.3,1);}',
    '@keyframes ' + NS + '-in{from{opacity:0;transform:translateY(14px) scale(.97);}to{opacity:1;transform:none;}}',

    /* ---------- cabeçalho ---------- */
    '.' + NS + '-header{',
    '  flex:0 0 auto;padding:14px 14px 14px 16px;',
    '  background:linear-gradient(160deg,var(--' + NS + '-ink-2) 0%,var(--' + NS + '-ink) 100%);',
    '  color:#fff;',
    '}',
    '.' + NS + '-header__row{display:flex;align-items:center;gap:12px;}',
    /* A caixa existe só para pendurar a bolinha de online na borda da foto: o
       avatar tem overflow:hidden (é ele que recorta o retrato), então uma
       bolinha dentro dele seria cortada. */
    '.' + NS + '-avatarbox{',
    '  position:relative;flex:0 0 44px;width:44px;height:44px;',
    '}',
    '.' + NS + '-avatarbox .' + NS + '-dot--foto{',
    // centro da bolinha em cima da borda do círculo (44px de avatar): meio
    // dentro, meio fora, que é o que faz ela parecer presa à foto
    '  position:absolute;right:1px;bottom:1px;z-index:1;',
    '  width:10px;height:10px;flex:none;border-radius:50%;',
    '  background:var(--' + NS + '-online);',
    // o aro escuro é o que separa a bolinha da foto; acompanha o topo do
    // degradê do cabeçalho, que é onde o avatar fica
    '  box-shadow:0 0 0 2px #232529;',
    '  display:block !important;',
    '}',
    '.' + NS + '-avatar{',
    '  width:44px;height:44px;flex:0 0 44px;border-radius:50%;overflow:hidden;',
    '  background:linear-gradient(145deg,var(--' + NS + '-accent-2),var(--' + NS + '-accent));',
    '  color:#fff;display:flex;align-items:center;justify-content:center;',
    '  font-weight:600;font-size:17px;letter-spacing:.01em;',
    '}',
    /* Com foto o fundo fica escuro: o PNG da Sofia já vem com o próprio disco
       preto e os cantos transparentes — um fundo branco aqui vazaria como um
       aro claro na borda. O anel continua para o disco não sumir no preto do
       cabeçalho.
       Sem foto, some a classe e volta o degradê com a inicial (branca) — por
       isso o fundo NÃO pode estar na regra de cima. */
    '.' + NS + '-avatar--foto{',
    '  background:#15171a;',
    '}',
    '.' + NS + '-avatar img{',
    '  width:100%;height:100%;object-fit:cover;display:block;',
    /* A arte é um busto dentro de um disco, com folga sobrando: no círculo de
       44px o rosto ficava pequeno. Zoom leve, ancorado na altura dos olhos —
       acima de ~1.4 o cabelo começa a ser cortado nas laterais. */
    '  transform:scale(1.35);transform-origin:50% 28%;',
    '}',
    '.' + NS + '-titles{min-width:0;flex:1 1 auto;}',
    '.' + NS + '-title{',
    '  margin:0;font-size:15.5px;font-weight:600;color:#fff;line-height:1.25;letter-spacing:.005em;',
    '  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;',
    '}',
    '.' + NS + '-subtitle{',
    '  margin:3px 0 0;font-size:12px;line-height:1.3;color:#b9bcc2;font-weight:400;',
    '  display:flex;align-items:center;gap:6px;',
    '  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;',
    '}',
    '.' + NS + '-dot{',
    '  width:6px;height:6px;flex:0 0 6px;border-radius:50%;',
    '  background:var(--' + NS + '-online);display:block !important;',
    '}',
    '.' + NS + '-close{',
    '  flex:0 0 auto;width:32px;height:32px;padding:0;border:0;border-radius:50%;cursor:pointer;',
    '  background:rgba(255,255,255,.10);color:#d6d8dc;display:flex;align-items:center;justify-content:center;',
    '  font:inherit;transition:background-color .15s ease,color .15s ease;',
    '}',
    '.' + NS + '-close:hover{background:rgba(255,255,255,.2);color:#fff;}',
    '.' + NS + '-close:focus-visible{outline:2px solid #fff;outline-offset:2px;}',
    '.' + NS + '-close svg{width:15px;height:15px;display:block;pointer-events:none;}',
    '.' + NS + '-privacy{',
    '  margin:12px 0 0;padding:8px 10px;border-radius:10px;',
    '  background:rgba(255,255,255,.08);color:#d9dade;',
    '  font-size:11.5px;line-height:1.35;display:flex;gap:6px;align-items:flex-start;',
    '}',
    '.' + NS + '-privacy svg{width:13px;height:13px;flex:0 0 auto;margin-top:1px;}',

    /* ---------- mensagens ---------- */
    '.' + NS + '-log{',
    '  flex:1 1 auto;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;',
    '  padding:18px 16px 8px;display:flex;flex-direction:column;gap:12px;',
    '  background:var(--' + NS + '-canvas);',
    '  scrollbar-width:thin;scrollbar-color:#d8d6d3 transparent;',
    '}',
    '.' + NS + '-log::-webkit-scrollbar{width:8px;}',
    '.' + NS + '-log::-webkit-scrollbar-track{background:transparent;}',
    '.' + NS + '-log::-webkit-scrollbar-thumb{background:#dcdad7;border:2px solid var(--' + NS + '-canvas);border-radius:8px;}',
    '.' + NS + '-log::-webkit-scrollbar-thumb:hover{background:#c8c5c1;}',

    '.' + NS + '-row{display:flex;flex-direction:column;width:100%;max-width:100%;min-width:0;}',
    '.' + NS + '-row--user{align-items:flex-end;}',
    '.' + NS + '-row--bot,.' + NS + '-row--sys{align-items:flex-start;}',
    '.' + NS + '-bubble{',
    '  max-width:84%;padding:11px 14px;font-size:14px;line-height:1.55;',
    '  word-wrap:break-word;overflow-wrap:anywhere;',
    '  animation:' + NS + '-pop .2s cubic-bezier(.16,1,.3,1);',
    '}',
    '@keyframes ' + NS + '-pop{from{opacity:0;transform:translateY(5px);}to{opacity:1;transform:none;}}',
    '.' + NS + '-bubble--bot{',
    '  background:var(--' + NS + '-surface);color:var(--' + NS + '-ink);',
    '  border:1px solid var(--' + NS + '-line-soft);',
    '  border-radius:16px 16px 16px 5px;',
    '}',
    '.' + NS + '-bubble--user{',
    '  background:linear-gradient(160deg,var(--' + NS + '-ink-2),var(--' + NS + '-ink));color:#fff;',
    '  border-radius:16px 16px 5px 16px;',
    '}',
    /* Confirmação de ação: fica do lado da pessoa, porque foi ELA que mandou
       adicionar — o widget só está devolvendo o "feito". Some do lado da Sofia
       para não parecer que quem falou foi a IA. */
    '.' + NS + '-bubble--acao{',
    '  display:flex !important;align-items:center;gap:8px;',
    '  padding:9px 13px;font-size:13px;',
    '}',
    '.' + NS + '-bubble--acao .' + NS + '-check{',
    '  flex:0 0 16px;display:flex !important;color:var(--' + NS + '-cta-forte);',
    '}',
    /* bolha da agente é branca: o verde escuro do CTA é o que tem contraste */
    '.' + NS + '-bubble--acao{font-weight:600;color:var(--' + NS + '-ink);}',
    '.' + NS + '-bubble--acao .' + NS + '-check svg{width:16px;height:16px;}',
    '.' + NS + '-bubble--sys{',
    '  background:var(--' + NS + '-accent-tint);color:var(--' + NS + '-ink-soft);border:1px solid var(--' + NS + '-line);',
    '  font-size:13px;width:100%;max-width:100%;border-radius:12px;',
    '}',
    '.' + NS + '-bubble strong{font-weight:650;}',
    '.' + NS + '-bubble em{font-style:italic;}',
    '.' + NS + '-bubble br{display:inline !important;}',
    '.' + NS + '-link{color:inherit;text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:2px;}',
    '.' + NS + '-bubble--user .' + NS + '-link{color:#fff;}',

    /* ---------- "digitando" ---------- */
    '.' + NS + '-typing{display:flex;gap:4px;align-items:center;padding:13px 15px;}',
    '.' + NS + '-typing i{',
    '  width:6px;height:6px;border-radius:50%;background:#b5b2ae;display:block !important;',
    '  animation:' + NS + '-blink 1.3s infinite ease-in-out;',
    '}',
    '.' + NS + '-typing i:nth-child(2){animation-delay:.16s;}',
    '.' + NS + '-typing i:nth-child(3){animation-delay:.32s;}',
    '@keyframes ' + NS + '-blink{0%,80%,100%{opacity:.3;transform:translateY(0);}40%{opacity:1;transform:translateY(-3px);}}',

    /* ---------- cards de produto ----------
       Sem barra de rolagem: no desktop arrasta com o mouse (ver ativarArrasto),
       no celular o swipe nativo continua valendo. */
    '.' + NS + '-cards{',
    '  display:flex;gap:10px;margin-top:10px;padding:2px 2px 10px;',
    '  width:100%;max-width:100%;min-width:0;',
    '  overflow-x:auto;-webkit-overflow-scrolling:touch;scroll-snap-type:x proximity;',
    '  scrollbar-width:none;-ms-overflow-style:none;cursor:grab;',
    '}',
    '.' + NS + '-cards::-webkit-scrollbar{display:none;width:0;height:0;}',
    '.' + NS + '-cards.is-dragging{cursor:grabbing;scroll-snap-type:none;scroll-behavior:auto;}',
    '.' + NS + '-card{',
    '  flex:0 0 152px;scroll-snap-align:start;background:#fff;',
    '  border:1px solid var(--' + NS + '-line-soft);border-radius:14px;overflow:hidden;',
    '  display:flex;flex-direction:column;',
    '  transition:border-color .15s ease,box-shadow .15s ease,transform .12s ease;',
    '}',
    '.' + NS + '-card--link{cursor:pointer;}',
    '.' + NS + '-card--link:hover{border-color:#ded9d2;transform:translateY(-1px);}',
    '.' + NS + '-card--link:focus-visible{outline:2px solid var(--' + NS + '-accent);outline-offset:2px;}',
    '.' + NS + '-card__media{',
    '  width:100%;aspect-ratio:1/1;background:#f4f2f0;',
    '  display:flex !important;align-items:center;justify-content:center;overflow:hidden;',
    '}',
    '.' + NS + '-card__media img{width:100%;height:100%;object-fit:cover;display:block;}',
    '.' + NS + '-card__ph{width:32px;height:32px;opacity:.3;display:block;}',
    '.' + NS + '-card__body{padding:10px 11px 11px;display:flex;flex-direction:column;gap:3px;flex:1 1 auto;}',
    /* Pesos vindos do card de produto da loja (título 400/500, preço 500): o
       card do chat estava mais pesado que o da vitrine e destoava. Tamanhos
       menores porque o card aqui tem 152px, não a largura da grade. */
    '.' + NS + '-card__name{',
    '  font-size:13px;font-weight:500;line-height:1.4;color:var(--' + NS + '-ink);margin:0;',
    '  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;',
    '}',
    '.' + NS + '-card__price{font-size:14.5px;font-weight:600;color:var(--' + NS + '-ink);margin:3px 0 10px;letter-spacing:-.01em;}',
    /* Mesmo desenho do "COMPRAR" da vitrine: verde cheio, branco, caixa alta e
       uma palavra só — em 152px, "ADICIONAR AO CARRINHO" em caixa alta quebraria
       em duas linhas e pesaria mais que o próprio produto. */
    '.' + NS + '-card__cta{',
    '  margin-top:auto;display:flex !important;align-items:center;justify-content:center;gap:6px;',
    '  width:100%;text-align:center;text-decoration:none;cursor:pointer;',
    '  padding:10px 6px;border-radius:8px;border:1px solid var(--' + NS + '-cta);',
    '  background:var(--' + NS + '-cta);color:#fff;',
    '  font-size:11.5px;font-weight:600;font-family:inherit;line-height:1.2;',
    '  letter-spacing:.04em;text-transform:uppercase;',
    '  transition:background-color .15s ease,border-color .15s ease;',
    '}',
    '.' + NS + '-card__cta:hover:not(:disabled){background:var(--' + NS + '-cta-hover);border-color:var(--' + NS + '-cta-hover);color:#fff;}',
    '.' + NS + '-card__cta .' + NS + '-check{flex:0 0 14px;display:flex !important;color:#fff;}',
    '.' + NS + '-card__cta .' + NS + '-check svg{width:14px;height:14px;}',
    '.' + NS + '-card__cta:focus-visible{outline:2px solid var(--' + NS + '-accent);outline-offset:2px;}',
    '.' + NS + '-card__cta:disabled{opacity:.65;cursor:default;}',
    /* Já no carrinho: o mesmo verde, um tom mais escuro, com o check de traço. */
    '.' + NS + '-card__cta.is-ok{',
    '  background:var(--' + NS + '-cta-forte);border-color:var(--' + NS + '-cta-forte);color:#fff;opacity:1;',
    '}',
    '.' + NS + '-card__err{margin:6px 0 0;font-size:10.5px;line-height:1.35;color:#8c2f2f;}',

    /* vitrine com UM produto: card deitado ocupando a largura da bolha.
       Um card de 152px sozinho ao lado de 220px vazios parece bug de layout. */
    '.' + NS + '-cards--solo{overflow:visible;padding-bottom:2px;}',
    '.' + NS + '-cards--solo .' + NS + '-card{flex:1 1 auto;width:100%;flex-direction:row;}',
    '.' + NS + '-cards--solo .' + NS + '-card__media{',
    '  width:96px;flex:0 0 96px;aspect-ratio:auto;align-self:stretch;min-height:96px;',
    '}',
    '.' + NS + '-cards--solo .' + NS + '-card__body{justify-content:center;padding:11px 12px;}',
    '.' + NS + '-cards--solo .' + NS + '-card__name{font-size:13px;-webkit-line-clamp:3;}',

    /* ---------- botões de link (todo link do texto vira botão) ---------- */
    '.' + NS + '-linkbtns{',
    '  display:flex;flex-direction:column;gap:6px;align-self:stretch;margin-top:8px;',
    '}',
    '.' + NS + '-linkbtn{',
    '  display:flex;align-items:center;justify-content:space-between;gap:10px;',
    '  padding:10px 12px 10px 14px;border-radius:12px;text-decoration:none;cursor:pointer;',
    '  background:#fff;border:1px solid var(--' + NS + '-line);color:var(--' + NS + '-ink);',
    '  font-size:13px;font-weight:600;line-height:1.3;font-family:inherit;',
    '  transition:border-color .15s ease,background-color .15s ease,transform .12s ease;',
    '}',
    '.' + NS + '-linkbtn:hover{border-color:var(--' + NS + '-accent);background:var(--' + NS + '-accent-tint);color:var(--' + NS + '-ink);transform:translateY(-1px);}',
    '.' + NS + '-linkbtn:focus-visible{outline:2px solid var(--' + NS + '-accent);outline-offset:2px;}',
    '.' + NS + '-linkbtn__txt{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}',
    '.' + NS + '-linkbtn__ico{flex:0 0 auto;display:flex !important;color:var(--' + NS + '-accent);}',
    '.' + NS + '-linkbtn__ico svg{width:15px;height:15px;}',
    /* link que leva ao checkout herda o peso do CTA principal */
    '.' + NS + '-linkbtn--forte{',
    '  background:var(--' + NS + '-cta);',
    '  border-color:transparent;color:#fff;',
    '}',
    '.' + NS + '-linkbtn--forte:hover{background:var(--' + NS + '-cta-hover);color:#fff;border-color:transparent;}',
    '.' + NS + '-linkbtn--forte .' + NS + '-linkbtn__ico{color:#fff;}',

    /* ---------- finalizar compra ---------- */
    '.' + NS + '-checkout{',
    '  display:block;align-self:stretch;margin-top:10px;padding:12px 16px;border-radius:12px;text-align:center;',
    '  background:var(--' + NS + '-cta);',
    '  color:#fff;text-decoration:none;font-weight:600;font-size:13.5px;letter-spacing:.01em;',
    '  transition:background-color .15s ease,transform .15s ease;',
    '}',
    '.' + NS + '-checkout:hover{background:var(--' + NS + '-cta-hover);transform:translateY(-1px);color:#fff;}',
    '.' + NS + '-checkout:focus-visible{outline:2px solid var(--' + NS + '-ink);outline-offset:2px;}',

    /* ---------- repetir ---------- */
    '.' + NS + '-retry{',
    '  margin-top:8px;padding:7px 12px;border-radius:9px;cursor:pointer;font-family:inherit;',
    '  border:1px solid var(--' + NS + '-line);background:#fff;color:var(--' + NS + '-ink-soft);font-size:12.5px;font-weight:600;',
    '  transition:background-color .15s ease,color .15s ease;',
    '}',
    '.' + NS + '-retry:hover{background:var(--' + NS + '-accent);color:#fff;border-color:var(--' + NS + '-accent);}',

    /* ---------- ações de carrinho ---------- */
    '.' + NS + '-acts{',
    '  align-self:stretch;margin-top:10px;padding:12px;border-radius:12px;',
    '  background:var(--' + NS + '-accent-tint);border:1px solid var(--' + NS + '-line);',
    '}',
    '.' + NS + '-acts__title{',
    '  margin:0 0 8px;font-size:12.5px;font-weight:700;color:var(--' + NS + '-ink);',
    '}',
    '.' + NS + '-acts__list{margin:0 0 10px;padding:0 0 0 16px;list-style:disc;}',
    '.' + NS + '-acts__item{font-size:12.5px;line-height:1.5;color:var(--' + NS + '-ink-soft);}',
    '.' + NS + '-acts__row{display:flex;gap:8px;flex-wrap:wrap;}',
    '.' + NS + '-acts__ok,.' + NS + '-acts__no{',
    '  padding:8px 14px;border-radius:9px;cursor:pointer;font-family:inherit;',
    '  font-size:12.5px;font-weight:600;transition:background-color .15s ease,color .15s ease;',
    '}',
    '.' + NS + '-acts__ok{border:1px solid var(--' + NS + '-cta);background:var(--' + NS + '-cta);color:#fff;}',
    '.' + NS + '-acts__ok:hover{background:var(--' + NS + '-cta-hover);border-color:var(--' + NS + '-cta-hover);}',
    '.' + NS + '-acts__no{border:1px solid var(--' + NS + '-line);background:#fff;color:var(--' + NS + '-ink-soft);}',
    '.' + NS + '-acts__no:hover{background:var(--' + NS + '-accent-tint);}',
    '.' + NS + '-acts__ok:focus-visible,.' + NS + '-acts__no:focus-visible{outline:2px solid var(--' + NS + '-ink);outline-offset:2px;}',
    '.' + NS + '-acts__note,.' + NS + '-acts__done,.' + NS + '-acts__err{',
    '  margin:0;font-size:12.5px;line-height:1.5;color:var(--' + NS + '-ink-soft);',
    '}',
    '.' + NS + '-acts__done{font-weight:600;}',
    '.' + NS + '-acts__err{color:#8c2f2f;}',

    /* ---------- sacola do servidor ----------
       Fica entre a conversa e o campo: recolhida mostra contagem e total, e é a
       única coisa na tela que fala da sacola montada pela Sofia. */
    '.' + NS + '-bag{flex:0 0 auto;background:#fff;display:none;}',
    '.' + NS + '-bag.is-on{display:block;}',
    '.' + NS + '-bag__head{',
    '  display:flex;align-items:center;gap:10px;width:100%;margin:0;',
    '  padding:10px 14px;border:0;border-top:1px solid var(--' + NS + '-line);cursor:pointer;',
    '  background:var(--' + NS + '-accent-tint);color:var(--' + NS + '-ink);',
    '  font:inherit;text-align:left;',
    '}',
    '.' + NS + '-bag__head:hover{background:#e9eaec;}',
    '.' + NS + '-bag__head:focus-visible{outline:2px solid var(--' + NS + '-accent);outline-offset:-2px;}',
    '.' + NS + '-bag__ico{flex:0 0 auto;display:flex !important;color:var(--' + NS + '-accent);}',
    '.' + NS + '-bag__ico svg{width:17px;height:17px;}',
    '.' + NS + '-bag__label{',
    '  flex:1 1 auto;min-width:0;font-size:12.5px;font-weight:700;line-height:1.3;',
    '}',
    '.' + NS + '-bag__sub{display:block;margin-top:1px;font-size:11.5px;font-weight:400;color:var(--' + NS + '-ink-soft);}',
    '.' + NS + '-bag__chev{flex:0 0 auto;display:flex !important;color:#8b8f95;transition:transform .18s ease;}',
    '.' + NS + '-bag__chev svg{width:15px;height:15px;transform:rotate(90deg);}',
    '.' + NS + '-bag.is-open .' + NS + '-bag__chev{transform:rotate(180deg);}',
    '.' + NS + '-bag__body{display:none;padding:8px 14px 0;max-height:196px;overflow-y:auto;scrollbar-width:thin;}',
    '.' + NS + '-bag.is-open .' + NS + '-bag__body{display:block;}',
    '.' + NS + '-bag__item{display:flex;gap:9px;align-items:center;padding:6px 0;}',
    '.' + NS + '-bag__item+.' + NS + '-bag__item{border-top:1px solid var(--' + NS + '-line-soft);}',
    /* grupo dos brindes */
    '.' + NS + '-bag__grupo{',
    '  margin:10px 0 2px;padding-top:9px;border-top:1px solid var(--' + NS + '-line);',
    '  font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;',
    '  color:var(--' + NS + '-ink-soft);',
    '}',
    '.' + NS + '-bag__grupo+.' + NS + '-bag__item{border-top:0;}',
    '.' + NS + '-bag__item--brinde .' + NS + '-bag__meta{color:var(--' + NS + '-cta-forte);font-weight:600;}',
    '.' + NS + '-bag__item--brinde .' + NS + '-bag__thumb{border:1px dashed var(--' + NS + '-cta-line);}',
    '.' + NS + '-bag__thumb{',
    '  width:36px;height:36px;flex:0 0 36px;border-radius:8px;overflow:hidden;background:#f4f2f0;',
    '  display:flex !important;align-items:center;justify-content:center;',
    '}',
    '.' + NS + '-bag__thumb img{width:100%;height:100%;object-fit:cover;display:block;}',
    '.' + NS + '-bag__thumb svg{width:18px;height:18px;opacity:.35;}',
    '.' + NS + '-bag__txt{min-width:0;flex:1 1 auto;}',
    '.' + NS + '-bag__name{',
    '  margin:0;font-size:12px;font-weight:600;line-height:1.35;color:var(--' + NS + '-ink);',
    '  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;',
    '}',
    '.' + NS + '-bag__meta{margin:2px 0 0;font-size:11.5px;line-height:1.3;color:var(--' + NS + '-ink-soft);}',
    '.' + NS + '-bag__foot{padding:10px 14px 12px;}',
    '.' + NS + '-bag__cta{',
    '  display:block;width:100%;padding:11px 14px;border:0;border-radius:11px;cursor:pointer;',
    '  background:var(--' + NS + '-cta);',
    '  color:#fff;font-family:inherit;font-size:13px;font-weight:600;text-align:center;text-decoration:none;',
    '  transition:background-color .15s ease;',
    '}',
    '.' + NS + '-bag__cta:hover{background:var(--' + NS + '-cta-hover);color:#fff;}',
    '.' + NS + '-bag__cta:disabled{opacity:.6;cursor:default;box-shadow:none;filter:none;}',
    '.' + NS + '-bag__cta:focus-visible{outline:2px solid var(--' + NS + '-ink);outline-offset:2px;}',
    /* "Finalizar compra" e "Ver meu carrinho" dividem a linha: o painel já come
       altura da conversa, e empilhar dois botões comia mais. */
    '.' + NS + '-bag__acoes{display:flex;gap:8px;align-items:stretch;}',
    '.' + NS + '-bag__acoes .' + NS + '-bag__cta{flex:1 1 56%;min-width:0;}',
    '.' + NS + '-bag__acoes .' + NS + '-bag__2nd{flex:1 1 44%;min-width:0;margin-top:0;}',
    '.' + NS + '-bag__acoes .' + NS + '-bag__cta,.' + NS + '-bag__acoes .' + NS + '-bag__2nd{',
    '  display:flex;align-items:center;justify-content:center;padding:11px 8px;line-height:1.2;',
    '}',
    '.' + NS + '-bag__2nd{',
    '  display:block;width:100%;margin-top:7px;padding:9px 14px;border-radius:10px;cursor:pointer;',
    '  border:1px solid var(--' + NS + '-line);background:#fff;color:var(--' + NS + '-ink);',
    '  font-family:inherit;font-size:12.5px;font-weight:600;text-align:center;text-decoration:none;',
    '}',
    '.' + NS + '-bag__2nd:hover{border-color:var(--' + NS + '-accent);background:var(--' + NS + '-accent-tint);color:var(--' + NS + '-ink);}',
    '.' + NS + '-bag__note{margin:8px 0 0;font-size:11.5px;line-height:1.45;color:var(--' + NS + '-ink-soft);text-align:center;}',
    '.' + NS + '-bag__note--err{color:#8c2f2f;}',
    '.' + NS + '-bag__note--ok{color:var(--' + NS + '-cta-forte);font-weight:600;}',

    /* ---------- composer ---------- */
    '.' + NS + '-foot{',
    '  flex:0 0 auto;border-top:1px solid var(--' + NS + '-line-soft);background:#fff;',
    '  padding:12px 14px calc(12px + env(safe-area-inset-bottom,0px));',
    '}',
    '.' + NS + '-status{',
    '  font-size:11.5px;color:var(--' + NS + '-ink-soft);margin:0 4px 8px;line-height:1.3;display:none;',
    '}',
    '.' + NS + '-status.is-on{display:block;}',
    '.' + NS + '-form{display:flex;align-items:flex-end;gap:9px;}',
    '.' + NS + '-field{',
    '  flex:1 1 auto;min-width:0;display:flex !important;align-items:center;',
    '  background:#fff;border:1px solid var(--' + NS + '-line);border-radius:21px;',
    '  padding:0 4px 0 15px;',
    '  transition:border-color .15s ease,box-shadow .15s ease;',
    '}',
    '.' + NS + '-field.is-focus{',
    '  background:#fff;border-color:#8f9499;',
    '}',
    '.' + NS + '-input{',
    /* textarea vazio é :empty — o `*:empty{display:none}` do tema sumia com o campo.
       overflow escondido: a altura é calculada no JS, então barra de rolagem só
       aparece depois que o texto passa do limite (ver autoGrow). */
    '  display:block !important;',
    '  flex:1 1 auto;width:100%;min-width:0;',
    '  min-height:40px;max-height:116px;height:40px;',
    '  padding:10px 0;margin:0;border:0;background:transparent;resize:none;',
    '  overflow-y:hidden;overflow-x:hidden;',
    '  color:var(--' + NS + '-ink);font-family:inherit;font-size:16px;line-height:20px;',
    '  -webkit-appearance:none;appearance:none;box-shadow:none;border-radius:0;',
    '  scrollbar-width:thin;',
    '}',
    '.' + NS + '-input:focus{outline:none;box-shadow:none;background:transparent;}',
    '.' + NS + '-input:disabled{opacity:.55;cursor:not-allowed;}',
    /* 14px só no texto de exemplo. O campo em si fica em 16px de propósito:
       abaixo disso o Safari do iPhone dá zoom na página ao focar o campo. */
    '.' + NS + '-input::placeholder{color:#9aa0a6;opacity:1;font-size:14px;}',
    '.' + NS + '-input::-webkit-scrollbar{width:6px;}',
    '.' + NS + '-input::-webkit-scrollbar-thumb{background:#dcdad7;border-radius:6px;}',
    '.' + NS + '-send{',
    '  flex:0 0 auto;width:40px;height:40px;padding:0;border:0;border-radius:50%;cursor:pointer;',
    '  background:var(--' + NS + '-ink);color:#fff;display:flex;align-items:center;justify-content:center;',
    '  font:inherit;transition:background-color .15s ease,transform .12s ease;',
    '}',
    '.' + NS + '-send:hover:not(:disabled){background:var(--' + NS + '-accent);transform:scale(1.04);}',
    '.' + NS + '-send:active:not(:disabled){transform:scale(.96);}',
    '.' + NS + '-send:disabled{background:#eceded;color:#a8acb0;cursor:default;}',
    '.' + NS + '-send:focus-visible{outline:2px solid var(--' + NS + '-accent);outline-offset:2px;}',
    '.' + NS + '-send svg{width:19px;height:19px;display:block;pointer-events:none;}',
    '.' + NS + '-count{font-size:11px;color:#9aa0a6;text-align:right;margin:7px 4px 0;display:none;}',
    '.' + NS + '-count.is-on{display:block;}',
    '.' + NS + '-count.is-over{color:#b3261e;font-weight:600;}',

    /* ícones: o tema esconde elementos vazios (path, rect...) com *:empty */
    '.' + NS + '-panel svg,.' + NS + '-launcher svg{display:block !important;}',
    '.' + NS + '-panel svg *,.' + NS + '-launcher svg *{display:inline !important;}',
    /* O tema declara `svg{fill:…}` no global e CSS vence o atributo fill="none"
       do markup: sem isto, ícone de traço (sacola, seta, cadeado, frasco) vira
       mancha sólida. O avião de papel é o único desenhado em preenchimento. */
    '.' + NS + '-panel svg,.' + NS + '-launcher svg{fill:none;}',
    '.' + NS + '-send svg,.' + NS + '-send svg *{fill:currentColor;}',

    /* leitor de tela apenas */
    '.' + NS + '-sr{position:absolute !important;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;}',

    /* ---------- mobile: tela cheia ---------- */
    '@media (max-width:639px){',
    '  .' + NS + '-launcher{right:' + LAUNCHER_RIGHT_MOBILE + 'px;bottom:' + LAUNCHER_BOTTOM_MOBILE + 'px;width:54px;height:54px;}',
    '  .' + NS + '-panel{',
    '    right:0;left:0;top:0;bottom:0;width:100%;max-width:100%;height:100%;max-height:none;',
    '    border-radius:0;border:0;box-shadow:none;',
    '    padding-top:env(safe-area-inset-top,0px);',
    '    padding-left:env(safe-area-inset-left,0px);padding-right:env(safe-area-inset-right,0px);',
    '  }',
    '  .' + NS + '-header{padding:14px 12px 14px 14px;}',
    '  .' + NS + '-log{padding:16px 14px 8px;}',
    '  .' + NS + '-bubble{max-width:88%;}',
    '  .' + NS + '-bag__body{max-height:32vh;}',
    '}',
    '@supports (height:100dvh){',
    '  @media (max-width:639px){.' + NS + '-panel{height:100dvh;}}',
    '}',
    'html.' + NS + '-lock,body.' + NS + '-lock{overflow:hidden !important;}',

    /* O drawer do carrinho (o tema marca `body.cart-sidebar-show`) roda em
       z-index baixo, bem abaixo do 100050 que o chat precisa ter para vencer o
       popup da roleta. Resultado: o carrinho abria ATRÁS do chat. Enquanto o
       drawer está aberto, o chat desce para trás dele — e volta ao fechar.
       Não fecha o chat de propósito: a conversa continua onde estava. */
    'body.cart-sidebar-show .' + NS + '-panel,',
    'body.cart-sidebar-show .' + NS + '-launcher{z-index:90;}',

    /* Enquanto o chat está aberto, some com quem disputa a tela com ele
       (hoje: o popup da roleta e a aba que o abre). Ver OCULTAR_QUANDO_ABERTO. */
    (OCULTAR_QUANDO_ABERTO.length
      ? 'html.' + NS + '-com-chat ' +
        OCULTAR_QUANDO_ABERTO.join(',html.' + NS + '-com-chat ') +
        '{display:none !important;}'
      : ''),

    /* ---------- movimento reduzido ---------- */
    '@media (prefers-reduced-motion:reduce){',
    '  .' + NS + '-panel.is-open,.' + NS + '-bubble{animation:none !important;}',
    '  .' + NS + '-typing i{animation:none !important;opacity:.6;}',
    '  .' + NS + '-launcher,.' + NS + '-send,.' + NS + '-card__cta,.' + NS + '-checkout,.' + NS + '-field{transition:none !important;}',
    '}'
  ].join('\n');

  /* ==========================================================================
   * 6. ÍCONES (inline, sem fonte externa)
   * ========================================================================== */

  var ICONS = {
    chat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9.5 9.5 0 0 1-3.2-.5L3 21l1.7-4.6A8.2 8.2 0 0 1 3.6 11.5a8.4 8.4 0 0 1 9-8.4 8.4 8.4 0 0 1 8.4 8.4Z"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    // avião de papel sólido, com a dobra interna em recorte (fill-rule evenodd)
    send: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="M21.7 2.3a1 1 0 0 1 .23 1.06l-6.75 17.5a1 1 0 0 1-1.84.06l-3.2-6.85-6.85-3.2a1 1 0 0 1 .06-1.84l17.5-6.75a1 1 0 0 1 1.06.23ZM11.4 13.3l2.53 5.42 4.95-12.83-7.48 7.41Zm5.9-8.86L4.47 9.4l5.42 2.53 7.41-7.49Z"/></svg>',
    lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>',
    seta: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6"/></svg>',
    externo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14 4h6v6"/><path d="M20 4l-8.5 8.5"/><path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M8 12.3l2.7 2.7L16 9.7"/></svg>',
    sacola: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 7.5h12l-1 12.5H7L6 7.5Z"/><path d="M9.2 7.5V5.8a2.8 2.8 0 0 1 5.6 0v1.7"/></svg>',
    bottle: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M10 2h4v4h-4z"/><path d="M8 10a4 4 0 0 1 2-3.4V6h4v.6A4 4 0 0 1 16 10v10a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2Z"/></svg>'
  };

  /* ==========================================================================
   * 7. CONSTRUÇÃO DO DOM
   * ========================================================================== */

  function h(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) {
      for (var k in attrs) {
        if (Object.prototype.hasOwnProperty.call(attrs, k)) node.setAttribute(k, attrs[k]);
      }
    }
    return node;
  }

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = h('style', null, { id: STYLE_ID });
    style.appendChild(document.createTextNode(CSS));
    (document.head || document.documentElement).appendChild(style);
  }

  /**
   * Escreve nome, cargo e foto da agente da vez no cabeçalho.
   *
   * Existe como função separada porque o cabeçalho PRECISA ser repintado quando
   * a sessão troca (newSession → outra agente). Antes era montado só uma vez no
   * build(): a conversa começava do zero com outra personalidade no servidor e a
   * pessoa continuava vendo o nome e a foto da anterior.
   */
  function pintarAgente() {
    if (!el.avatar || !el.title || !el.subtitle) return;
    var agente = getAgente();

    // avatar: foto quando existe, senão a inicial do nome
    el.avatar.className = NS + '-avatar';
    while (el.avatar.firstChild) el.avatar.removeChild(el.avatar.firstChild);

    if (isSafeUrl(agente.avatar)) {
      var img = h('img', null, { src: agente.avatar, alt: '', loading: 'lazy' });
      el.avatar.className += ' ' + NS + '-avatar--foto';
      // Cada foto enquadra diferente: o CSS tem o padrão, a agente ajusta.
      if (agente.zoom) img.style.transform = 'scale(' + agente.zoom + ')';
      if (agente.foco) img.style.transformOrigin = agente.foco;
      on(img, 'error', (function (caixa, node, ag) {
        return function () {
          // Foto fora do ar: volta para o degradê com a inicial, senão sobra um
          // círculo branco vazio no cabeçalho.
          if (node.parentNode === caixa) caixa.removeChild(node);
          caixa.className = NS + '-avatar';
          caixa.textContent = inicialDoAgente(ag);
        };
      })(el.avatar, img, agente));
      el.avatar.appendChild(img);
    } else {
      el.avatar.textContent = inicialDoAgente(agente);
    }

    el.title.textContent = agente.nome;

    // Só o cargo: a bolinha de online saiu daqui e foi para a borda da foto.
    el.subtitle.textContent = agente.cargo;

    if (el.panel) {
      el.panel.setAttribute('aria-label', 'Chat com ' + agente.artigo + ' ' + agente.nome);
    }
    if (el.input) {
      el.input.setAttribute('aria-label', 'Sua mensagem para ' + agente.artigo + ' ' + agente.nome);
    }
  }

  function build() {
    injectStyle();

    var agente = getAgente();

    /* ---- launcher (opcional: por padrão quem abre é o menu do WhatsApp) ---- */
    if (SHOW_LAUNCHER) {
      el.launcher = h('button', NS + '-launcher', {
        type: 'button',
        'aria-label': 'Abrir o chat com ' + agente.artigo + ' ' + agente.nome,
        'aria-expanded': 'false',
        'aria-haspopup': 'dialog'
      });
      el.launcher.innerHTML = ICONS.chat;  // markup fixo do próprio arquivo, nunca da API
      el.launcher.appendChild(h('span', NS + '-launcher__dot', { 'aria-hidden': 'true' }));
    }

    /* ---- painel ---- */
    el.panel = h('div', NS + '-panel', {
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': 'Chat com ' + agente.artigo + ' ' + agente.nome,
      tabindex: '-1'
    });

    var header = h('div', NS + '-header');
    var row = h('div', NS + '-header__row');

    el.avatar = h('div', NS + '-avatar', { 'aria-hidden': 'true' });

    // avatar + bolinha de online moram juntos numa caixa posicionada
    el.avatarbox = h('div', NS + '-avatarbox');
    el.avatarbox.appendChild(el.avatar);
    el.avatarbox.appendChild(h('span', NS + '-dot ' + NS + '-dot--foto', {
      'aria-hidden': 'true'
    }));

    var titles = h('div', NS + '-titles');
    el.title = h('p', NS + '-title');
    el.subtitle = h('p', NS + '-subtitle');
    titles.appendChild(el.title);
    titles.appendChild(el.subtitle);
    pintarAgente();

    el.close = h('button', NS + '-close', { type: 'button', 'aria-label': 'Fechar o chat' });
    el.close.innerHTML = ICONS.close;

    row.appendChild(el.avatarbox);
    row.appendChild(titles);
    row.appendChild(el.close);
    header.appendChild(row);

    if (SHOW_PRIVACY_NOTE) {
      var privacy = h('p', NS + '-privacy');
      var lock = h('span', null, { 'aria-hidden': 'true' });
      lock.innerHTML = ICONS.lock;
      privacy.appendChild(lock);
      privacy.appendChild(document.createTextNode(PRIVACY_NOTE));
      header.appendChild(privacy);
    }

    /* ---- log de mensagens ---- */
    el.log = h('div', NS + '-log', {
      role: 'log',
      'aria-live': 'polite',
      'aria-relevant': 'additions text',
      'aria-label': 'Conversa'
    });

    /* ---- rodapé / composer ---- */
    var foot = h('div', NS + '-foot');

    el.status = h('p', NS + '-status', { role: 'status', 'aria-live': 'polite' });

    el.form = h('form', NS + '-form', { novalidate: 'novalidate' });
    el.input = h('textarea', NS + '-input', {
      rows: '1',
      placeholder: PLACEHOLDER_PADRAO,
      'aria-label': 'Sua mensagem para ' + agente.artigo + ' ' + agente.nome,
      maxlength: String(MAX_CHARS),
      autocomplete: 'off',
      autocapitalize: 'sentences',
      spellcheck: 'true'
    });
    el.send = h('button', NS + '-send', { type: 'submit', 'aria-label': 'Enviar mensagem' });
    el.send.innerHTML = ICONS.send;

    // O campo mora dentro de uma "pílula" própria: é ela que recebe o foco
    // visual, e não o textarea — assim a borda não pula de altura quando o
    // campo cresce com o texto.
    el.field = h('div', NS + '-field');
    el.field.appendChild(el.input);

    el.form.appendChild(el.field);
    el.form.appendChild(el.send);

    el.count = h('p', NS + '-count', { 'aria-hidden': 'true' });

    foot.appendChild(el.status);
    foot.appendChild(el.form);
    foot.appendChild(el.count);

    /* ---- sacola do servidor: só aparece quando a resposta traz item ---- */
    el.bag = h('div', NS + '-bag');

    el.panel.appendChild(header);
    el.panel.appendChild(el.log);
    el.panel.appendChild(el.bag);
    el.panel.appendChild(foot);

    if (el.launcher) document.body.appendChild(el.launcher);
    document.body.appendChild(el.panel);

    bindEvents();

    // Hash da sessão resolvido antes de qualquer coisa: é ele que valida a
    // conversa salva e que amarra o carrinho à conversa.
    getSessionId();

    // Tinha conversa desta aba? Remonta e reabre se estava aberta.
    var salvo = restaurarConversa();
    if (salvo) {
      if (salvo.aberto) {
        // Sem foco automático: em celular, focar no load abre o teclado
        // sozinho e empurra a página inteira.
        track(setTimeout(function () { api.open({ focus: false }); }, 0));
      }
      return;
    }

    // Sem conversa salva: a tela nasce limpa (ou com a saudação local, se
    // MOSTRAR_BOAS_VINDAS estiver ligada). A marca diz a
    // pintarHistoricoServidor() que a tela pode ser trocada pelo histórico que
    // vier do /sessao, sem perder nada.
    if (MOSTRAR_BOAS_VINDAS) addBotMessage(welcomeText(), null, null);
    state.soBoasVindas = true;
  }

  /* ==========================================================================
   * 8. RENDERIZAÇÃO DE MENSAGENS
   * ========================================================================== */

  function scrollToEnd() {
    // rAF para rolar depois do layout das imagens/cards entrarem.
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        if (el.log) el.log.scrollTop = el.log.scrollHeight;
      });
    } else if (el.log) {
      el.log.scrollTop = el.log.scrollHeight;
    }
  }

  function addRow(kind) {
    var row = h('div', NS + '-row ' + NS + '-row--' + kind);
    el.log.appendChild(row);
    return row;
  }

  function addUserMessage(text) {
    var row = addRow('user');
    var bubble = h('div', NS + '-bubble ' + NS + '-bubble--user');
    bubble.textContent = text;             // mensagem do próprio visitante: textContent basta
    row.appendChild(bubble);
    lembrarMensagem({ r: 'u', t: text });
    scrollToEnd();
    return row;
  }

  function addBotMessage(text, produtos, linkCarrinho, acoes) {
    var row = addRow('bot');
    var bubble = h('div', NS + '-bubble ' + NS + '-bubble--bot');
    // Aqui sim vai innerHTML — mas só com a saída de renderTexto(),
    // que escapou o texto ANTES de converter a marcação.
    var render = renderTexto(text);
    // Resposta que era só um link fica sem texto: não cria bolha vazia.
    if (render.html.replace(/<br>|\s|&nbsp;/g, '')) {
      bubble.innerHTML = render.html;
      row.appendChild(bubble);
    }

    // Todo link do texto vira botão, com rótulo tirado da própria URL.
    var botoes = buildBotoesLink(render.botoes, linkCarrinho);
    if (botoes) row.appendChild(botoes);

    var cards = buildCards(produtos);
    if (cards) row.appendChild(cards);

    if (acoes && acoes.length) row.appendChild(buildAcoesBlock(acoes));

    if (isSafeHref(linkCarrinho)) {
      var ctaInterno = false;
      try { ctaInterno = mesmaLoja(new URL(linkCarrinho, location.href)); } catch (e) {}
      var cta = h('a', NS + '-checkout', {
        href: linkCarrinho,
        // Checkout da própria loja abre na MESMA aba: é a continuação da
        // compra, não um desvio. A conversa volta inteira pelo sessionStorage.
        target: ctaInterno ? '_self' : '_blank',
        rel: 'noopener noreferrer'
      });
      cta.textContent = 'Finalizar compra';
      row.appendChild(cta);
    }

    // As ações NÃO são guardadas: ao voltar para a página, um botão
    // "Adicionar ao carrinho" remontado poderia ser clicado de novo e duplicar
    // a linha. Texto, produtos e link sobrevivem; ação é de uso único.
    lembrarMensagem({ r: 'b', t: text, p: produtos || [], l: linkCarrinho || null });

    scrollToEnd();
    return row;
  }

  /**
   * Confirmação de ação ("Adicionado ao carrinho"), do lado da agente.
   *
   * Decisão da loja (17/09/2026): lê melhor como resposta dela ao clique do que
   * como fala da pessoa — a pessoa não digitou "adicionado ao carrinho".
   *
   * ATENÇÃO ao mexer: quem escreve essa linha é o WIDGET, não o backend. A
   * agente só fica sabendo do clique na mensagem seguinte (ver NOTA_CARRINHO),
   * então ela não vai comentar o item por conta própria antes disso.
   */
  function addAcaoMessage(text) {
    var row = addRow('bot');
    var bubble = h('div', NS + '-bubble ' + NS + '-bubble--bot ' + NS + '-bubble--acao');

    var ico = h('span', NS + '-check', { 'aria-hidden': 'true' });
    ico.innerHTML = ICONS.check;          // markup do próprio arquivo, nunca da API
    bubble.appendChild(ico);

    var txt = h('span');
    txt.textContent = text;
    bubble.appendChild(txt);

    row.appendChild(bubble);
    lembrarMensagem({ r: 'a', t: text });
    scrollToEnd();
    return row;
  }

  function addSystemMessage(text, onRetry, rotulo) {
    var row = addRow('sys');
    var bubble = h('div', NS + '-bubble ' + NS + '-bubble--sys');
    bubble.textContent = text;             // mensagem de sistema: texto puro
    row.appendChild(bubble);

    // Só guarda avisos definitivos; o de erro traz botão de repetir, que não
    // faz sentido remontar numa outra página.
    if (typeof onRetry !== 'function') lembrarMensagem({ r: 's', t: text });

    if (typeof onRetry === 'function') {
      var btn = h('button', NS + '-retry', { type: 'button' });
      btn.textContent = rotulo || 'Tentar de novo';
      on(btn, 'click', function () {
        row.parentNode && row.parentNode.removeChild(row);
        onRetry();
      });
      bubble.appendChild(btn);
    }

    scrollToEnd();
    return row;
  }

  function buildCards(produtos) {
    if (!produtos || !produtos.length) return null;      // pode vir [], 1 ou vários

    var list = [];
    for (var i = 0; i < produtos.length; i++) {
      var p = produtos[i];
      if (p && typeof p === 'object') list.push(p);
    }
    if (!list.length) return null;

    // Se vier mais de MAX_CARDS, mostra só os 4 primeiros. A tira rola na
    // horizontal porque 4 cards de 156px não cabem nos 380px do painel.
    var shown = list.slice(0, MAX_CARDS);
    // Vitrine de `formato: 'imagem'` chega com 1 produto: card deitado, largura
    // toda. Um card de 152px sozinho no painel de 380px parece bug.
    var wrap = h('div', NS + '-cards' + (shown.length === 1 ? ' ' + NS + '-cards--solo' : ''),
      { role: 'list', 'aria-label': 'Produtos sugeridos' });

    for (var j = 0; j < shown.length; j++) {
      var prod = shown[j];
      var card = h('div', NS + '-card', { role: 'listitem' });

      var media = h('div', NS + '-card__media');
      if (isSafeUrl(prod.imagem)) {
        var img = h('img', null, {
          src: String(prod.imagem),
          alt: '',
          loading: 'lazy',
          decoding: 'async'
        });
        // imagem quebrada não pode destruir o layout: cai no placeholder
        on(img, 'error', (function (mediaNode, imgNode) {
          return function () {
            if (imgNode.parentNode === mediaNode) mediaNode.removeChild(imgNode);
            mediaNode.appendChild(placeholderIcon());
          };
        })(media, img));
        media.appendChild(img);
      } else {
        media.appendChild(placeholderIcon());
      }

      var body = h('div', NS + '-card__body');

      var name = h('p', NS + '-card__name');
      name.textContent = prod.nome ? String(prod.nome) : 'Produto Prohall';

      var price = h('p', NS + '-card__price');
      price.textContent = prod.preco ? String(prod.preco) : '';
      if (!price.textContent) price.style.display = 'none';

      body.appendChild(name);
      body.appendChild(price);

      if (CARD_ADD_CARRINHO) {
        var cta = h('button', NS + '-card__cta', { type: 'button' });
        rotuloDoBotao(cta, 'Adicionar');
        cta.setAttribute('aria-label', 'Adicionar ' + name.textContent + ' ao carrinho');
        on(cta, 'click', (function (produto, botao) {
          return function (ev) {
            ev.preventDefault();
            ev.stopPropagation();   // não dispara o clique do card
            adicionarProdutoDoCard(produto, botao);
          };
        })(prod, cta));
        body.appendChild(cta);

      } else if (isSafeUrl(prod.url)) {
        var verProduto = h('a', NS + '-card__cta', {
          href: String(prod.url), target: '_blank', rel: 'noopener noreferrer'
        });
        verProduto.textContent = 'Ver produto';
        body.appendChild(verProduto);
      }

      card.appendChild(media);
      card.appendChild(body);

      // Card inteiro clicável: abre a página do produto. O nome é o que temos
      // para procurar, então card sem nome não vira link.
      if (CARD_CLICAVEL && prod.nome) {
        card.className += ' ' + NS + '-card--link';
        card.setAttribute('role', 'link');
        card.setAttribute('tabindex', '0');
        var abrir = (function (produto, node) {
          return function () { abrirProdutoDoCard(produto, node); };
        })(prod, card);
        on(card, 'click', abrir);
        on(card, 'keydown', function (ev) {
          if (ev.key === 'Enter' || ev.keyCode === 13 || ev.key === ' ' || ev.keyCode === 32) {
            ev.preventDefault();
            abrir();
          }
        });
      }

      wrap.appendChild(card);
    }

    ativarArrasto(wrap);
    return wrap;
  }

  /* ---------------------------------------------------------------------------
   * Arrastar o carrossel com o mouse
   *
   * A barra de rolagem saiu do CSS, então no desktop precisa de arrasto — sem
   * isso, quem não tem trackpad fica sem como ver o 3º card. No touch não mexe:
   * o swipe nativo é melhor que qualquer emulação.
   * ------------------------------------------------------------------------- */
  function ativarArrasto(wrap) {
    if (!window.PointerEvent) return;

    var pegando = false;
    var xInicial = 0;
    var scrollInicial = 0;
    var arrastou = false;

    on(wrap, 'pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      pegando = true;
      arrastou = false;
      xInicial = e.clientX;
      scrollInicial = wrap.scrollLeft;
      /* NADA de setPointerCapture aqui.
       *
       * Capturar o ponteiro no pointerdown faz o Chrome REDIRECIONAR os eventos
       * de mouse seguintes — inclusive o `click` — para o elemento que capturou.
       * Resultado: o clique morria no carrossel e nunca chegava ao botão
       * "Adicionar ao carrinho" do card. Clique nenhum dentro da vitrine
       * funcionava, com mouse. (Com .click() por script funcionava, porque esse
       * caminho não passa por hit-testing — foi o que escondeu o bug no teste.)
       *
       * A captura entra só quando o arrasto começa de verdade, lá no pointermove:
       * aí o clique redirecionado é justamente o que queremos descartar. */
    });

    on(wrap, 'pointermove', function (e) {
      if (!pegando) return;
      var d = e.clientX - xInicial;
      if (!arrastou && Math.abs(d) > 5) {
        arrastou = true;
        wrap.classList.add('is-dragging');
        // Agora sim: segurar o ponteiro para o arrasto continuar mesmo se o
        // cursor sair do carrossel.
        try { wrap.setPointerCapture(e.pointerId); } catch (err) {}
      }
      if (arrastou) {
        wrap.scrollLeft = scrollInicial - d;
        e.preventDefault();
      }
    });

    function soltar(e) {
      if (!pegando) return;
      pegando = false;
      wrap.classList.remove('is-dragging');
      try { if (e && e.pointerId != null) wrap.releasePointerCapture(e.pointerId); } catch (err) {}
    }
    on(wrap, 'pointerup', soltar);
    on(wrap, 'pointercancel', soltar);

    // Arrastar termina em "click" no card embaixo do cursor: na fase de captura
    // dá para engolir esse clique antes que ele abra o produto.
    on(wrap, 'click', function (e) {
      if (arrastou) {
        e.preventDefault();
        e.stopPropagation();
        arrastou = false;
      }
    }, true);

    // Imagem dentro de <a>/<div> tem arrasto nativo do navegador: atrapalha.
    on(wrap, 'dragstart', function (e) { e.preventDefault(); });
  }

  /**
   * Bloco de ações dentro da bolha. A sacola NÃO muda sozinha: por padrão a
   * pessoa confirma. Se a IA entender errado, o estrago é uma sugestão ignorada
   * em vez de um produto estranho aparecendo no checkout. Para aplicar direto,
   * ligue ACOES_AUTO lá em cima.
   */
  function buildAcoesBlock(acoes) {
    var box = h('div', NS + '-acts');

    function clear() { while (box.firstChild) box.removeChild(box.firstChild); }

    function buildLista() {
      var ul = h('ul', NS + '-acts__list');
      for (var i = 0; i < acoes.length; i++) {
        var li = h('li', NS + '-acts__item');
        li.textContent = acaoLabel(acoes[i]);   // textContent: o título vem do modelo
        ul.appendChild(li);
      }
      return ul;
    }

    function estadoPergunta() {
      clear();
      var title = h('p', NS + '-acts__title');
      title.textContent = 'Quer que eu atualize sua sacola?';
      box.appendChild(title);
      box.appendChild(buildLista());

      var row = h('div', NS + '-acts__row');
      var ok = h('button', NS + '-acts__ok', { type: 'button' });
      ok.textContent = 'Atualizar sacola';
      var no = h('button', NS + '-acts__no', { type: 'button' });
      no.textContent = 'Agora não';
      on(ok, 'click', aplicar);
      on(no, 'click', estadoRecusado);
      row.appendChild(ok);
      row.appendChild(no);
      box.appendChild(row);
      scrollToEnd();
    }

    function estadoRecusado() {
      clear();
      var p = h('p', NS + '-acts__note');
      p.textContent = 'Tudo bem, deixei a sacola como estava.';
      box.appendChild(p);
    }

    function estadoCarregando() {
      clear();
      var p = h('p', NS + '-acts__note');
      p.textContent = 'Atualizando sua sacola...';
      box.appendChild(p);
    }

    function estadoPronto(cart) {
      clear();
      var n = (cart && typeof cart.item_count === 'number') ? cart.item_count : null;
      var p = h('p', NS + '-acts__done');
      p.textContent = (n === null)
        ? 'Sacola atualizada.'
        : 'Sacola atualizada - ' + n + (n === 1 ? ' item.' : ' itens.');
      box.appendChild(p);

      // Só oferece checkout se sobrou alguma coisa lá dentro.
      if (n) {
        var cta = h('a', NS + '-checkout', { href: shopRoot() + 'checkout' });
        cta.textContent = 'Finalizar compra';
        box.appendChild(cta);
      }
      scrollToEnd();
    }

    function estadoErro(err) {
      clear();
      var p = h('p', NS + '-acts__err');
      p.textContent = (err && err.message)
        ? String(err.message).slice(0, 160)
        : 'Não consegui atualizar a sacola.';
      box.appendChild(p);

      var btn = h('button', NS + '-retry', { type: 'button' });
      btn.textContent = 'Tentar de novo';
      on(btn, 'click', aplicar);
      box.appendChild(btn);
      scrollToEnd();
    }

    function aplicar() {
      estadoCarregando();
      runAcoes(acoes).then(estadoPronto, estadoErro);
    }

    if (ACOES_AUTO) aplicar(); else estadoPergunta();
    return box;
  }

  function placeholderIcon() {
    var span = h('span', NS + '-card__ph', { 'aria-hidden': 'true' });
    span.innerHTML = ICONS.bottle;
    return span;
  }

  function showTyping() {
    hideTyping();
    var row = addRow('bot');
    row.setAttribute('data-' + NS + '-typing', 'true');
    var box = h('div', NS + '-bubble ' + NS + '-bubble--bot ' + NS + '-typing', {
      'aria-hidden': 'true'
    });
    box.appendChild(h('i'));
    box.appendChild(h('i'));
    box.appendChild(h('i'));
    row.appendChild(box);
    var sr = h('span', NS + '-sr');
    sr.textContent = getAgente().nome + ' está digitando';
    row.appendChild(sr);
    el.typing = row;
    scrollToEnd();
  }

  function hideTyping() {
    if (el.typing && el.typing.parentNode) el.typing.parentNode.removeChild(el.typing);
    el.typing = null;
  }

  /* ==========================================================================
   * 8b. SESSÃO (hash)
   *
   * O hash é anônimo: bytes aleatórios do navegador, sem nada do visitante
   * dentro. Não é derivado de e-mail, IP, cart token nem user-agent — hash de
   * dado pessoal continua sendo dado pessoal para a LGPD.
   * ========================================================================== */

  function randomId() {
    try {
      if (window.crypto && window.crypto.randomUUID) {
        return 's_' + window.crypto.randomUUID().replace(/-/g, '');
      }
    } catch (e) {}
    try {
      if (window.crypto && window.crypto.getRandomValues) {
        var bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        var hex = '';
        for (var i = 0; i < bytes.length; i++) hex += ('0' + bytes[i].toString(16)).slice(-2);
        return 's_' + hex;
      }
    } catch (e) {}
    // Último recurso (navegador muito antigo): não é criptográfico, mas serve
    // para correlacionar mensagens da mesma aba.
    return 's_' + Date.now().toString(16) + Math.random().toString(16).slice(2, 12);
  }

  function readCookie(name) {
    try {
      var parts = String(document.cookie || '').split(';');
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i].replace(/^\s+/, '');
        if (p.indexOf(name + '=') === 0) return decodeURIComponent(p.slice(name.length + 1));
      }
    } catch (e) {}
    return '';
  }

  function writeCookie(name, value, maxAgeSeconds) {
    try {
      var c = name + '=' + encodeURIComponent(value) +
        ';path=/;max-age=' + maxAgeSeconds + ';SameSite=Lax';
      if (SESSION_COOKIE_DOMAIN) c += ';domain=' + SESSION_COOKIE_DOMAIN;
      // Secure só faz sentido em https (a loja é https; o preview local, não).
      if (location.protocol === 'https:') c += ';Secure';
      document.cookie = c;
    } catch (e) {}
  }

  /**
   * Formato esperado do hash — descarta cookie adulterado por outro script E
   * hash curto de versão antiga do widget. O backend exige [A-Za-z0-9_-] com 16
   * a 64 caracteres; um cookie de 10 chars passaria daqui e tomaria erro lá.
   */
  function isValidSessionId(id) {
    return typeof id === 'string' && /^[A-Za-z0-9_-]{16,64}$/.test(id);
  }

  /**
   * Devolve o hash da sessão, criando na primeira chamada.
   * Toda leitura/escrita de storage vai em try/catch: Safari em janela privada
   * e navegador com cookie bloqueado lançam exceção, e aí caímos na memória —
   * o chat continua funcionando, só perde a continuidade entre pageviews.
   */
  function getSessionId() {
    if (state.sessionId) return state.sessionId;

    var found = '';
    if (SESSION_SCOPE === 'cookie') {
      found = readCookie(SESSION_COOKIE_NAME);
    } else if (SESSION_SCOPE === 'tab') {
      try { found = window.sessionStorage.getItem(SESSION_COOKIE_NAME) || ''; } catch (e) {}
    }

    state.sessionId = isValidSessionId(found) ? found : randomId();
    touchSession();
    return state.sessionId;
  }

  /** Renova a janela de inatividade. Chamado a cada mensagem enviada. */
  function touchSession() {
    if (!state.sessionId) return;
    var ttl = Math.max(1, SESSION_TTL_MIN) * 60;
    if (SESSION_SCOPE === 'cookie') {
      writeCookie(SESSION_COOKIE_NAME, state.sessionId, ttl);
    } else if (SESSION_SCOPE === 'tab') {
      try { window.sessionStorage.setItem(SESSION_COOKIE_NAME, state.sessionId); } catch (e) {}
    }
  }

  /**
   * Zera a sessão: novo hash, conversa local limpa, sessão nova no servidor na
   * próxima mensagem.
   *
   * ATENÇÃO: isso corta o VÍNCULO com a conversa, não apaga a conversa. Quem
   * guarda o histórico agora é o backend, indexado pelo hash antigo — apagar de
   * verdade (LGPD) é pedido para o lado deles.
   */
  function resetSession() {
    state.sessionId = null;
    state.history.length = 0;
    state.cartToken = null;
    state.cartTokenAnterior = null;

    // Sessão do servidor e sacola pertencem ao hash velho: nada disso sobrevive.
    state.sessaoAberta = false;
    state.abrindoSessao = null;
    state.encerrada = false;
    state.sacola = null;
    state.sacolaSincronizada = '';
    state.itensDaSofia = {};
    state.adicionados = [];
    state.rajada = 0;
    state.rajadaPintada = -1;
    state.assinatura = '';

    if (SESSION_SCOPE === 'cookie') writeCookie(SESSION_COOKIE_NAME, '', 0);
    if (SESSION_SCOPE === 'tab') {
      try { window.sessionStorage.removeItem(SESSION_COOKIE_NAME); } catch (e) {}
    }
    limparConversaSalva();   // a conversa velha não pode voltar na próxima página

    // Sessão nova = hash novo = provavelmente outra agente. O cabeçalho tem que
    // acompanhar, senão a conversa recomeça com a cara da anterior. A persona
    // confirmada era da sessão velha: some junto, e o /sessao confirma a nova.
    state.persona = null;
    var novoHash = getSessionId();
    pintarAgente();

    if (el.log) {
      while (el.log.firstChild) el.log.removeChild(el.log.firstChild);
      el.typing = null;
      if (MOSTRAR_BOAS_VINDAS) addBotMessage(welcomeText(), null, null);
      state.soBoasVindas = true;
    }
    renderSacola();
    updateComposerState();
    salvarConversa();
    return novoHash;
  }

  /* ==========================================================================
   * 8c. CONVERSA QUE ATRAVESSA A NAVEGAÇÃO
   *
   * O chat precisa continuar aberto quando o visitante muda de página, e só
   * fechar no X. Isso obriga a guardar duas coisas fora da memória do JS:
   * se o painel estava aberto e o que já foi dito.
   *
   * Fica em sessionStorage, não em localStorage nem cookie:
   *   - sobrevive a recarregar e a trocar de página (é o que foi pedido);
   *   - morre quando a aba fecha, então não vira histórico permanente no
   *     dispositivo — que pode ser compartilhado;
   *   - não vai junto em requisição nenhuma (cookie iria, e sem necessidade).
   *
   * CHAT_PERSIST = 'off' desliga tudo e volta ao comportamento antigo
   * (recarregou, some).
   * ========================================================================== */

  var CHAT_PERSIST = 'tab';
  var FECHAR_COM_ESC = true;        // false = só o X fecha, nem o teclado
  var PERSIST_KEY = 'ph_sofia_chat';
  var PERSIST_MAX_MSGS = 40;
  var PERSIST_MAX_BYTES = 120000;   // teto de segurança da cota do storage

  function persistOn() {
    return CHAT_PERSIST === 'tab' && !state.destroyed;
  }

  function lembrarMensagem(msg) {
    if (!persistOn() || state.restoring) return;
    state.msgs.push(msg);
    if (state.msgs.length > PERSIST_MAX_MSGS) {
      state.msgs = state.msgs.slice(-PERSIST_MAX_MSGS);
    }
    salvarConversa();
  }

  function salvarConversa() {
    if (!persistOn() || state.restoring) return;
    try {
      var dados = {
        v: 1,
        sid: state.sessionId,
        aberto: !!state.open,
        msgs: state.msgs,
        hist: state.history.slice(-MAX_HISTORY * 2)
      };
      var txt = JSON.stringify(dados);
      // Estourou o teto? Corta pela metade mais antiga e tenta de novo.
      if (txt.length > PERSIST_MAX_BYTES) {
        dados.msgs = dados.msgs.slice(-Math.ceil(PERSIST_MAX_MSGS / 2));
        dados.hist = dados.hist.slice(-MAX_HISTORY);
        txt = JSON.stringify(dados);
      }
      window.sessionStorage.setItem(PERSIST_KEY, txt);
    } catch (e) {
      // cota cheia / storage bloqueado: segue sem persistir
    }
  }

  function lerConversa() {
    if (!persistOn()) return null;
    try {
      var txt = window.sessionStorage.getItem(PERSIST_KEY);
      if (!txt) return null;
      var d = JSON.parse(txt);
      if (!d || d.v !== 1 || !d.msgs || !d.msgs.length) return null;
      // Sessão diferente da atual (cookie expirou, newSession): descarta.
      if (d.sid && state.sessionId && d.sid !== state.sessionId) return null;
      return d;
    } catch (e) {
      return null;
    }
  }

  function limparConversaSalva() {
    state.msgs = [];
    try { window.sessionStorage.removeItem(PERSIST_KEY); } catch (e) {}
  }

  /**
   * Remonta a conversa salva. Devolve true se restaurou algo — nesse caso o
   * build() não escreve a mensagem de boas-vindas (ela já está na lista).
   */
  function restaurarConversa() {
    var d = lerConversa();
    if (!d) return null;

    state.restoring = true;
    try {
      for (var i = 0; i < d.msgs.length; i++) {
        var m = d.msgs[i];
        if (!m || typeof m.t !== 'string') continue;
        if (m.r === 'u') addUserMessage(m.t);
        else if (m.r === 'a') addAcaoMessage(m.t);
        else if (m.r === 's') addSystemMessage(m.t);
        else addBotMessage(m.t, m.p || [], m.l || null, null);
      }
      state.msgs = d.msgs.slice(-PERSIST_MAX_MSGS);
      if (Array.isArray(d.hist)) state.history = d.hist.slice(-MAX_HISTORY * 2);
    } finally {
      state.restoring = false;
    }
    return d;
  }

  /* ==========================================================================
   * 9. CONTEXTO DA LOJA (cart.js + PH_CHAT + URL)
   * ========================================================================== */

  function fetchWithTimeout(url, options, ms) {
    options = options || {};
    if (!window.fetch) return Promise.reject(new Error('sem fetch'));

    var ctrl = null;
    var timeoutId = null;

    if (window.AbortController) {
      ctrl = new window.AbortController();
      options.signal = ctrl.signal;
    }

    var request = window.fetch(url, options);

    var guard = new Promise(function (_, reject) {
      timeoutId = track(setTimeout(function () {
        if (ctrl) { try { ctrl.abort(); } catch (e) {} }
        reject(new Error('timeout'));
      }, ms));
    });

    return Promise.race([request, guard]).then(function (res) {
      if (timeoutId) clearTimeout(timeoutId);
      return res;
    }, function (err) {
      if (timeoutId) clearTimeout(timeoutId);
      throw err;
    });
  }

  /** /cart.js cru, do jeito que a Shopify devolve. Usado pelo sync do tema. */
  function getRawCart() {
    return fetchWithTimeout(shopRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    }, CART_TIMEOUT_MS)
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .catch(function () { return null; });
  }

  /** Recorte do carrinho que vai no contrato. Falha em silêncio: é opcional. */
  function getCart() {
    return getRawCart()
      .then(function (cart) {
        if (!cart || !cart.items) return null;
        var items = [];
        for (var i = 0; i < cart.items.length && i < 30; i++) {
          var it = cart.items[i] || {};
          items.push({
            product_id: it.product_id,
            variant_id: it.variant_id,
            // `key` identifica a LINHA (variante + properties). É o que o
            // backend devolve em `line_key` quando a mesma variante aparece
            // duas vezes na sacola (brinde, kit, personalização).
            key: it.key,
            title: it.title,
            quantity: it.quantity,
            price: it.price
          });
        }
        // ATENÇÃO — medido na loja, não é teoria:
        // com a sacola VAZIA o /cart.js devolve um token novo a cada pageview
        // (7d05c254… numa página, fb73ae0e… na seguinte). Ele só vira um
        // identificador estável depois que entra o primeiro item — aí o cookie
        // `cart` gruda e o token se mantém entre páginas.
        // Por isso só tratamos como "token de verdade" carrinho com item.
        var estavel = (cart.item_count || 0) > 0 && !!cart.token;

        if (estavel && cart.token !== state.cartToken) {
          // Rotação real (checkout concluído, sacola esvaziada): guarda o
          // anterior para o backend migrar a sessão em vez de achar que é
          // outro visitante.
          if (state.cartToken) state.cartTokenAnterior = state.cartToken;
          state.cartToken = cart.token;
        }
        if (!estavel) state.cartToken = null;

        // Só os campos do contrato. O resto do /cart.js (atributos, nota,
        // endereço estimado) fica de fora de propósito.
        return {
          token: cart.token,
          token_estavel: estavel,
          item_count: cart.item_count || 0,
          total_price: cart.total_price,
          items: items
        };
      })
      .catch(function () { return null; });
  }

  /**
   * window.PH_CHAT é opcional. Usamos só duas coisas dela:
   *   - product_id: vai no `contexto.page`;
   *   - nome: fica no NAVEGADOR, só para a saudação local.
   * O e-mail é deliberadamente ignorado: o contrato da API não tem campo para
   * dado pessoal e o widget não manda dado pessoal para lugar nenhum.
   */
  function readPhChat() {
    var ph = window.PH_CHAT;
    if (!ph || typeof ph !== 'object') return {};
    return ph;
  }

  /** Só as personalidades liberadas para a escolha. */
  function elencoAtivo() {
    var out = [];
    for (var i = 0; i < AGENTES.length; i++) {
      if (AGENTES[i] && AGENTES[i].ativo !== false) out.push(AGENTES[i]);
    }
    return out.length ? out : AGENTES.slice(0, 1);
  }

  /**
   * Qual personalidade atende ESTA sessão.
   *
   * Derivada do hash, não sorteada com Math.random(): o hash já é estável entre
   * páginas e recarregamentos (cookie), então a agente também é — sem guardar
   * mais nada e sem risco de trocar de nome no meio da conversa.
   */
  function agenteDaSessao() {
    var lista = elencoAtivo();
    if (!ELENCO_ROTATIVO || lista.length < 2) return lista[0];

    var h = String(getSessionId() || '');
    var soma = 0;
    for (var i = 0; i < h.length; i++) {
      soma = (soma * 31 + h.charCodeAt(i)) >>> 0;
    }
    return lista[soma % lista.length];
  }

  /** Entrada do elenco com este número de persona (1 a 4), ou null. */
  function agentePorPersona(numero) {
    for (var i = 0; i < AGENTES.length; i++) {
      if (AGENTES[i] && AGENTES[i].persona === numero) return AGENTES[i];
    }
    return null;
  }

  /** Entrada do elenco com este id, ativa ou não, ou null. */
  function agentePorId(id) {
    for (var i = 0; i < AGENTES.length; i++) {
      if (AGENTES[i] && AGENTES[i].id === id) return AGENTES[i];
    }
    return null;
  }

  /**
   * Guarda quem o BACKEND disse que atende, e repinta o cabeçalho se mudou.
   *
   * As duas rotas devolvem `persona: { numero, nome }`. Numa sessão que já
   * existia, isso pode ser DIFERENTE do que mandamos: lá a escolha gruda na
   * criação e o número enviado depois é ignorado. Desenhar o sorteio local
   * nesse caso deixaria "Samira" no cabeçalho respondendo "aqui é a Ana Beatriz".
   */
  function receberPersona(p) {
    if (!p || typeof p !== 'object') return;

    var numero = parseInt(p.numero, 10);
    if (!isFinite(numero)) numero = null;
    var nome = typeof p.nome === 'string' ? p.nome.trim().slice(0, 40) : '';
    if (numero === null && !nome) return;

    var atual = state.persona;
    if (atual && atual.numero === numero && atual.nome === nome) return;

    state.persona = { numero: numero, nome: nome };
    pintarAgente();
  }

  /** Identidade do agente: a da sessão + o que a loja passar em PH_CHAT. */
  function getAgente() {
    var custom = readPhChat().agente;

    // 1) o sorteio local, ou a vendedora que a campanha forçou por id — nesse
    //    caso a entrada INTEIRA (foto, cargo, número), não só o id.
    var base = agenteDaSessao();
    if (custom && typeof custom === 'object' && typeof custom.id === 'string') {
      var forcada = agentePorId(custom.id.trim());
      if (forcada) base = forcada;
    }

    // 2) o que o backend confirmou vence a campanha e o sorteio. A foto, o cargo
    //    e o artigo saem da entrada de MESMO NÚMERO; se o número não estiver no
    //    elenco daqui (vendedora nova lá antes de chegar no tema), fica a
    //    inicial do nome no círculo — melhor que a foto de outra pessoa.
    var confirmada = state.persona;
    if (confirmada) {
      var doNumero = confirmada.numero ? agentePorPersona(confirmada.numero) : null;
      if (doNumero) base = doNumero;
      else if (confirmada.nome) {
        base = {
          id: '', persona: confirmada.numero, nome: confirmada.nome,
          artigo: 'a', cargo: base.cargo, apresentacao: base.apresentacao,
          avatar: '', zoom: base.zoom, foco: base.foco
        };
      }
    }

    var ag = {
      id: base.id,
      persona: base.persona,
      nome: base.nome,
      artigo: base.artigo,
      cargo: base.cargo,
      apresentacao: base.apresentacao,
      avatar: base.avatar,
      zoom: base.zoom,
      foco: base.foco
    };
    // 3) ajustes soltos de PH_CHAT (nome avulso, outra foto, cargo de campanha).
    if (custom && typeof custom === 'object') {
      // `zoom` é número e a URL da foto passa de 80 caracteres: por isso a lista
      // explícita, em vez de varrer as chaves.
      var campos = ['id', 'nome', 'artigo', 'cargo', 'apresentacao', 'avatar', 'foco'];
      for (var i = 0; i < campos.length; i++) {
        var k = campos[i];
        if (typeof custom[k] === 'string' && custom[k].trim()) {
          ag[k] = custom[k].trim().slice(0, k === 'avatar' ? 400 : 80);
        }
      }
      if (typeof custom.zoom === 'number' && isFinite(custom.zoom)) ag.zoom = custom.zoom;
      // Escapatória para mandar um número sem ter a entrada aqui no elenco.
      if (typeof custom.persona === 'number' && custom.persona >= 1) ag.persona = custom.persona;
    }

    // 4) por último, e por cima de tudo: quem o backend confirmou. A campanha
    //    escolhe enquanto a conversa não existe; existindo, quem responde é ele.
    if (confirmada && confirmada.nome) ag.nome = confirmada.nome;
    if (confirmada && confirmada.numero) ag.persona = confirmada.numero;

    return ag;
  }

  /** Inicial para o avatar quando não há imagem. */
  function inicialDoAgente(ag) {
    var n = (ag.nome || '').replace(/\s+/g, '');
    return n ? n.charAt(0).toUpperCase() : '•';
  }

  /** Texto da saudação local. Só é usado com MOSTRAR_BOAS_VINDAS ligada. */
  function welcomeText() {
    var ag = getAgente();
    var nome = readPhChat().nome;
    var saudacao = 'Oi!';
    if (typeof nome === 'string' && nome.trim()) {
      // só o primeiro nome, curto; o texto ainda passa pelo escape de renderRichText
      saudacao = 'Oi, ' + nome.trim().split(/\s+/)[0].slice(0, 24) + '!';
    }
    var apres = ag.apresentacao ? ', ' + ag.apresentacao : '';
    return saudacao + ' Eu sou ' + ag.artigo + ' *' + ag.nome + '*' + apres + '.\n' + PERGUNTA_INICIAL;
  }

  function currentProductId() {
    var ph = readPhChat();
    if (ph.product_id) return ph.product_id;
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.product && meta.product.id) return meta.product.id;
    } catch (e) {}
    return null;
  }

  function buildContext() {
    var page = { url: String(location.href).slice(0, 500) };
    var pid = currentProductId();
    if (pid) page.product_id = pid;

    // `contexto` NÃO está no contrato do embed — o backend ignora campos extras
    // hoje. Continua sendo enviado de propósito: é como eles passam a ver o
    // carrinho nativo e a preencher a coluna shopify_cart_token quando quiserem
    // ligar conversa e sacola, sem precisar de outra versão do widget.
    //
    // `capacidades` existe para o backend não ter que adivinhar a versão do
    // widget: só emita `acoes` se `cart.write` estiver na lista.
    var capacidades = ACOES_HABILITADAS ? ['cart.read', 'cart.write'] : ['cart.read'];

    return getCart().then(function (cart) {
      var ctx = { page: page, capacidades: capacidades };
      if (cart) ctx.cart = cart;
      return ctx;
    }).catch(function () {
      return { page: page, capacidades: capacidades };  // nada impede o envio
    });
  }

  /* ==========================================================================
   * 9B. AÇÕES DE CARRINHO (executor)
   *
   * Tudo que chega em `data.acoes` passa por aqui. Duas regras que não podem
   * ser afrouxadas:
   *
   *   1) NADA vindo do modelo entra cru. normalizeAcoes() valida op, id e
   *      quantidade e DESCARTA em silêncio o que não bate. É preferível a Sofia
   *      "esquecer" de mexer na sacola do que mexer errado.
   *   2) As escritas são em SÉRIE. A Ajax API trabalha sobre a mesma sacola;
   *      dois POST simultâneos se atropelam e o segundo pode gravar por cima
   *      de um estado velho.
   * ========================================================================== */

  function shopRoot() {
    // Loja multi-idioma tem prefixo (/pt-br/). window.Shopify.routes.root já
    // vem com a barra no fim — ver a doc da Ajax API sobre URLs locale-aware.
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return (typeof r === 'string' && r) ? r : '/';
  }

  /**
   * POST na Ajax API. `credentials: 'same-origin'` é obrigatório: o cookie
   * `cart` é o que diz à Shopify de qual sacola estamos falando.
   */
  function postCart(path, body) {
    return fetchWithTimeout(shopRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      cache: 'no-store'
    }, CART_OP_TIMEOUT_MS).then(function (res) {
      return res.text().then(function (txt) {
        var data = null;
        try { data = txt ? JSON.parse(txt) : null; } catch (e) {}
        if (!res.ok) {
          // 422 é o caso comum e tem texto bom: esgotado, limite por pedido,
          // variante inexistente. A Shopify manda em `description`.
          var err = new Error((data && (data.description || data.message)) || 'Não deu para atualizar a sacola.');
          err.status = res.status;
          throw err;
        }
        return data;
      });
    });
  }

  function toPositiveInt(v) {
    var n = (typeof v === 'number') ? v : parseInt(String(v == null ? '' : v).trim(), 10);
    return (isFinite(n) && n > 0) ? Math.floor(n) : 0;
  }

  /** Chave de linha do /cart.js: "<variant_id>:<hash>". */
  function isLineKey(v) {
    return typeof v === 'string' && /^[0-9]+:[a-z0-9]+$/i.test(v);
  }

  function normalizeAcoes(raw) {
    if (!ACOES_HABILITADAS || !Array.isArray(raw)) return [];

    var out = [];
    for (var i = 0; i < raw.length && out.length < MAX_ACOES; i++) {
      var a = raw[i];
      if (!a || typeof a !== 'object') continue;

      var op = String(a.op || '').toLowerCase();
      if (op !== 'add' && op !== 'set' && op !== 'remove') continue;

      var variantId = toPositiveInt(a.variant_id);
      var lineKey = isLineKey(a.line_key) ? a.line_key : '';
      if (!variantId && !lineKey) continue;

      // 'add' só sabe somar por variante; sem variant_id não há o que fazer.
      if (op === 'add' && !variantId) continue;

      var qty = (op === 'remove') ? 0 : (toPositiveInt(a.quantity) || 1);
      if (qty > MAX_QTY) qty = MAX_QTY;

      out.push({
        op: op,
        variant_id: variantId,
        line_key: lineKey,
        quantity: qty,
        titulo: (typeof a.titulo === 'string') ? a.titulo.slice(0, 80) : ''
      });
    }
    return out;
  }

  function acaoLabel(a) {
    var nome = a.titulo || 'produto';
    if (a.op === 'add') return 'Adicionar ' + a.quantity + '× ' + nome;
    if (a.op === 'remove') return 'Remover ' + nome;
    return 'Deixar ' + a.quantity + '× ' + nome;
  }

  function runAcao(a) {
    if (a.op === 'add') {
      return postCart('cart/add.js', { items: [{ id: a.variant_id, quantity: a.quantity }] });
    }
    // 'set' e 'remove' são a mesma chamada: remover é definir quantidade 0.
    // `id` aceita a chave de linha ou o variant_id em texto.
    return postCart('cart/change.js', {
      id: a.line_key || String(a.variant_id),
      quantity: a.quantity
    });
  }

  /**
   * Depois de escrever, a tela do TEMA precisa saber. O drawer do Halo não
   * escuta nada sozinho (o AJAX dele só troca .previewCart-wrapper), então
   * chamamos as funções dele na mão e ainda disparamos um evento para quem
   * vier depois poder ouvir sem mexer neste arquivo.
   */
  function syncTheme() {
    return getRawCart().then(function (cart) {
      if (!cart) return null;

      var halo = window.halo;
      try { if (halo && typeof halo.updateCart === 'function') halo.updateCart(cart); } catch (e) {}
      try { if (halo && typeof halo.updateSidebarCart === 'function') halo.updateSidebarCart(cart); } catch (e) {}

      // Rede de segurança: se um dia essas funções sumirem do tema, pelo menos
      // o contador do header não fica mentindo.
      try {
        var nodes = document.querySelectorAll('[data-cart-count]');
        for (var i = 0; i < nodes.length; i++) nodes[i].textContent = cart.item_count;
      } catch (e) {}

      try {
        document.dispatchEvent(new CustomEvent('sofia:cart:updated', { detail: { cart: cart } }));
      } catch (e) {}

      return cart;
    });
  }

  /** Executa a lista em série e devolve o carrinho final. */
  function runAcoes(acoes) {
    var chain = Promise.resolve(null);

    for (var i = 0; i < acoes.length; i++) {
      chain = chain.then((function (acao) {
        return function () { return runAcao(acao); };
      })(acoes[i]));
    }

    // O sync roda mesmo se uma ação falhar no meio: parte já foi aplicada e a
    // tela não pode ficar mostrando a sacola antiga.
    return chain.then(
      function () { return syncTheme(); },
      function (err) { return syncTheme().then(function () { throw err; }); }
    );
  }

  /* ==========================================================================
   * 9B-bis. PRODUTO DO CARD -> PRODUTO DA LOJA
   *
   * A vitrine chega sem `url` e sem `variant_id`: dá para desenhar o card, mas
   * não para clicar nele. Quem resolve é a própria loja, por dois endpoints
   * públicos que já existem em qualquer tema:
   *
   *   /search/suggest.json  -> handle + url do produto
   *   /products/<handle>.js -> variantes, com id e disponibilidade
   *
   * Regras que valem a pena manter:
   *   - só no clique. Resolver os 4 cards de cada vitrine no render gastaria
   *     ~18 KB por resposta para links que quase ninguém usa;
   *   - com mais de uma variante disponível, NÃO escolhemos por ninguém: manda
   *     para a página do produto escolher tamanho/versão;
   *   - se o preço do card e o da loja não baterem, é sinal de que a busca
   *     achou outro produto. Aí também manda para a página, em vez de jogar o
   *     item errado no carrinho.
   * ========================================================================== */

  var cacheProduto = {};   // nome (minúsculo) -> promise da resolução

  /** "R$ 1.234,56" | 1234.56 | "58.90" -> 1234.56 (ou null). */
  function valorNumerico(v) {
    if (typeof v === 'number') return isFinite(v) ? v : null;
    if (typeof v !== 'string' || !v.trim()) return null;
    var limpo = v.replace(/[^\d.,]/g, '');
    if (limpo.indexOf(',') >= 0) limpo = limpo.replace(/\./g, '').replace(',', '.');
    var n = parseFloat(limpo);
    return isFinite(n) ? n : null;
  }

  function getJson(url, ms) {
    return fetchWithTimeout(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    }, ms || BUSCA_TIMEOUT_MS).then(function (res) {
      return res && res.ok ? res.json() : null;
    }).catch(function () { return null; });
  }

  /** minúsculas, sem acento e sem pontuação: "4ABC 450g" -> "4abc 450g". */
  function normalizarTexto(t) {
    var s = String(t || '').toLowerCase();
    if (typeof s.normalize === 'function') {
      s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }
    return s.replace(/[^a-z0-9]+/g, ' ').trim();
  }

  /**
   * O que a busca achou é MESMO o produto do card?
   *
   * POR QUE ISSO EXISTE (17/09/2026): a vitrine não traz `variant_id` nem `url`,
   * só o nome — então o tema procura o produto pelo nome na própria loja. Medido
   * em produção: pedir "Creme de Pentear … Curvaturas 4ABC 450g" devolvia ora o
   * "… Curvaturas 2ABC 450g" (produto IRMÃO, mesmo preço), ora um kit. O 2ABC
   * passava por todas as guardas — mesmo preço, uma variante — e ia PARA O
   * CARRINHO no lugar do que a cliente pediu.
   *
   * A regra: todo pedaço com NÚMERO do nome pedido tem que aparecer no título
   * achado. É o que separa irmão de irmão (4abc/3abc/2abc, 450g/200ml/1l) e é
   * exatamente onde a busca da Shopify erra, porque ela ignora esses tokens.
   * Depois disso, a maior parte das palavras ainda tem que bater, senão "Creme
   * de Pentear" casaria com qualquer creme.
   *
   * Errar para menos (dizer "não achei" quando o produto existe) é aceitável:
   * a pessoa ainda tem a conversa e a busca da loja. Errar para mais é vender
   * o produto errado.
   */
  function combinaComPedido(nomePedido, tituloAchado) {
    var pedido = normalizarTexto(nomePedido).split(' ');
    var achado = normalizarTexto(tituloAchado);
    if (!pedido.length || !achado) return 0;

    var semEspaco = achado.replace(/ /g, '');   // "450 g" também casa com "450g"
    var palavras = {};
    var lista = achado.split(' ');
    for (var i = 0; i < lista.length; i++) palavras[lista[i]] = true;

    var batem = 0;
    var total = 0;
    for (var j = 0; j < pedido.length; j++) {
      var p = pedido[j];
      if (!p) continue;
      total++;
      if (/[0-9]/.test(p)) {
        if (semEspaco.indexOf(p) < 0) return 0;   // token decisivo faltando: descarta
        batem++;
      } else if (palavras[p]) {
        batem++;
      }
    }
    if (!total) return 0;
    var razao = batem / total;
    return razao >= 0.7 ? razao : 0;
  }

  /** Nome do card -> { handle, url, titulo, preco, disponivel } da loja, ou null. */
  function resolverProduto(prod) {
    var nome = String((prod && prod.nome) || '').trim();
    if (!nome) return Promise.resolve(null);

    var chave = nome.toLowerCase();
    if (cacheProduto[chave]) return cacheProduto[chave];

    // 10 e não 1: o primeiro resultado da Shopify para um nome comprido costuma
    // ser um irmão ou um kit. Quem escolhe é combinaComPedido(), não o ranking.
    var url = shopRoot() + 'search/suggest.json?q=' + encodeURIComponent(nome) +
      '&resources[type]=product&resources[limit]=10';

    var promessa = getJson(url).then(function (j) {
      var lista = j && j.resources && j.resources.results && j.resources.results.products;
      if (!lista || !lista.length) return null;

      var melhor = null;
      var melhorNota = 0;
      for (var i = 0; i < lista.length; i++) {
        var c = lista[i];
        if (!c || !c.handle) continue;
        var nota = combinaComPedido(nome, c.title);
        if (nota > melhorNota) { melhorNota = nota; melhor = c; }
      }
      if (!melhor) return null;   // nenhum bateu: é "não achei", não "quase"

      return {
        handle: String(melhor.handle),
        // a url da busca vem com rastreio (?_pos=1&_psq=…): fica só o caminho
        url: String(melhor.url || ('/products/' + melhor.handle)).split('?')[0],
        titulo: String(melhor.title || ''),
        preco: valorNumerico(melhor.price),
        disponivel: melhor.available !== false
      };
    });

    cacheProduto[chave] = promessa;
    return promessa;
  }

  /** Abre a página do produto. Sem url pronta, procura antes. */
  function abrirProdutoDoCard(prod, card) {
    if (isSafeUrl(prod.url)) { window.location.href = String(prod.url); return; }

    if (card) card.style.opacity = '.6';
    resolverProduto(prod).then(function (info) {
      if (card) card.style.opacity = '';
      if (info && info.url) window.location.href = info.url;
      else erroNoCard(card, 'Não achei esse produto na loja.');
    });
  }

  function erroNoCard(card, msg) {
    if (!card) return;
    var corpo = card.querySelector('.' + NS + '-card__body') || card;
    var antigo = corpo.querySelector('.' + NS + '-card__err');
    if (antigo) corpo.removeChild(antigo);
    var p = h('p', NS + '-card__err');
    p.textContent = msg;
    corpo.appendChild(p);
    scrollToEnd();
  }

  /**
   * PLANO B do card: em vez de "não achei", pedir o produto PARA a agente.
   *
   * POR QUE (17/09/2026): o card vem sem `variant_id`, então o tema traduz nome
   * -> produto pela busca da loja. Quando a busca não tem o produto (medido: os
   * cremes 3ABC e 4ABC dão 0 resultados, embora ativos e com página no ar), a
   * trava de combinaComPedido() recusa — e a venda morria ali.
   *
   * Só que a SACOLA dela vem com `variant_id` certo (medido: pedir o 4ABC
   * devolve 51228667871538, a variante certa). Então o caminho existe: pedir na
   * conversa, ela monta a sacola, e a sacola entra no carrinho nativo pelo
   * add.js — sem passar pela busca da loja em momento nenhum.
   *
   * O preço é a espera da IA (20 a 50s) em vez do clique instantâneo. Só roda
   * nos produtos que a busca não acha; nos outros o atalho continua direto.
   */
  function pedirNaConversa(prod, botao, rotulo, avisou) {
    // O botão volta ao normal: quem mostra o andamento daqui pra frente é a
    // conversa (bolinhas de digitando + campo travado).
    if (botao) { botao.disabled = false; rotuloDoBotao(botao, rotulo); }

    var ag = getAgente();
    var texto = 'Quero o ' + prod.nome + '. Coloca 1 na minha sacola, por favor.';

    // Nada de encanamento na tela: "não está na busca da loja" é problema
    // nosso, não da cliente. Para ela, a agente está separando o produto.
    addSystemMessage('Só um instante — ' + ag.nome + ' está separando esse pra você.');

    // submitText recusa EM SILÊNCIO quando já tem mensagem em voo, conversa
    // encerrada ou cooldown de 429. `emVoo` sobe de forma síncrona lá dentro,
    // então dá para saber se foi ou não — e, não indo, é melhor dizer.
    var antes = state.emVoo;
    submitText(texto, { semBolha: !!avisou });
    if (state.emVoo === antes) {
      addSystemMessage('Não consegui pedir agora — manda o nome dele aqui na conversa que ela resolve.');
    }
  }

  /**
   * Botão "Adicionar ao carrinho" do card.
   * Caminho feliz: resolve o produto, pega a variante única e manda no add.js —
   * sem passar pela Sofia e sem esperar os 20s do debounce.
   */
  function adicionarProdutoDoCard(prod, botao) {
    var card = botao ? botao.parentNode && botao.parentNode.parentNode : null;
    var rotulo = botao ? botao.textContent : '';   // sem o ícone: é só o texto
    var antigo = card && card.querySelector('.' + NS + '-card__err');
    if (antigo && antigo.parentNode) antigo.parentNode.removeChild(antigo);

    // O nome escolhido entra na conversa como mensagem da pessoa. Como isso
    // rola a tela, o resultado (sucesso ou erro) também precisa vir por bolha:
    // o botão do card fica lá em cima.
    var avisou = AVISAR_SELECAO_NO_CHAT && !!prod.nome;
    if (avisou) addUserMessage(String(prod.nome));

    function falhar(msg) {
      if (botao) { botao.disabled = false; rotuloDoBotao(botao, rotulo); }
      if (avisou) addSystemMessage(msg);
      else erroNoCard(card, msg);
    }

    if (botao) { botao.disabled = true; rotuloDoBotao(botao, 'Adicionando…'); }

    // Backend já mandou a variante? Pula a loja inteira.
    var direto = toPositiveInt(prod.variant_id);
    var alvo = direto
      ? Promise.resolve({ variant_id: direto })
      : resolverProduto(prod).then(function (info) {
          if (!info) return null;
          if (!info.disponivel) return { esgotado: true };

          // Preços diferentes = achou outro produto, mesmo com o nome parecido.
          var precoCard = valorNumerico(prod.preco);
          if (precoCard != null && info.preco != null && Math.abs(precoCard - info.preco) > 0.01) {
            return { escolher: true, url: info.url, motivo: 'preco' };
          }

          return getJson(shopRoot() + 'products/' + info.handle + '.js').then(function (p) {
            var variantes = (p && p.variants) ? p.variants : [];
            if (!variantes.length) return { escolher: true, url: info.url, motivo: 'variante' };

            var disponiveis = [];
            for (var i = 0; i < variantes.length; i++) {
              if (variantes[i] && variantes[i].available) disponiveis.push(variantes[i]);
            }
            if (!disponiveis.length) return { esgotado: true };
            // Mais de uma opção: escolher tamanho/versão pela pessoa seria chute.
            if (disponiveis.length > 1) return { escolher: true, url: info.url, motivo: 'variante' };
            return { variant_id: toPositiveInt(disponiveis[0].id) };
          });
        });

    alvo.then(function (r) {
      // Nada na busca da loja: em vez de morrer aqui, pede para ela (ver
      // pedirNaConversa). O `falhar` fica para quando nem isso é possível.
      if (!r) { pedirNaConversa(prod, botao, rotulo, avisou); return null; }
      if (r.esgotado) { falhar('Esse está esgotado.'); return null; }
      if (r.escolher) {
        // A pessoa decide na PDP. O texto muda com o motivo: dizer "mais de uma
        // versão" quando o preço é que não bateu mandava a pessoa procurar uma
        // opção que não existe na página.
        var aviso = r.motivo === 'preco'
          ? 'O preço desse mudou na loja — abrindo a página para você conferir.'
          : 'Esse tem mais de uma versão — abrindo a página para você escolher.';
        if (avisou) addSystemMessage(aviso);
        window.location.href = r.url;
        return null;
      }
      if (!r.variant_id) { falhar('Não consegui identificar a versão do produto.'); return null; }

      return postCart('cart/add.js', { items: [{ id: r.variant_id, quantity: 1 }] })
        .then(function () { return syncTheme(); })
        .then(function () {
          if (botao) {
            rotuloDoBotao(botao, 'Adicionado', true);
            botao.classList.add('is-ok');
            botao.disabled = true;
          }
          // A Sofia não viu este clique: fica anotado para a próxima mensagem.
          registrarAdicionado(prod.nome);
          if (avisou) addAcaoMessage('Adicionado ao carrinho');
          // Com item no carrinho, o painel aparece com "Finalizar compra".
          painelDoCarrinhoNativo();
        });
    }).catch(function (err) {
      // 422 da Shopify vem com texto bom ("esgotado", "limite por pedido").
      falhar((err && err.message) ? String(err.message).slice(0, 90) : 'Não deu para adicionar.');
    });
  }

  /* ==========================================================================
   * 9C. SACOLA DO SERVIDOR
   *
   * Quem monta a sacola é o backend: toda resposta traz `sacola` inteira e o
   * widget REDESENHA em cima, sem acumular estado próprio. A sacola vive no
   * servidor da Sofia — o carrinho da Shopify só sabe dela quando a pessoa
   * confirma, e é aí que entra sincronizarSacola().
   *
   * A sincronia é IDEMPOTENTE de propósito: soma só a diferença entre o que a
   * sacola pede e o que o carrinho já tem. Clicar duas vezes, ou a Sofia repetir
   * o mesmo item numa resposta seguinte, não duplica linha.
   *
   * O `link` de checkout do backend NÃO é usado quando existe sacola: permalink
   * de /cart/... SUBSTITUI o carrinho do visitante, apagando o que ele tinha
   * posto por conta própria. Preferimos add.js + /checkout.
   * ========================================================================== */

  /**
   * Texto de preço para a tela. O contrato manda os dois formatos conforme o
   * campo: `preco: 109.9` (número, em reais — não em centavos como o /cart.js) e
   * `preco_texto: "R$ 109,90"`. Quando vem pronto, é ele que vale.
   */
  function precoTexto(valor, texto) {
    if (typeof texto === 'string' && texto.trim()) return texto.trim().slice(0, 24);
    if (typeof valor === 'string' && valor.trim()) return valor.trim().slice(0, 24);
    var n = Number(valor);
    if (!isFinite(n) || n < 0) return '';
    try {
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    } catch (e) {
      return 'R$ ' + n.toFixed(2).replace('.', ',');
    }
  }

  /** `sacola` da resposta -> o que o painel desenha. Item sem variante é descartado. */
  function normalizarSacola(raw) {
    if (!SACOLA_VISIVEL || !raw || typeof raw !== 'object') return null;

    var itens = Array.isArray(raw.itens) ? raw.itens : [];
    var out = [];
    var count = 0;

    for (var i = 0; i < itens.length && out.length < MAX_ITENS_SACOLA; i++) {
      var it = itens[i];
      if (!it || typeof it !== 'object') continue;

      // variant_id chega como STRING de dígitos ("49684105232690"): é o mesmo
      // número que o /cart/add.js espera, mas precisa virar inteiro aqui.
      var vid = toPositiveInt(it.variant_id);
      if (!vid) continue;   // sem variante não há o que mandar para a Shopify

      var qty = toPositiveInt(it.qty) || 1;
      if (qty > MAX_QTY) qty = MAX_QTY;
      count += qty;

      var semPreco = (Number(it.preco) === 0);

      out.push({
        variant_id: vid,
        titulo: (typeof it.titulo === 'string' && it.titulo.trim())
          ? it.titulo.trim().slice(0, 90) : 'Produto Prohall',
        variante: (typeof it.variante === 'string' ? it.variante.trim().slice(0, 60) : ''),
        qty: qty,
        imagem: isSafeUrl(it.imagem) ? String(it.imagem) : '',
        brinde: semPreco,
        preco_texto: semPreco ? 'Grátis' : precoTexto(it.preco, it.preco_texto)
      });
    }

    return {
      itens: out,
      count: count,
      total_texto: precoTexto(raw.total, raw.total_texto)
    };
  }

  /** Identidade do conteúdo da sacola: muda quando item ou quantidade mudam. */
  function assinaturaSacola(sac) {
    if (!sac || !sac.itens.length) return '';
    var partes = [];
    for (var i = 0; i < sac.itens.length; i++) {
      partes.push(sac.itens[i].variant_id + ':' + sac.itens[i].qty);
    }
    return partes.sort().join(',');
  }

  /**
   * Passa a sacola para o carrinho NATIVO da Shopify.
   *
   * Em série, não em lote: um 422 ("esgotado", "limite por pedido") num item
   * derrubaria a chamada inteira e o visitante não saberia qual produto falhou.
   * Escritas simultâneas na mesma sacola também se atropelam.
   */
  function sincronizarSacola(sacola) {
    var sac = sacola || state.sacola;
    if (!sac || !sac.itens.length) return Promise.resolve(null);

    return getRawCart().then(function (cart) {
      var tem = {};
      if (cart && cart.items) {
        for (var i = 0; i < cart.items.length; i++) {
          var it = cart.items[i] || {};
          var k = String(it.variant_id);
          tem[k] = (tem[k] || 0) + (it.quantity || 0);
        }
      }

      // Só a DIFERENÇA: o que o visitante já tem no carrinho conta, e nada é
      // removido — mexer no que ele pôs por conta própria não é nosso papel.
      var faltando = [];
      for (var j = 0; j < sac.itens.length; j++) {
        var item = sac.itens[j];
        var delta = item.qty - (tem[String(item.variant_id)] || 0);
        if (delta > 0) faltando.push({ id: item.variant_id, quantity: delta });
      }

      if (!faltando.length) return syncTheme();   // já está tudo lá

      var chain = Promise.resolve(null);
      for (var n = 0; n < faltando.length; n++) {
        chain = chain.then((function (linha) {
          return function () {
            return postCart('cart/add.js', { items: [linha] }).then(function (r) {
              // Anota o que entrou por conta dela, para saber o que desfazer.
              var vid = String(linha.id);
              state.itensDaSofia[vid] = (state.itensDaSofia[vid] || 0) + linha.quantity;
              return r;
            });
          };
        })(faltando[n]));
      }

      // O sync do tema roda mesmo com falha no meio: parte já entrou e o header
      // não pode ficar mostrando o contador antigo.
      return chain.then(
        function () { return syncTheme(); },
        function (err) { return syncTheme().then(function () { throw err; }); }
      );
    });
  }

  /**
   * Tira do carrinho o que saiu da sacola dela.
   *
   * Compara o que ESTE widget colocou (state.itensDaSofia) com a sacola que
   * acabou de chegar. O que ela tirou, sai — na quantidade exata e por LINHA do
   * carrinho, nunca zerando o que a pessoa colocou sozinha.
   */
  function espelharRemocoes(itensDaSacola) {
    if (!ESPELHAR_REMOCAO) return Promise.resolve(null);

    var nossos = state.itensDaSofia;
    var temNosso = false;
    for (var chave in nossos) {
      if (Object.prototype.hasOwnProperty.call(nossos, chave) && nossos[chave] > 0) temNosso = true;
    }
    if (!temNosso) return Promise.resolve(null);

    var querem = {};
    for (var i = 0; i < itensDaSacola.length; i++) {
      querem[String(itensDaSacola[i].variant_id)] = itensDaSacola[i].qty;
    }

    var tirar = [];
    for (var vid in nossos) {
      if (!Object.prototype.hasOwnProperty.call(nossos, vid)) continue;
      var nossa = nossos[vid] || 0;
      var agora = querem[vid] || 0;
      if (nossa > agora) tirar.push({ vid: vid, qtd: nossa - agora });
    }
    if (!tirar.length) return Promise.resolve(null);

    return getRawCart().then(function (cart) {
      if (!cart || !cart.items) return null;

      var chain = Promise.resolve(null);

      for (var i = 0; i < tirar.length; i++) {
        var alvo = tirar[i];
        var linha = null;
        for (var j = 0; j < cart.items.length; j++) {
          if (String(cart.items[j].variant_id) === alvo.vid) { linha = cart.items[j]; break; }
        }
        if (!linha) {                       // já não está no carrinho
          state.itensDaSofia[alvo.vid] = 0;
          continue;
        }

        // Nunca abaixo de zero, e nunca tirando mais do que ela pôs.
        var restante = Math.max(0, (linha.quantity || 0) - alvo.qtd);
        chain = chain.then((function (l, a, q) {
          return function () {
            return postCart('cart/change.js', {
              id: l.key || String(a.vid),   // a chave de linha é mais precisa
              quantity: q
            }).then(function (r) {
              state.itensDaSofia[a.vid] = Math.max(0, (state.itensDaSofia[a.vid] || 0) - a.qtd);
              return r;
            });
          };
        })(linha, alvo, restante));
      }

      // O sync roda mesmo se uma linha falhar: parte já saiu.
      return chain.then(function () { return syncTheme(); },
        function () { return syncTheme(); });
    }).catch(function () { return null; });
  }

  /**
   * Redesenha o painel inteiro a partir de state.sacola.
   * `estado` é o do botão: 'pronto' | 'enviando' | 'ok' | 'erro'.
   */
  function renderSacola(estado, erro) {
    if (!el.bag) return;

    while (el.bag.firstChild) el.bag.removeChild(el.bag.firstChild);

    var sac = state.sacola;
    if (!sac || !sac.itens.length) {
      el.bag.classList.remove('is-on');
      return;
    }
    el.bag.classList.add('is-on');

    var assinatura = assinaturaSacola(sac);
    var jaNoCarrinho = state.sacolaSincronizada === assinatura;
    if (!estado) estado = jaNoCarrinho ? 'ok' : 'pronto';

    /* ---- cabeçalho clicável: contagem + total ---- */
    var head = h('button', NS + '-bag__head', {
      type: 'button',
      'aria-expanded': el.bag.classList.contains('is-open') ? 'true' : 'false'
    });
    var ico = h('span', NS + '-bag__ico', { 'aria-hidden': 'true' });
    ico.innerHTML = ICONS.sacola;              // markup do próprio arquivo
    head.appendChild(ico);

    var nativa = !!sac.nativa;

    // Conta só o que a pessoa comprou: brinde não é item de compra, e somar os
    // dois faz o número do topo não bater com o que ela está pagando.
    var nBrindes = 0, nComprados = 0;
    for (var c = 0; c < sac.itens.length; c++) {
      if (sac.itens[c].brinde) nBrindes += sac.itens[c].qty;
      else nComprados += sac.itens[c].qty;
    }
    if (!nComprados && !nBrindes) nComprados = sac.count;

    var texto = (nativa ? 'Seu carrinho' : 'Sacola da ' + getAgente().nome) +
      ' · ' + nComprados + (nComprados === 1 ? ' item' : ' itens');
    if (nBrindes) texto += ' + ' + nBrindes + (nBrindes === 1 ? ' brinde' : ' brindes');

    var label = h('span', NS + '-bag__label');
    label.appendChild(document.createTextNode(texto));
    if (sac.total_texto) {
      var sub = h('span', NS + '-bag__sub');
      sub.textContent = sac.total_texto +
        (nativa ? ' · pronto para finalizar' : (estado === 'ok' ? ' · já no seu carrinho' : ''));
      label.appendChild(sub);
    }
    head.appendChild(label);

    var chev = h('span', NS + '-bag__chev', { 'aria-hidden': 'true' });
    chev.innerHTML = ICONS.seta;
    head.appendChild(chev);

    on(head, 'click', function () {
      var aberto = el.bag.classList.toggle('is-open');
      head.setAttribute('aria-expanded', aberto ? 'true' : 'false');
      // Abrir a lista encurta o log: sem isto, a última bolha some atrás dela.
      scrollToEnd();
    });
    el.bag.appendChild(head);

    /* ---- lista: comprados primeiro, brindes depois ----
       Misturados, "R$ 0,00" no meio da lista parece erro de preço. Separando,
       a pessoa entende que ganhou — e o total continua fechando com o que ela
       paga. */
    var body = h('div', NS + '-bag__body');

    var comprados = [], brindes = [];
    for (var f = 0; f < sac.itens.length; f++) {
      (sac.itens[f].brinde ? brindes : comprados).push(sac.itens[f]);
    }
    var ordenados = comprados.concat(brindes);

    for (var i = 0; i < ordenados.length; i++) {
      var item = ordenados[i];

      // cabeçalho do grupo de brindes, uma vez só
      if (item.brinde && (i === 0 || !ordenados[i - 1].brinde)) {
        var tituloGrupo = h('p', NS + '-bag__grupo');
        tituloGrupo.textContent = brindes.length === 1
          ? 'Brinde' : 'Brindes (' + brindes.length + ')';
        body.appendChild(tituloGrupo);
      }

      var linha = h('div', NS + '-bag__item' + (item.brinde ? ' ' + NS + '-bag__item--brinde' : ''));

      var thumb = h('div', NS + '-bag__thumb', { 'aria-hidden': 'true' });
      if (item.imagem) {
        var img = h('img', null, { src: item.imagem, alt: '', loading: 'lazy', decoding: 'async' });
        on(img, 'error', (function (caixa, node) {
          return function () {
            if (node.parentNode === caixa) caixa.removeChild(node);
            caixa.innerHTML = ICONS.bottle;
          };
        })(thumb, img));
        thumb.appendChild(img);
      } else {
        thumb.innerHTML = ICONS.bottle;
      }
      linha.appendChild(thumb);

      var txt = h('div', NS + '-bag__txt');
      var nome = h('p', NS + '-bag__name');
      nome.textContent = item.titulo;          // textContent: o título vem do modelo
      txt.appendChild(nome);

      var meta = h('p', NS + '-bag__meta');
      var partes = [];
      if (item.variante) partes.push(item.variante);
      partes.push(item.qty + '×');
      if (item.preco_texto) partes.push(item.preco_texto);
      meta.textContent = partes.join(' · ');
      txt.appendChild(meta);

      linha.appendChild(txt);
      body.appendChild(linha);
    }
    el.bag.appendChild(body);

    /* ---- rodapé: um CTA por estado ---- */
    var foot = h('div', NS + '-bag__foot');

    function nota(texto, classe) {
      var p = h('p', NS + '-bag__note' + (classe ? ' ' + NS + '-bag__note--' + classe : ''));
      p.textContent = texto;
      return p;
    }

    if (estado === 'enviando') {
      var esperando = h('button', NS + '-bag__cta', { type: 'button', disabled: 'disabled' });
      esperando.textContent = 'Adicionando ao carrinho…';
      foot.appendChild(esperando);

    } else if (estado === 'ok') {
      var linha = h('div', NS + '-bag__acoes');

      var checkout = h('a', NS + '-bag__cta', { href: shopRoot() + 'checkout' });
      checkout.textContent = 'Finalizar compra';
      linha.appendChild(checkout);

      var verCarrinho = h('a', NS + '-bag__2nd', { href: shopRoot() + 'cart' });
      verCarrinho.textContent = 'Ver meu carrinho';
      on(verCarrinho, 'click', abrirDrawerDoTema);
      linha.appendChild(verCarrinho);

      foot.appendChild(linha);
      // No painel nativo a frase seria redundante: ele É o carrinho da loja.
      if (!nativa) foot.appendChild(nota('Estes itens já estão no carrinho da loja.', 'ok'));

    } else {
      var cta = h('button', NS + '-bag__cta', { type: 'button' });
      // "Atualizar" quando parte já foi sincronizada: some/troca item na conversa.
      cta.textContent = state.sacolaSincronizada
        ? 'Atualizar meu carrinho'
        : 'Adicionar ao carrinho da loja';
      on(cta, 'click', aplicarSacola);
      foot.appendChild(cta);

      if (estado === 'erro') {
        foot.appendChild(nota(
          (erro && erro.message) ? String(erro.message).slice(0, 160)
            : 'Não consegui adicionar ao carrinho.',
          'err'
        ));
      }
    }

    el.bag.appendChild(foot);
  }

  /**
   * Painel montado a partir do carrinho NATIVO.
   *
   * Existe para o caso em que a pessoa monta o carrinho sozinha, clicando nos
   * cards: a Sofia não tem sacola nenhuma e, sem isto, não haveria botão de
   * finalizar dentro do chat — ela teria que pedir o link para a IA e esperar o
   * turno inteiro.
   *
   * A sacola DELA tem precedência: enquanto houver proposta não confirmada, é a
   * proposta que aparece.
   */
  function painelDoCarrinhoNativo() {
    if (!SACOLA_VISIVEL || state.destroyed) return Promise.resolve(null);

    // A proposta dela só tem precedência enquanto AINDA NÃO foi para o carrinho.
    // Depois de aplicada (ou com SACOLA_AUTO), o carrinho nativo é mais completo.
    if (state.sacola && !state.sacola.nativa && state.sacola.itens.length &&
        assinaturaSacola(state.sacola) !== state.sacolaSincronizada) {
      return Promise.resolve(null);
    }

    return getRawCart().then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) {
        // Carrinho esvaziou (checkout, remoção no drawer): some com o painel.
        if (state.sacola && state.sacola.nativa) {
          state.sacola = null;
          state.sacolaSincronizada = '';
          renderSacola();
        }
        return null;
      }

      var itens = [];
      for (var i = 0; i < cart.items.length && itens.length < MAX_ITENS_SACOLA; i++) {
        var it = cart.items[i] || {};
        // o /cart.js devolve imagem sem protocolo (//cdn.shopify.com/...)
        var img = typeof it.image === 'string' ? it.image : '';
        if (img.indexOf('//') === 0) img = location.protocol + img;

        // O tema não marca brinde em lugar nenhum: o que identifica é o preço
        // zerado da linha (é assim que os apps de brinde entram no carrinho).
        var centavos = (it.final_line_price != null ? it.final_line_price : it.line_price);
        var ehBrinde = Number(centavos) === 0;

        itens.push({
          variant_id: toPositiveInt(it.variant_id),
          titulo: String(it.product_title || it.title || 'Produto').slice(0, 90),
          variante: (it.variant_title && it.variant_title !== 'Default Title')
            ? String(it.variant_title).slice(0, 60) : '',
          qty: it.quantity || 1,
          imagem: isSafeUrl(img) ? img : '',
          brinde: ehBrinde,
          // preços do /cart.js vêm em CENTAVOS, ao contrário dos da sacola
          preco_texto: ehBrinde ? 'Grátis' : precoTexto(centavos / 100, null)
        });
      }

      state.sacola = {
        itens: itens,
        count: cart.item_count || 0,
        total_texto: precoTexto((cart.total_price || 0) / 100, null),
        nativa: true
      };
      // Já está no carrinho: o painel nasce no estado de finalizar.
      state.sacolaSincronizada = assinaturaSacola(state.sacola);
      renderSacola('ok');
      scrollToEnd();
      return cart;
    }).catch(function () { return null; });
  }

  /**
   * Guarda o que a pessoa adicionou por conta própria, para contar à Sofia na
   * próxima mensagem. A sacola montada por ELA não entra aqui: ela já sabe.
   */
  function registrarAdicionado(nome) {
    if (!AVISAR_IA_DO_CARRINHO) return;
    var titulo = String(nome || '').trim().slice(0, 90);
    if (!titulo) return;

    for (var i = 0; i < state.adicionados.length; i++) {
      if (state.adicionados[i] === titulo) return;   // não repete o mesmo item
    }
    state.adicionados.push(titulo);
    if (state.adicionados.length > MAX_ITENS_NOTA) {
      state.adicionados = state.adicionados.slice(-MAX_ITENS_NOTA);
    }
  }

  /** Linha de contexto que vai grudada na próxima mensagem. '' se não há nada. */
  function notaDoCarrinho() {
    if (!AVISAR_IA_DO_CARRINHO || !state.adicionados.length) return '';
    var lista = [];
    for (var i = 0; i < state.adicionados.length; i++) {
      lista.push('"' + state.adicionados[i] + '"');
    }
    return NOTA_CARRINHO.replace('{itens}', lista.join(', '));
  }

  /**
   * Escreve o rótulo do botão do card, com ícone opcional.
   * Existe porque o estado "adicionado" precisa de um check DESENHADO — o "✓"
   * de texto sai com peso e alinhamento diferentes em cada fonte do tema.
   */
  function rotuloDoBotao(botao, texto, comCheck) {
    while (botao.firstChild) botao.removeChild(botao.firstChild);
    if (comCheck) {
      var ico = h('span', NS + '-check', { 'aria-hidden': 'true' });
      ico.innerHTML = ICONS.check;        // markup do próprio arquivo
      botao.appendChild(ico);
    }
    var txt = h('span');
    txt.textContent = texto;
    botao.appendChild(txt);
  }

  /**
   * Abre o drawer de carrinho do tema em vez de navegar para /cart.
   *
   * A página /cart deste tema não mostra carrinho nenhum: o
   * sections/main-cart.liquid faz `window.location = '/?cart_show'` na primeira
   * linha. Ou seja, o link levava a pessoa a um recarregamento completo, para a
   * home, só para o drawer abrir lá — e a conversa ia junto para o sessionStorage
   * no meio do caminho.
   *
   * QUEM ABRE É O BOTÃO DO TEMA, não a classe no body.
   *
   * O conteúdo do drawer é montado PREGUIÇOSAMENTE: o `initSidebarCart()` do
   * theme.js só chama `Shopify.getCart` + `halo.updateSidebarCart` no primeiro
   * clique do ícone do header ([data-cart-sidebar]), e só uma vez. Ligar a
   * classe `cart-sidebar-show` na mão abria a gaveta antes de alguém ter
   * preenchido — daí o "Meu carrinho (0 itens)" vazio, que só ficava certo
   * depois que a pessoa clicava no ícone do header.
   *
   * Clicando no elemento do tema, os dois handlers dele rodam na ordem certa.
   * Sem esse elemento (layouts de page builder), cai no comportamento antigo e,
   * por último, deixa o link /cart seguir.
   */
  function abrirDrawerDoTema(ev) {
    var gatilho = document.querySelector('[data-cart-sidebar]');

    if (gatilho) {
      if (ev && ev.preventDefault) ev.preventDefault();
      try { gatilho.click(); } catch (e) {}
      return;
    }

    if (!document.getElementById('halo-cart-sidebar')) return;   // segue o href
    if (ev && ev.preventDefault) ev.preventDefault();

    // Sem o gatilho do tema: preenche na mão antes de abrir, senão abre vazio.
    try {
      if (window.Shopify && typeof window.Shopify.getCart === 'function' &&
          window.halo && typeof window.halo.updateSidebarCart === 'function') {
        window.Shopify.getCart(function (cart) {
          try { window.halo.updateSidebarCart(cart); } catch (e) {}
        });
      }
    } catch (e) {}

    try { document.body.classList.add('cart-sidebar-show'); } catch (e) {}
  }

  /** Confirmação da pessoa (ou SACOLA_AUTO): joga a sacola no carrinho nativo. */
  function aplicarSacola() {
    var sac = state.sacola;
    if (!sac || !sac.itens.length) return Promise.resolve(null);
    var assinatura = assinaturaSacola(sac);

    renderSacola('enviando');

    return sincronizarSacola(sac).then(function (cart) {
      state.sacolaSincronizada = assinatura;
      renderSacola('ok');
      // Com adição automática, a sacola dela já É parte do carrinho. Mostrar só
      // a proposta esconderia o que a pessoa colocou pelos cards — então o
      // painel passa a ser o carrinho inteiro.
      if (SACOLA_AUTO) painelDoCarrinhoNativo();
      return cart;
    }, function (err) {
      renderSacola('erro', err);
      return null;
    });
  }

  /** Aplica a sacola que veio na resposta e desenha o painel. */
  function receberSacola(raw) {
    if (!SACOLA_VISIVEL) return;

    var nova = normalizarSacola(raw);
    // Resposta sem o campo (ou com sacola vazia) não apaga o que está na tela:
    // sacola vazia no contrato é { itens: [], total: 0 }, e aí sim limpamos.
    if (!nova && raw === undefined) return;

    // O que ela tirou da sacola sai do carrinho ANTES de o painel ser redesenhado.
    espelharRemocoes((nova && nova.itens) ? nova.itens : []).then(function (cart) {
      if (cart) painelDoCarrinhoNativo();
    });

    // Sacola dela vazia não deixa a tela sem painel: cai para o carrinho nativo
    // (a pessoa pode ter montado tudo pelos cards).
    if (!nova || !nova.itens.length) {
      state.sacola = null;
      state.sacolaSincronizada = '';
      renderSacola();
      painelDoCarrinhoNativo();
      return;
    }

    state.sacola = nova;

    if (SACOLA_ABERTA && state.sacola && el.bag) el.bag.classList.add('is-open');
    renderSacola();
    scrollToEnd();   // o painel mudou de altura; a conversa desceu junto

    if (SACOLA_AUTO && state.sacola && assinaturaSacola(state.sacola) !== state.sacolaSincronizada) {
      aplicarSacola();
    }
  }

  /* ==========================================================================
   * 10. CAMADA DE API (mock e real com a MESMA assinatura)
   *
   * Retorno normalizado: { status:Number, retryAfter:Number|null, body:Object|null }
   * A UI só conhece esse formato — por isso trocar USE_MOCK não mexe em mais nada.
   * ========================================================================== */

  /* ---------------------------------------------------------------------------
   * Vitrines -> cards
   *
   * O contrato manda `vitrines`: uma lista de vitrines, cada uma com `produtos`.
   * Dois detalhes que quebram silenciosamente se ninguém traduzir:
   *   - o campo da foto é `imagem_url`, e o card lê `imagem`;
   *   - não vem `url` de produto, então o "Ver produto" só existe se o backend
   *     mandar `url` ou `handle`.
   * `formato` ('imagem' | 'carrossel') é dica de quantidade, não de layout: quem
   * decide o desenho é o número de produtos (ver buildCards).
   * ------------------------------------------------------------------------- */
  function produtosDeVitrines(vitrines) {
    if (!Array.isArray(vitrines)) return [];

    var out = [];
    for (var i = 0; i < vitrines.length; i++) {
      var v = vitrines[i];
      var lista = (v && Array.isArray(v.produtos)) ? v.produtos : [];

      for (var j = 0; j < lista.length && out.length < 20; j++) {
        var p = lista[j];
        if (!p || typeof p !== 'object') continue;
        out.push({
          id: p.id,
          nome: (typeof p.nome === 'string' ? p.nome : '').slice(0, 120),
          preco: precoTexto(p.preco, p.preco_texto),
          imagem: isSafeUrl(p.imagem_url) ? String(p.imagem_url)
            : (isSafeUrl(p.imagem) ? String(p.imagem) : ''),
          url: isSafeUrl(p.url) ? String(p.url) : urlDoProduto(p)
        });
      }
    }
    return out;
  }

  /** Sem `url` no payload, monta a da loja pelo handle — quando ele vem. */
  function urlDoProduto(p) {
    var handle = (typeof p.handle === 'string' && /^[a-z0-9][a-z0-9-]{1,119}$/i.test(p.handle))
      ? p.handle : '';
    if (!handle) return '';
    return location.protocol + '//' + location.host + shopRoot() + 'products/' + handle.toLowerCase();
  }

  /**
   * O `link` do backend chega no host DELE (sofia.prohall.app.br). Um
   * /cart/<variante>:<qtd> nesse domínio não é o carrinho deste visitante e o
   * checkout seria de outro storefront — então o host vira o da loja atual,
   * preservando caminho e query. Domínio fora de HOSTS_DO_AGENTE passa intacto.
   */
  function linkDaLoja(link) {
    if (!isSafeUrl(link)) return null;
    if (!REESCREVER_LINK_DO_AGENTE) return String(link);
    try {
      var u = new URL(String(link), location.href);
      var host = String(u.host || '').toLowerCase().replace(/^www\./, '');
      for (var i = 0; i < HOSTS_DO_AGENTE.length; i++) {
        if (host === String(HOSTS_DO_AGENTE[i]).toLowerCase().replace(/^www\./, '')) {
          return location.protocol + '//' + location.host + u.pathname + u.search;
        }
      }
      return String(link);
    } catch (e) {
      return String(link);
    }
  }

  /* ---------------------------------------------------------------------------
   * Requisição
   * ------------------------------------------------------------------------- */

  /**
   * POST JSON nos endpoints do embed, com o retorno já normalizado.
   * `credentials: 'omit'`: a API é pública e o hash vai no corpo, então o
   * backend não precisa de Allow-Credentials nem de origem espelhada.
   */
  function postJson(path, payload, ms) {
    return fetchWithTimeout(BASE_URL + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'omit',
      mode: 'cors',
      cache: 'no-store'
    }, ms).then(function (res) {
      var retryAfter = null;
      try {
        // Só chega aqui se o servidor expuser o header via
        // Access-Control-Expose-Headers: Retry-After.
        var raw = res.headers && res.headers.get ? res.headers.get('Retry-After') : null;
        if (raw && !isNaN(parseInt(raw, 10))) retryAfter = parseInt(raw, 10);
      } catch (e) {}

      return res.text().then(function (txt) {
        var body = null;
        try { body = txt ? JSON.parse(txt) : null; } catch (e) { body = null; }
        return { status: res.status, retryAfter: retryAfter, body: body };
      });
    });
  }

  function apiSessao(sessao) {
    if (USE_MOCK) return mockSessao(sessao);
    var ag = getAgente();
    var payload = { sessao: sessao, empresa_id: EMPRESA_ID };

    // `persona` (1 a 4) é quem deve atender. Vale na CRIAÇÃO da sessão: se ela
    // já existe, o backend mantém a que gravou e devolve essa em `persona`.
    // Número fora da lista não dá erro lá — cai no sorteio deles. Sem o campo,
    // idem: sorteiam. Por isso omitir é seguro quando não há número.
    if (ag.persona) payload.persona = ag.persona;

    return postJson(EP_SESSAO, payload, SESSAO_TIMEOUT_MS);
  }

  function apiMensagem(payload) {
    if (USE_MOCK) return mockMensagem(payload);
    return postJson(EP_MENSAGEM, payload, REQUEST_TIMEOUT_MS);
  }

  /* ---------------------------------------------------------------------------
   * MOCK — mesmo formato do backend, para QA sem rede
   *
   * Gatilhos (digite no chat): #sacola #vitrine #acoes #fim #lento #429 #403 #rede
   * ------------------------------------------------------------------------- */

  var mockTurn = 0;

  // Espelha o debounce: a 2ª e a 3ª mensagem da mesma rajada recebem o MESMO
  // corpo, com msgs_no_burst crescendo — igualzinho ao backend.
  var mockRajada = { id: -1, body: null, n: 0 };

  function mockSacola() {
    if (!MOCK_VARIANT_ADD) return { itens: [], total: 0, total_texto: 'R$ 0,00' };
    return {
      itens: [{
        produto_id: 305,
        variant_id: String(MOCK_VARIANT_ADD),
        titulo: 'Select One Um Só Passo 300ml',
        variante: null,
        qty: 1,
        imagem: '',
        preco: 109.9,
        preco_texto: 'R$ 109,90'
      }],
      total: 109.9,
      total_texto: 'R$ 109,90'
    };
  }

  /**
   * Quem o "backend" do mock diz que atende. Ecoa o sorteio local: para testar
   * o caso que dói (sessão retomada com OUTRA vendedora), troque por um número
   * fixo diferente, ex.: { numero: 3, nome: 'Samira Machado' }.
   */
  function mockPersona() {
    var ag = getAgente();
    return { numero: ag.persona || 1, nome: ag.nome };
  }

  function mockSessao(sessao) {
    return new Promise(function (resolve) {
      track(setTimeout(function () {
        resolve({
          status: 200, retryAfter: null,
          body: {
            success: true,
            sessao: sessao,
            persona: mockPersona(),  // o backend sempre diz quem atende
            mensagens: [],           // troque por itens { autor, texto, em } p/ testar o retomar
            sacola: { itens: [], total: 0, total_texto: 'R$ 0,00' },
            encerrada: false
          }
        });
      }, 300));
    });
  }

  function mockMensagem(payload) {
    var msg = (payload && payload.mensagem ? payload.mensagem : '').toLowerCase();

    // Mesma rajada: devolve a resposta já decidida, só com msgs_no_burst maior.
    if (mockRajada.id === state.rajada && mockRajada.body) {
      mockRajada.n++;
      var repetida = {};
      for (var kr in mockRajada.body) {
        if (Object.prototype.hasOwnProperty.call(mockRajada.body, kr)) repetida[kr] = mockRajada.body[kr];
      }
      repetida.msgs_no_burst = mockRajada.n;
      return new Promise(function (resolve) {
        track(setTimeout(function () {
          resolve({ status: 200, retryAfter: null, body: repetida });
        }, MOCK_DELAY_MS));
      });
    }

    function ok(body, atraso) {
      // Primeira da rajada: é esta resposta que as próximas vão repetir.
      if (body && body.success === true) {
        mockRajada = { id: state.rajada, body: body, n: 1 };
      }
      return new Promise(function (resolve) {
        track(setTimeout(function () {
          resolve({ status: 200, retryAfter: null, body: body });
        }, atraso == null ? MOCK_DELAY_MS : atraso));
      });
    }

    var base = { success: true, message: '', persona: mockPersona(), vitrines: [], sacola: mockSacola(), link: null, encerrada: false, msgs_no_burst: 1 };

    function com(extra) {
      var o = {};
      for (var k in base) if (Object.prototype.hasOwnProperty.call(base, k)) o[k] = base[k];
      for (var k2 in extra) if (Object.prototype.hasOwnProperty.call(extra, k2)) o[k2] = extra[k2];
      return o;
    }

    // Sacola + link de checkout no host do backend (é assim que ele manda).
    if (msg.indexOf('#sacola') !== -1) {
      return ok(com({
        texto: 'Montei sua sacola com o *Select One*. Quer que eu jogue no carrinho da loja?',
        link: 'https://sofia.prohall.app.br/cart/' + (MOCK_VARIANT_ADD || 0) + ':1?utm_source=sofia'
      }));
    }

    // Vitrine de 1 produto (formato 'imagem') -> card deitado.
    if (msg.indexOf('#vitrine') !== -1) {
      return ok(com({
        texto: 'Essa é a minha indicação para *frizz*:',
        vitrines: [{
          formato: 'imagem',
          produtos: [{
            id: 278, nome: 'Leave-In Secar sem Frizz 200ml', preco: 'R$ 54,90',
            imagem_url: 'https://cdn.shopify.com/s/files/1/0000/0000/products/sofia-mock-1.jpg',
            handle: 'leave-in-secar-sem-frizz'
          }]
        }],
        sacola: { itens: [], total: 0, total_texto: 'R$ 0,00' }
      }));
    }

    // Extensão `acoes` (o backend ainda não emite; ver docs/sofia-acoes-carrinho.md).
    if (msg.indexOf('#acoes') !== -1 || msg.indexOf('#carrinho') !== -1) {
      return ok(com({
        texto: 'Fechado! Vou *somar a máscara* e tirar o shampoo da sua sacola.',
        sacola: { itens: [], total: 0, total_texto: 'R$ 0,00' },
        acoes: [
          { op: 'add', variant_id: MOCK_VARIANT_ADD, quantity: 1, titulo: 'Máscara Nutritiva Ambar Oil 500g' },
          { op: 'remove', variant_id: MOCK_VARIANT_REMOVE, titulo: 'Shampoo Reconstrução Profissional' }
        ]
      }));
    }

    if (msg.indexOf('#fim') !== -1) {
      return ok(com({ texto: 'Foi um prazer! Qualquer coisa, me chama de novo.', encerrada: true }));
    }

    // Exercita a espera real do debounce: dicas de espera + timeout.
    if (msg.indexOf('#lento') !== -1) {
      return ok(com({ texto: 'Demorei de propósito: era isso que o debounce de 20s faz.' }), 26000);
    }

    if (msg.indexOf('#429') !== -1) {
      return new Promise(function (resolve) {
        track(setTimeout(function () {
          resolve({
            status: 429, retryAfter: 15,
            body: { success: false, message: 'Muitas mensagens. Aguarde alguns segundos.' }
          });
        }, MOCK_DELAY_MS));
      });
    }
    if (msg.indexOf('#403') !== -1) {
      return new Promise(function (resolve) {
        track(setTimeout(function () {
          resolve({
            status: 403, retryAfter: null,
            body: { success: false, message: 'Origem não permitida.' }
          });
        }, MOCK_DELAY_MS));
      });
    }
    if (msg.indexOf('#rede') !== -1) {
      return new Promise(function (_, reject) {
        track(setTimeout(function () { reject(new Error('mock: falha de rede')); }, MOCK_DELAY_MS));
      });
    }

    var scenario = mockTurn % 4;
    mockTurn++;

    // (a) só texto, com marcação e quebras de linha
    if (scenario === 0) {
      return ok(com({
        texto:
          'Perfeito! Pra cabelo com *frizz* e ressecado eu sigo três passos:\n\n' +
          '1. Lavar com shampoo _sem sal_;\n' +
          '2. Máscara de reconstrução 1x por semana;\n' +
          '3. Finalizador com proteção térmica ~todo dia~ sempre antes da chapinha.\n\n' +
          'Quer que eu monte o kit? Dá uma olhada em https://lojaprohall.com.br/collections/tratamento',
        sacola: { itens: [], total: 0, total_texto: 'R$ 0,00' }
      }));
    }

    // (b) texto + carrossel de 3 produtos + sacola montada
    if (scenario === 1) {
      return ok(com({
        texto: 'Separei *3 opções* que combinam com o que você me contou:',
        vitrines: [{
          formato: 'carrossel',
          produtos: [
            {
              id: 812, nome: 'Shampoo Reconstrução Profissional 300ml', preco: 'R$ 189,90',
              imagem_url: 'https://cdn.shopify.com/s/files/1/0000/0000/products/sofia-mock-1.jpg',
              url: 'https://lojaprohall.com.br/products/shampoo-reconstrucao'
            },
            {
              id: 813, nome: 'Máscara Nutritiva Ambar Oil 500g', preco: 'R$ 229,90',
              imagem_url: '',   // foto ausente de propósito: cai no placeholder
              url: 'https://lojaprohall.com.br/products/mascara-ambar-oil'
            },
            {
              id: 814, nome: 'Leave-in Protetor Térmico 200ml', preco: 119.9,
              imagem_url: 'https://cdn.shopify.com/s/files/1/0000/0000/products/sofia-mock-3.jpg',
              handle: 'leave-in-termico'
            }
          ]
        }]
      }));
    }

    // (c) pergunta de qualificação, sem vitrine e sem sacola
    if (scenario === 2) {
      return ok(com({
        texto:
          'Entendi! Me conta uma coisa antes de indicar: seu cabelo é *liso*, *ondulado*, ' +
          '*cacheado* ou *crespo*? E ele tem química (progressiva, coloração, descoloração)?',
        sacola: { itens: [], total: 0, total_texto: 'R$ 0,00' }
      }));
    }

    // (d) erro de negócio: HTTP 200 com success:false
    return ok({ success: false, message: 'Não consegui processar agora. Pode reformular a pergunta?' });
  }

  /* ==========================================================================
   * 11. FLUXO DE ENVIO
   * ========================================================================== */

  function inCooldown() {
    return state.cooldownUntil > Date.now();
  }

  /**
   * Quando o campo trava.
   *
   * Com TRAVAR_DURANTE_ESPERA (padrão), qualquer requisição aberta desabilita o
   * campo: uma mensagem por turno, sem rajada. Desligando, o campo continua
   * livre durante a espera e o backend agrupa as mensagens — só o teto de
   * MAX_EM_VOO limita.
   *
   * Os outros dois bloqueios valem sempre: cooldown de 429 e conversa encerrada.
   */
  function updateComposerState() {
    if (!el.input) return;

    var esperando = state.emVoo > 0;
    var travadoPelaEspera = TRAVAR_DURANTE_ESPERA && esperando;
    var bloqueado = inCooldown() || state.encerrada || travadoPelaEspera;

    el.input.disabled = bloqueado;
    el.send.disabled = bloqueado || state.emVoo >= MAX_EM_VOO || !el.input.value.trim();

    // Campo apagado sem explicação parece quebrado, ainda mais numa espera de
    // ~56s. O texto do placeholder é o aviso — as três bolinhas contam o resto.
    var texto = travadoPelaEspera ? PLACEHOLDER_ESPERANDO : PLACEHOLDER_PADRAO;
    if (el.input.getAttribute('placeholder') !== texto) {
      el.input.setAttribute('placeholder', texto);
    }
  }

  /** Teto local de 12/min: tomar 429 do backend gasta um turno do visitante. */
  function podeEnviar() {
    var agora = Date.now();
    var recentes = [];
    for (var i = 0; i < state.envios.length; i++) {
      if (agora - state.envios[i] < 60000) recentes.push(state.envios[i]);
    }
    state.envios = recentes;
    return recentes.length < LIMITE_POR_MINUTO;
  }

  function startCooldown(seconds) {
    var secs = parseInt(seconds, 10);
    if (isNaN(secs) || secs <= 0) secs = DEFAULT_RETRY_AFTER;
    if (secs > 300) secs = 300;

    state.cooldownUntil = Date.now() + secs * 1000;
    if (el.cooldownTimer) clearInterval(el.cooldownTimer);

    function tick() {
      var left = Math.ceil((state.cooldownUntil - Date.now()) / 1000);
      if (left <= 0) {
        clearInterval(el.cooldownTimer);
        el.cooldownTimer = null;
        setStatus('');
        updateComposerState();
        return;
      }
      setStatus('Aguarde ' + left + 's para enviar de novo.');
      updateComposerState();
    }

    tick();
    el.cooldownTimer = track(setInterval(tick, 1000));
  }

  function setStatus(text) {
    if (!el.status) return;
    el.status.textContent = text || '';
    if (text) el.status.classList.add('is-on');
    else el.status.classList.remove('is-on');
  }

  /* ---------------------------------------------------------------------------
   * Espera do debounce
   *
   * São ~20s de janela + 5 a 13s de modelo. Três bolinhas piscando por 30s
   * parecem travamento, então a linha de status vai contando a história.
   * ------------------------------------------------------------------------- */
  function iniciarDicas() {
    pararDicas();
    for (var i = 0; i < DICAS_ESPERA.length; i++) {
      var ms = DICAS_ESPERA[i][0];
      var txt = DICAS_ESPERA[i][1];
      state.dicaTimers.push(track(setTimeout((function (texto) {
        return function () {
          if (state.emVoo > 0 && !inCooldown()) setStatus(texto);
        };
      })(txt), ms)));
    }
  }

  function pararDicas() {
    for (var i = 0; i < state.dicaTimers.length; i++) clearTimeout(state.dicaTimers[i]);
    state.dicaTimers = [];
  }

  /**
   * Mantém o "digitando" no FIM da conversa enquanto a rajada não fecha.
   * showTyping() remove a linha antiga e reanexa no fim — sem isso, a bolha da
   * resposta nasceria embaixo das três bolinhas.
   */
  function reposicionarTyping() {
    if (state.emVoo > 0) showTyping();
  }

  function terminouRequisicao() {
    state.emVoo = Math.max(0, state.emVoo - 1);
    if (state.emVoo === 0) {
      hideTyping();
      pararDicas();
      if (!inCooldown()) setStatus('');
    }
    updateComposerState();
  }

  /* ==========================================================================
   * 11b. SESSÃO NO SERVIDOR (POST /sessao)
   *
   * Quem guarda a conversa é o backend. O /sessao abre ou RETOMA pelo hash e
   * devolve `mensagens` + `sacola` — é o que faz a conversa sobreviver a fechar
   * a aba e trocar de dispositivo (mesmo cookie, mesma conversa).
   * ========================================================================== */

  /**
   * Abre a sessão uma vez por página. NUNCA rejeita: falhar aqui (CORS, rede,
   * 500) não pode impedir o envio — a primeira /mensagem abre a sessão do lado
   * do servidor de qualquer jeito, só sem histórico na tela.
   */
  function garantirSessao() {
    if (state.destroyed || state.sessaoAberta) return Promise.resolve(null);
    if (state.abrindoSessao) return state.abrindoSessao;

    var sessao = getSessionId();

    state.abrindoSessao = apiSessao(sessao).then(function (res) {
      state.abrindoSessao = null;
      state.sessaoAberta = true;

      var body = res && res.body;
      if (!body || typeof body !== 'object' || body.success !== true) return null;

      // Antes do histórico: é aqui que se descobre que a conversa retomada é de
      // outra vendedora, e o cabeçalho tem que mudar antes de as bolhas voltarem.
      receberPersona(body.persona);

      pintarHistoricoServidor(body.mensagens);
      if (body.sacola) receberSacola(body.sacola);
      if (body.encerrada === true) encerrarConversa();
      return body;
    }, function () {
      // Não fica tentando de novo a cada mensagem: uma sessão que não abre é
      // quase sempre CORS do ambiente, e insistir só enche o log de erro.
      state.abrindoSessao = null;
      state.sessaoAberta = true;
      return null;
    });

    return state.abrindoSessao;
  }

  /**
   * Histórico do servidor -> tela.
   *
   * Só assume quando a tela tem APENAS a saudação local. O espelho do
   * sessionStorage é mais RICO (guarda cards e botões de link, que o /sessao não
   * devolve — lá vêm só autor/texto/em), então quando ele existe é ele que fica.
   * E nunca repinta com requisição aberta: apagaria a mensagem que a pessoa
   * acabou de mandar.
   */
  function pintarHistoricoServidor(mensagens) {
    if (!Array.isArray(mensagens) || !mensagens.length) return;
    if (!state.soBoasVindas || state.emVoo > 0 || !el.log) return;

    var lista = mensagens.slice(-PERSIST_MAX_MSGS);

    state.restoring = true;
    try {
      while (el.log.firstChild) el.log.removeChild(el.log.firstChild);
      el.typing = null;
      state.msgs = [];

      for (var i = 0; i < lista.length; i++) {
        var m = lista[i];
        if (!m || typeof m.texto !== 'string' || !m.texto.trim()) continue;
        if (m.autor === 'cliente') addUserMessage(m.texto);
        else addBotMessage(m.texto, null, null, null);
      }
    } finally {
      state.restoring = false;
    }

    // Reespelha o que ficou na tela, para a próxima página repintar na hora.
    state.msgs = [];
    for (var j = 0; j < lista.length; j++) {
      var n = lista[j];
      if (!n || typeof n.texto !== 'string' || !n.texto.trim()) continue;
      state.msgs.push(n.autor === 'cliente' ? { r: 'u', t: n.texto } : { r: 'b', t: n.texto, p: [], l: null });
    }

    state.soBoasVindas = false;
    state.history = [];   // o servidor é a fonte do histórico agora
    salvarConversa();
    scrollToEnd();
  }

  /** `encerrada: true`: a Sofia fechou o atendimento. Trava o campo e oferece recomeço. */
  function encerrarConversa() {
    if (state.encerrada) return;
    state.encerrada = true;
    updateComposerState();
    setStatus('');
    addSystemMessage(
      'Este atendimento foi encerrado.',
      function () { resetSession(); focusInput(); },
      'Começar uma nova conversa'
    );
  }

  /* ==========================================================================
   * 11c. FLUXO DE ENVIO
   * ========================================================================== */

  /**
   * Últimos MAX_HISTORY turnos no formato antigo (`historico`). Só é usado com
   * SEND_LOCAL_HISTORY ligado — hoje quem guarda o histórico é o servidor.
   */
  function historyPayload() {
    if (!SEND_LOCAL_HISTORY) return [];
    var h2 = state.history.slice(-MAX_HISTORY);
    var out = [];
    for (var i = 0; i < h2.length; i++) {
      out.push({ role: h2[i].role, content: String(h2[i].content).slice(0, 4000) });
    }
    return out;
  }

  function submitText(text, opts) {
    opts = opts || {};
    if (state.destroyed) return;

    text = String(text == null ? '' : text).trim();
    if (!text) return;

    if (text.length > MAX_CHARS) {
      addSystemMessage('Mensagem muito longa (máx. ' + MAX_CHARS + ' caracteres).');
      return;
    }
    if (state.encerrada) {
      addSystemMessage('Este atendimento foi encerrado.');
      return;
    }
    if (inCooldown()) return;

    // A trava de verdade mora aqui, não na UI: campo desabilitado é só a parte
    // visível. Qualquer outro caminho de envio (Enter, retry, chamada externa)
    // passa por esta linha.
    if (TRAVAR_DURANTE_ESPERA && state.emVoo > 0) return;

    // Rajada cheia: melhor dizer isso do que abrir a 5ª requisição paralela.
    if (state.emVoo >= MAX_EM_VOO) {
      addSystemMessage('Deixa ' + getAgente().artigo + ' ' + getAgente().nome +
        ' responder essas primeiro — ela lê tudo de uma vez.');
      return;
    }
    if (!podeEnviar()) {
      addSystemMessage('Muitas mensagens em pouco tempo. Aguarde um instante.');
      startCooldown(20);
      return;
    }

    // `semBolha`: o card já escreveu o nome do produto como fala da pessoa; o
    // texto que vai para a IA é mais explícito ("coloca 1 na sacola") e repetir
    // isso na tela seria a pessoa falando duas vezes.
    if (!opts.isRetry && !opts.semBolha) addUserMessage(text);
    state.soBoasVindas = false;   // a tela já não é só a saudação

    // A nota some da fila agora para não ir duas vezes; se o envio falhar, ela
    // volta para a fila no catch, e a pessoa não perde o contexto.
    var nota = notaDoCarrinho();
    var notaPendente = state.adicionados;
    if (nota) state.adicionados = [];

    // Rajada: sem requisição aberta, começa um grupo novo. Com uma aberta, esta
    // mensagem entra no MESMO grupo — porque o backend vai responder as duas
    // com o mesmo texto e só a primeira resposta deve virar bolha.
    if (state.emVoo === 0) {
      state.rajada++;
      state.assinatura = '';
    }
    var rajada = state.rajada;

    state.envios.push(Date.now());
    state.emVoo++;
    updateComposerState();
    showTyping();
    iniciarDicas();

    // Renova a janela do cookie a cada mensagem (rolling).
    var sessao = getSessionId();
    touchSession();

    // A sessão abre antes da primeira mensagem (e nunca rejeita); o contexto é
    // best-effort: se o /cart.js falhar ou demorar, manda sem ele.
    garantirSessao().then(function () {
      return buildContext();
    }).then(function (contexto) {
      // O que a pessoa adicionou sozinha viaja junto com a mensagem dela: é o
      // único canal que o backend lê hoje (ver NOTA_CARRINHO).
      var agenteAtual = getAgente();
      var payload = {
        sessao: sessao,
        empresa_id: EMPRESA_ID,
        mensagem: text + nota
      };

      // A mesma persona da abertura, em toda mensagem (ver AGENTES). O backend
      // ignora numa sessão que já tem vendedora — vai junto porque é o exemplo
      // do contrato e porque a 1ª /mensagem pode ser quem cria a sessão (se o
      // /sessao tiver falhado).
      if (agenteAtual.persona) payload.persona = agenteAtual.persona;

      // Campos fora do contrato (o backend ignora extras): contexto da loja e o
      // par sessão/carrinho. Ver o comentário em buildContext().
      if (contexto) payload.contexto = contexto;
      if (SEND_LOCAL_HISTORY) payload.historico = historyPayload();

      // Só sobe quando é estável (sacola com item). Com a sacola vazia o token
      // troca a cada pageview e o backend guardaria lixo por página.
      if (contexto && contexto.cart && contexto.cart.token_estavel) {
        payload.cart_token = contexto.cart.token;
        // Trocou desde a última mensagem (checkout, sacola esvaziada): o backend
        // usa isso para mover a sessão em vez de abrir outra.
        if (state.cartTokenAnterior && state.cartTokenAnterior !== contexto.cart.token) {
          payload.cart_token_anterior = state.cartTokenAnterior;
          state.cartTokenAnterior = null;
        }
      }

      return apiMensagem(payload);
    }).then(function (res) {
      terminouRequisicao();
      handleResponse(res, text, rajada);
    }).catch(function () {
      terminouRequisicao();
      // O aviso não chegou: volta para a fila, para pegar a próxima mensagem.
      if (nota) state.adicionados = notaPendente.concat(state.adicionados);
      // Rede/timeout: bolha de sistema + repetir. Nunca trava sem explicação.
      addSystemMessage('Não consegui enviar. Tenta de novo?', function () {
        submitText(text, { isRetry: true });
      });
      reposicionarTyping();
      focusInput();
    });
  }

  function handleResponse(res, sentText, rajada) {
    res = res || {};
    var body = res.body;
    var status = res.status;

    // 429: o único erro que não vem com HTTP 200. Bloqueia o campo pelo tempo do
    // Retry-After, com contagem visível.
    if (status === 429) {
      addSystemMessage((body && body.message) || 'Muitas mensagens. Aguarde alguns segundos.');
      startCooldown(res.retryAfter);
      reposicionarTyping();
      return;
    }

    if (status === 403) {
      addSystemMessage((body && body.message) || 'Origem não permitida.');
      reposicionarTyping();
      return;
    }

    if (!body || typeof body !== 'object') {
      addSystemMessage('Não consegui enviar. Tenta de novo?', function () {
        submitText(sentText, { isRetry: true });
      });
      reposicionarTyping();
      return;
    }

    // Erro de negócio: HTTP 200 com success:false. Ler o campo, não o status.
    if (body.success !== true) {
      addSystemMessage(body.message || 'Não consegui responder agora. Tenta de novo?');
      reposicionarTyping();
      return;
    }

    /* ---- contrato do embed: campos no TOPO da resposta, sem `data` ---- */
    // Quem assina a bolha que vai ser pintada logo abaixo.
    receberPersona(body.persona);

    var texto = typeof body.texto === 'string' ? body.texto : '';
    var produtos = produtosDeVitrines(body.vitrines);
    var link = linkDaLoja(body.link);
    var acoes = normalizeAcoes(body.acoes);   // extensão; hoje não vem

    // `msgs_no_burst` diz quantas mensagens do cliente entraram neste turno.
    // A deduplicação abaixo não depende dele (o controle de rajada é nosso e é
    // mais estrito), mas ele é o jeito de conferir isso em QA.
    if (readPhChat().debug && window.console && console.log) {
      console.log('[Sofia] rajada', rajada, 'msgs_no_burst', body.msgs_no_burst);
    }

    /* ---- DEDUPLICAÇÃO DA RAJADA ----
     * 3 mensagens seguidas = 3 requisições que resolvem com a MESMA resposta.
     * Sem isso, a pessoa vê a mesma bolha três vezes. */
    var assinatura = [
      texto,
      produtos.length,
      link || '',
      body.sacola ? assinaturaSacola(normalizarSacola(body.sacola)) : '',
      acoes.length
    ].join('|');

    if (rajada === state.rajadaPintada && assinatura === state.assinatura) {
      reposicionarTyping();      // cópia da mesma resposta: descarta
      return;
    }
    state.rajadaPintada = rajada;
    state.assinatura = assinatura;

    // A sacola é redesenhada inteira a cada resposta (fonte da verdade é o
    // servidor). `undefined` = campo ausente: mantém o que está na tela.
    receberSacola(body.sacola);

    var temSacola = !!(state.sacola && state.sacola.itens.length);

    /* ---- O BOTÃO DO LINK ----
     * Ela escreve "é só finalizar por aqui:" — o botão TEM que aparecer, ou a
     * frase fica no vazio (era o que acontecia: com sacola, o link era jogado
     * fora e não sobrava nada embaixo da bolha).
     *
     * O que muda é o DESTINO, não a presença:
     *   - com sacola: o permalink /cart/<variante>:1 SUBSTITUI o carrinho do
     *     visitante, apagando o que ele pôs sozinho. Como a sacola dela já foi
     *     para o carrinho nativo pelo add.js, o botão vai para o /checkout da
     *     loja — mesmo conteúdo, sem destruir nada.
     *   - sem sacola: não há o que proteger, vale o link como veio. */
    var linkCta = null;
    if (link) {
      linkCta = temSacola ? (shopRoot() + 'checkout') : link;
    }

    if (!texto && !produtos.length && !acoes.length && !temSacola) {
      addSystemMessage('Não veio resposta. Tenta perguntar de outro jeito?');
      reposicionarTyping();
      return;
    }

    addBotMessage(texto || 'Separei isso pra você:', produtos, linkCta, acoes);

    // Histórico local: espelho para repintar a tela, não fonte da verdade.
    state.history.push({ role: 'user', content: sentText });
    state.history.push({ role: 'assistant', content: texto });
    if (state.history.length > MAX_HISTORY * 2) {
      state.history = state.history.slice(-MAX_HISTORY * 2);
    }
    salvarConversa();

    if (body.encerrada === true) encerrarConversa();

    reposicionarTyping();
    focusInput();
  }

  /* ==========================================================================
   * 12. EVENTOS / ACESSIBILIDADE
   * ========================================================================== */

  var INPUT_MIN_H = 40;
  var INPUT_MAX_H = 116;

  /**
   * Cresce o campo conforme o texto.
   * A barra de rolagem fica escondida enquanto o texto couber — era ela que
   * aparecia vazia no canto do campo, porque o tema deixa o textarea com
   * overflow visível por padrão.
   */
  function autoGrow() {
    var node = el.input;
    node.style.height = 'auto';
    var alvo = Math.max(INPUT_MIN_H, Math.min(node.scrollHeight, INPUT_MAX_H));
    node.style.height = alvo + 'px';
    node.style.overflowY = node.scrollHeight > INPUT_MAX_H ? 'auto' : 'hidden';
  }

  function updateCounter() {
    var len = el.input.value.length;
    if (len > MAX_CHARS - 120) {
      el.count.textContent = len + '/' + MAX_CHARS;
      el.count.classList.add('is-on');
      el.count.classList.toggle('is-over', len > MAX_CHARS);
    } else {
      el.count.classList.remove('is-on', 'is-over');
    }
  }

  function focusInput() {
    if (!el.input || el.input.disabled) return;
    try { el.input.focus({ preventScroll: true }); } catch (e) { el.input.focus(); }
  }

  function focusableInPanel() {
    var nodes = el.panel.querySelectorAll(
      'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'
    );
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.offsetWidth || n.offsetHeight || n.getClientRects().length) out.push(n);
    }
    return out;
  }

  function onKeydown(e) {
    if (!state.open) return;

    // Esc continua fechando: é o equivalente do X para quem usa teclado, e
    // diálogo com aria-modal precisa ter saída. Clique fora e troca de página
    // não fecham nada — só o X (ou o Esc).
    if (FECHAR_COM_ESC && (e.key === 'Escape' || e.keyCode === 27)) {
      e.preventDefault();
      api.close();
      return;
    }

    // aria-modal="true" exige que o Tab não escape do diálogo.
    if (e.key === 'Tab' || e.keyCode === 9) {
      var items = focusableInPanel();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (e.shiftKey && (active === first || !el.panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 639px)').matches;
  }

  /**
   * Avisa o tema que o chat abriu/fechou (`sofia:open` / `sofia:close`).
   * É assim que a bolinha do WhatsApp vira X e volta ao normal, sem que o
   * snippet precise ficar perguntando de tempo em tempo.
   */
  function emitir(nome) {
    try {
      var ev;
      if (typeof window.CustomEvent === 'function') {
        ev = new window.CustomEvent(nome, { detail: { aberto: !!state.open } });
      } else {
        ev = document.createEvent('CustomEvent');
        ev.initCustomEvent(nome, true, false, { aberto: !!state.open });
      }
      document.dispatchEvent(ev);
    } catch (e) {}
  }

  function bindEvents() {
    if (el.launcher) on(el.launcher, 'click', function () { api.toggle(); });
    on(el.close, 'click', function () { api.close(); });

    on(el.form, 'submit', function (e) {
      e.preventDefault();
      var text = el.input.value;
      el.input.value = '';
      autoGrow();
      updateCounter();
      updateComposerState();
      submitText(text);
    });

    on(el.input, 'keydown', function (e) {
      // Enter envia, Shift+Enter quebra linha.
      if ((e.key === 'Enter' || e.keyCode === 13) && !e.shiftKey) {
        e.preventDefault();
        if (!el.send.disabled) {
          if (typeof window.Event === 'function') {
            el.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          } else {
            var ev = document.createEvent('Event');
            ev.initEvent('submit', true, true);
            el.form.dispatchEvent(ev);
          }
        }
      }
    });

    on(el.input, 'input', function () {
      autoGrow();
      updateCounter();
      updateComposerState();
    });

    // foco visual na pílula, não no textarea
    on(el.input, 'focus', function () { el.field.classList.add('is-focus'); });
    on(el.input, 'blur', function () { el.field.classList.remove('is-focus'); });

    on(document, 'keydown', onKeydown);

    // Não fecha ao clicar fora de propósito: o visitante costuma clicar no site
    // enquanto conversa (ver produto, rolar a página) e perder a conversa irrita.
  }

  /* ==========================================================================
   * 13. API PÚBLICA
   * ========================================================================== */

  var api = {
    open: function (opts) {
      if (state.destroyed || state.open || !el.panel) return;
      var comFoco = !(opts && opts.focus === false);
      state.lastFocused = document.activeElement;
      state.open = true;
      el.panel.classList.add('is-open');
      // Tira da frente o popup da roleta e a aba dele (ver OCULTAR_QUANDO_ABERTO).
      document.documentElement.classList.add(NS + '-com-chat');
      if (el.launcher) {
        el.launcher.setAttribute('aria-expanded', 'true');
        el.launcher.setAttribute('aria-label', 'Fechar o chat');
      }
      if (isMobile()) {
        document.documentElement.classList.add(NS + '-lock');
        document.body.classList.add(NS + '-lock');
      }
      updateComposerState();
      salvarConversa();

      // Abre/retoma a conversa no servidor na PRIMEIRA abertura do painel (ver
      // SESSAO_NO_LOAD). É o que traz histórico e sacola de volta depois de
      // fechar a aba. Nunca rejeita, e o envio não espera por ela.
      garantirSessao();

      // Já busca o carrinho na abertura: assim o par sessao + cart_token existe
      // antes da primeira mensagem, e cartToken() responde na hora. E quem já
      // tem item no carrinho abre o chat com o botão de finalizar à mão.
      getCart();
      painelDoCarrinhoNativo();

      emitir('sofia:open');

      track(setTimeout(function () {
        if (comFoco) focusInput();
        scrollToEnd();
      }, 60));
    },

    close: function () {
      if (state.destroyed || !state.open || !el.panel) return;
      state.open = false;
      el.panel.classList.remove('is-open');
      // Chat fechado: a roleta volta a poder aparecer.
      document.documentElement.classList.remove(NS + '-com-chat');
      if (el.launcher) {
        el.launcher.setAttribute('aria-expanded', 'false');
        el.launcher.setAttribute('aria-label', 'Abrir o chat');
      }
      document.documentElement.classList.remove(NS + '-lock');
      document.body.classList.remove(NS + '-lock');
      salvarConversa();   // grava "fechado": não reabre na próxima página
      emitir('sofia:close');

      // O foco volta para quem abriu: a bolinha, quando existe, ou o item
      // "Comprar agora" do menu do WhatsApp (guardado em state.lastFocused).
      var back = el.launcher || state.lastFocused;
      if (back && back.focus && document.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
      }
      state.lastFocused = null;
    },

    toggle: function () { state.open ? api.close() : api.open(); },

    isOpen: function () { return !!state.open; },

    /** Hash da sessão atual (o mesmo valor que vai em `sessao`, no corpo). */
    sessionId: function () { return state.destroyed ? null : getSessionId(); },

    /**
     * Abre/retoma a sessão no servidor sem precisar abrir o painel.
     * Devolve o corpo do /sessao (ou null se falhou / se já estava aberta).
     */
    abrirSessao: function () {
      return state.destroyed ? Promise.resolve(null) : garantirSessao();
    },

    /** A sacola montada pela Sofia, já normalizada. null quando está vazia. */
    sacola: function () { return state.destroyed ? null : state.sacola; },

    /**
     * Joga a sacola da Sofia no carrinho nativo, do jeito que o botão do painel
     * faz: soma só a diferença e ressincroniza o tema. Devolve o /cart.js final.
     */
    aplicarSacola: function () {
      return state.destroyed ? Promise.resolve(null) : aplicarSacola();
    },

    /** Identidade do agente em uso — quem chama de fora monta o texto que quiser. */
    agente: function () { return getAgente(); },

    /**
     * Token do carrinho amarrado a esta conversa (o mesmo que vai em
     * `cart_token`). Null até o primeiro /cart.js responder.
     */
    cartToken: function () { return state.cartToken; },

    /** Recomeça: novo hash, conversa local zerada. Devolve o hash novo. */
    newSession: function () { return state.destroyed ? null : resetSession(); },

    /**
     * Aplica ações de carrinho na mão — mesmo formato de `data.acoes`.
     * Existe para QA e para o backend testar o contrato sem passar pelo chat:
     *   SofiaWidget.aplicarAcoes([{ op:'add', variant_id: 41234567890, quantity: 1 }])
     * Passa pela MESMA validação da resposta da IA. Devolve o carrinho final
     * (ou null se nenhuma ação sobreviveu à validação).
     */
    aplicarAcoes: function (acoes) {
      if (state.destroyed) return Promise.resolve(null);
      var lista = normalizeAcoes(acoes);
      if (!lista.length) return Promise.resolve(null);
      return runAcoes(lista);
    },

    /** O mesmo recorte de carrinho que vai em `contexto.cart`. */
    carrinho: function () { return state.destroyed ? Promise.resolve(null) : getCart(); },

    destroy: function () {
      if (state.destroyed) return;
      state.destroyed = true;

      for (var i = 0; i < listeners.length; i++) {
        try { listeners[i][0].removeEventListener(listeners[i][1], listeners[i][2], listeners[i][3]); } catch (e) {}
      }
      listeners.length = 0;

      for (var j = 0; j < timers.length; j++) {
        clearTimeout(timers[j]);
        clearInterval(timers[j]);
      }
      timers.length = 0;

      document.documentElement.classList.remove(NS + '-lock');
      document.documentElement.classList.remove(NS + '-com-chat');
      document.body.classList.remove(NS + '-lock');

      if (el.panel && el.panel.parentNode) el.panel.parentNode.removeChild(el.panel);
      if (el.launcher && el.launcher.parentNode) el.launcher.parentNode.removeChild(el.launcher);
      var style = document.getElementById(STYLE_ID);
      if (style && style.parentNode) style.parentNode.removeChild(style);

      state.history.length = 0;   // conversa morre junto com o widget
      state.sessionId = null;     // o cookie fica: destruir o widget não é sair da sessão
      state.sacola = null;
      state.emVoo = 0;
      state.dicaTimers = [];
      el = {};

      try { delete window.SofiaWidget; } catch (e) { window.SofiaWidget = undefined; }
    }
  };

  /* ==========================================================================
   * 14. BOOT
   * ========================================================================== */

  function boot() {
    if (state.destroyed || !document.body) return;
    try {
      build();
      window.SofiaWidget = api;
      // Padrão é abrir a sessão só quando o painel abre: a maioria dos
      // visitantes nunca abre o chat e esta loja já disputa main thread.
      if (SESSAO_NO_LOAD) garantirSessao();
    } catch (e) {
      // Nada do widget pode derrubar a página da loja.
      if (window.console && console.warn) console.warn('[SofiaWidget] falhou ao iniciar:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
